// import { useTranslation } from 'react-i18next'; // To be used later for internationalization
import ProductionOrderDocument from '@/components/ProductionOrderDocument';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Filter, Upload, Eye, Edit, Trash2, CheckCircle, ArrowUp, ArrowDown, ArrowUpDown, FileDown, Mail, FileImage, ZoomIn, ZoomOut, RotateCcw, Move, Copy, GripVertical, Stamp, ChevronDown, ChevronUp, List, X, Package, Layers, Printer, Link } from 'lucide-react';
import CoextrusionModal from './CoextrusionModal';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProductionOrders, ProductionOrder, ProductionOrderFilters } from '@/hooks/useProductionOrders';
import { useAuth } from '@/hooks/useAuth';
import { getMexicoDateString, addDaysToDateString } from '@/lib/mexicoDate';
import html2pdf from 'html2pdf.js';
import {
  DragDropContext,
  Droppable as DroppableOrig,
  Draggable as DraggableOrig,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
// Type cast to avoid React 18.3 / @hello-pangea/dnd render-prop incompatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Droppable = DroppableOrig as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Draggable = DraggableOrig as any;
import RoutePlanningSummary from '@/components/RoutePlanningSummary';
import ProcessSpecsTableForm, { normalizeColorRows } from '@/components/ProcessSpecsTableForm';
import LaminationSection from '@/components/LaminationSection';
import PackagingSection from '@/components/PackagingSection';
import { usePrintConfigImages } from '@/hooks/usePrintConfigImages';

// ─── Detail Design Section ───────────────────────────────────────────────────
// Shows the catalog design (official) with fallback to the per-order designUrl.
// Used inside the order detail modal.
function DetailDesignSection({ order }: { order: any }) {
  const [catFailed, setCatFailed] = React.useState(false);
  const [orderFailed, setOrderFailed] = React.useState(false);
  const [versions, setVersions] = React.useState<Array<{
    id: number; version: number; imageDate: string | null; comments: string | null;
    uploaderName: string | null; uploaderUsername: string | null; uploadedAt: string;
  }>>([]);

  React.useEffect(() => {
    setCatFailed(false);
    setOrderFailed(false);
    setVersions([]);
    if (!order?.productCode) return;
    const tkn = localStorage.getItem('access_token') || '';
    fetch(`/api/catalog-designs/by-product-code/${encodeURIComponent(order.productCode)}/versions`, {
      headers: { Authorization: `Bearer ${tkn}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setVersions(data); })
      .catch(() => {});
  }, [order?.productCode]);

  const fmtDate = (d: string | null | undefined) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const showCatalog = order?.productCode && !catFailed;
  const showOrder = !showCatalog && order?.designUrl?.startsWith('ordenesproduccion/') && !orderFailed;
  const showNone = !showCatalog && !showOrder;
  const latest = versions[0] ?? null;

  return (
    <div className="space-y-3">
      <h4 className="text-lg font-semibold text-indigo-600 border-b pb-2">🎨 Diseño</h4>

      {showCatalog && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
              Diseño oficial del catálogo
            </span>
            {latest && <span className="text-xs text-gray-400">v{latest.version}</span>}
          </div>
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            <img
              src={`/api/catalog-designs/by-product-code/${encodeURIComponent(order.productCode)}/image`}
              alt="Diseño del producto"
              className="w-full max-h-64 object-contain"
              onError={() => setCatFailed(true)}
            />
          </div>

          {/* Version history timeline — newest first */}
          {versions.length > 0 && (
            <div className="border border-purple-100 rounded-lg overflow-hidden">
              <div className="bg-purple-50 px-3 py-2 border-b border-purple-100">
                <span className="text-xs font-semibold text-purple-700">
                  📋 Historial de versiones ({versions.length})
                </span>
              </div>
              <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {versions.map((v, idx) => (
                  <div key={v.id} className={`px-3 py-2.5 ${idx === 0 ? 'bg-purple-50/60' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${idx === 0 ? 'text-purple-700' : 'text-gray-600'}`}>
                          v{v.version}
                        </span>
                        {idx === 0 && (
                          <span className="text-[10px] bg-purple-600 text-white px-1.5 py-0.5 rounded-full">ACTUAL</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-1">
                        <span>📅</span>
                        <span>{fmtDate(v.imageDate || v.uploadedAt)}</span>
                        {(v.uploaderName || v.uploaderUsername) && (
                          <span className="ml-1 text-gray-400">· {v.uploaderName || v.uploaderUsername}</span>
                        )}
                      </div>
                    </div>
                    {v.comments ? (
                      <p className="text-xs text-gray-600 italic">💬 {v.comments}</p>
                    ) : (
                      <p className="text-xs text-gray-300 italic">Sin comentarios</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showOrder && (
        <div className="space-y-2">
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Diseño de la orden</span>
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            <img
              src={`/api/production-orders/${order.id}/design-image`}
              alt="Diseño de la orden"
              className="w-full max-h-64 object-contain"
              onError={() => setOrderFailed(true)}
            />
          </div>
        </div>
      )}

      {showNone && (
        <div className="border rounded-lg p-8 text-center text-gray-400 bg-gray-50">
          <div className="text-4xl mb-2">🎨</div>
          <p className="font-medium">Sin diseño registrado</p>
          <p className="text-xs mt-1">Sube el diseño desde el módulo de Diseño</p>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────


export default function ProductionOrders() {
  const { urlFor: printConfigUrlFor } = usePrintConfigImages();
  // const { t } = useTranslation(); // To be used later for internationalization
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has permission to create catalog items
  const canCreateCatalogs = useMemo(() => {
    return user?.permissions?.includes('catalogs:create') ?? false;
  }, [user?.permissions]);
  
  const {
    loading,
    fetchProductionOrders,
    createProductionOrder,
    updateProductionOrder,
    deleteProductionOrder,
    changeOrderStatus
  } = useProductionOrders();

  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<ProductionOrderFilters>({
    page: 1,
    limit: 20,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDesignImageModal, setShowDesignImageModal] = useState(false);
  const [productDesignFailed, setProductDesignFailed] = useState(false);
  const [catalogDesignMeta, setCatalogDesignMeta] = useState<{
    version: number; imageDate: string | null; comments: string | null;
    uploaderName: string | null; uploaderUsername: string | null; uploadedAt: string;
  } | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEngravingModal, setShowEngravingModal] = useState(false);
  const [coextrusionOrderId, setCoextrusionOrderId] = useState<string | null>(null);
  const [savedCoextrusionMap, setSavedCoextrusionMap] = useState<Map<string, string>>(new Map());
  const [coextLinkedOrdersMap, setCoextLinkedOrdersMap] = useState<Map<string, any[]>>(new Map());
  const [expandedCoextLinks, setExpandedCoextLinks] = useState<Set<string>>(new Set());
  const [engravingOrdersMap, setEngravingOrdersMap] = useState<Record<string, any>>({});
  const [expandedEngravingOrders, setExpandedEngravingOrders] = useState<Set<string>>(new Set());
  const [expandedInventoryOrders, setExpandedInventoryOrders] = useState<Set<string>>(new Set());
  const [inventoryMovementsMap, setInventoryMovementsMap] = useState<Record<string, any[]>>({});
  const [inventoryMovementsLoading, setInventoryMovementsLoading] = useState<Set<string>>(new Set());
  const [inventoryMovementsFilters, setInventoryMovementsFilters] = useState<Record<string, Record<string, string>>>({});
  const [currentProcessMap, setCurrentProcessMap] = useState<Record<string, string>>({});
  const [processFilter, setProcessFilter] = useState('');
  const [engravingModalOrder, setEngravingModalOrder] = useState<ProductionOrder | null>(null);
  const [engravingModalData, setEngravingModalData] = useState<any>(null);
  const [engravingFormData, setEngravingFormData] = useState({
    lineaje: '', tipoPrint: 'FLEXO', espesorMaterial: '',
    especFotocelda: '', fotoceldaColor: '', fotoceldaColorNombre: '', cobrarGrabados: false,
    colores: ['', '', '', '', '', '', '', ''] as string[],
    observaciones: '', fechaEntrega: '',
    anchoIndividual: '', bobinasAlEje: '',
    repeticionIndividual: '', bobinasAlDesarrollo: '',
    rodilloFinal: '', flexo: '', codigoBarras: '',
    revisionNumero: '',
  });
  const [historyMatchLoading, setHistoryMatchLoading] = useState(false);
  const [historyMatchInfo, setHistoryMatchInfo] = useState<{ orderNumber: string; createdAt: string; updatedAt: string } | null>(null);
  const [historyMatchAlert, setHistoryMatchAlert] = useState<string | null>(null);
  const [engravingFormSaving, setEngravingFormSaving] = useState(false);
  const [engravingMissingFields, setEngravingMissingFields] = useState<string[]>([]);
  const [engravingSaveError, setEngravingSaveError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  // Image zoom and pan controls
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [statusChangeConfirm, setStatusChangeConfirm] = useState<{
    orderId: string;
    status: 'CREATED' | 'AUTHORIZED';
  } | null>(null);
  
  // Sorting state
  const [sortField, setSortField] = useState<keyof ProductionOrder | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Import modal state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [apiConfig, setApiConfig] = useState({
    url: '',
    method: 'GET',
    authType: 'bearer',
    credentials: '',
  });
  const [importOptions, setImportOptions] = useState({
    updateExisting: true,
    validateData: true,
    sendNotification: false,
  });

  // Create order form state - Extended for real production orders
  // NOTE: createForm/createFormErrors and the standalone "Create Order Modal"
  // that used them were removed here (dead code, found while verifying ROT-200)
  // — that modal was gated by the same `showEditModal` flag as the real 6-section
  // edit/create modal below, so it always rendered simultaneously, hidden behind
  // it; nothing in the file besides that dead modal ever read this state, and
  // the actual save handler (handleUpdateOrder) only ever reads `editForm`.

  // Edit order form state - Identical structure to create form
  const [editForm, setEditForm] = useState({
    // Datos del Cliente y Solicitud de Producción
    orderNumber: '',
    customerName: '',
    productCode: '',
    productName: '',
    codigoBarras: [] as string[],
    orderType: '', // Tipo de Pedido: Producto Nuevo o Repetición
    sourceOrderNumber: '', // OP de origen (para Repetición)
    processType: '',
    customerCode: '',
    branchName: '', // ROT-206: sucursal, para clientes con más de una planta
    purchaseOrder: '',
    purchaseDate: '', // Fecha O. Compra (del campo 'fecha' del JSON)
    productionDate: '', // Fecha O. Producción (del campo 'fecha' del JSON)
    commitmentDate: '', // Fecha Compromiso Entrega (del campo 'fSolicitada' del JSON)
    deliveryDate: '',
    machineName: '', // Máquina (del campo 'departamentoNombre' del JSON)
    equipoPreprensa: '', // Equipo Preprensa
    quantityPlanned: '',
    unit: 'kg',
    lotCode: '',
    
    // Básicos
    priority: '2', // Auto-calculada basada en commitmentDate
    dueDate: '',
    
    // Configuración de Impresión
    printConfigImage: undefined as number | undefined,
    
    // Materiales (items)
    materials: [{
      itemName: '',
      caliber: '',
      factor: '1',
      grm2: '',
      percentage: '',
      quantity: '',
      dispersionFactor: '',
      notes: ''
    }],
    
    // Especificaciones del Proceso
    processSpecs: {
      printType: 'FLEXO',
      individualWidth: '',
      bobinCount: '',
      printWidth: '',
      reelWidth: '',
      baggingCutWidth: '',
      substrateWidth: '',
      barCode: '',
      individualRepetition: '',
      repetitionCount: '',
      cylinderCircumference: '',
      cylinderCount: '',
      authorizedRevision: '',
      cutLine: false,
      footHouse: false,
      photocell: { width: '', height: '' },
      pantoneColors: [] as string[],
      adhesiveLamination: '',
      laminationCatalyst: '',
      trilaminationAdhesive: '',
      trilaminationCatalyst: '',
      innerDiameter: '',
      innerDiameterUnit: 'mm',
      outerDiameter: '',
      outerDiameterUnit: 'mm',
      windingDirection: '',
      figureNumber: '',
      photocellSide: 'CENTRADA',
      fotoceldaColor: '',
      packaging: '',
      continuousRepetition: false,
      colorRows: normalizeColorRows([]),
    },
    
    // Observaciones
    observations: '',
    
    // Etiqueta
    label: '',
    
    // URL del diseño
    designUrl: '',
  });
  const [editFormErrors, setEditFormErrors] = useState<{[key: string]: string}>({});
  const [createServerError, setCreateServerError] = useState<string>('');

  // Material Structure Management
  const [orderMaterials, setOrderMaterials] = useState<any[]>([]);
  // ROT-220: a Clonar-sourced manufacturing route, staged until the cloned
  // order has an id to attach it to — see handleCloneOrder/handleUpdateOrder.
  const [pendingClonedRoutes, setPendingClonedRoutes] = useState<any[]>([]);
  const [expectedResults, setExpectedResults] = useState<any>(null);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    material: '',
    caliber: '',
    factor: '1',
    grm2: '',
    percentage: '',
    quantity: '',
    unit: 'KG',
    medida: '',
    catalogProductId: null as number | null,
    isMain: false,
  });
  
  // Refs for focus management in material form
  const factorInputRef = useRef<HTMLInputElement>(null);
  const editingFactorInputRef = useRef<HTMLInputElement>(null);
  
  // Material editing states
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editingMaterialData, setEditingMaterialData] = useState<any>(null);

  // Specification Management States
  const [_orderSpecifications, setOrderSpecifications] = useState<any[]>([]);
  const [_specificationsLoading, setSpecificationsLoading] = useState(false);

  // Manufacturing Routes Management States
  const [orderManufacturingRoutes, setOrderManufacturingRoutes] = useState<any[]>([]);
  const [manufacturingRoutesLoading, setManufacturingRoutesLoading] = useState(false);
  const [newManufacturingRoute, setNewManufacturingRoute] = useState({
    process: '',
    machineName: '',
    recordType: 'TEXTO',
  });
  const [editingManufacturingRouteId, setEditingManufacturingRouteId] = useState<string | null>(null);
  const [editingManufacturingRouteData, setEditingManufacturingRouteData] = useState<any>(null);
  // TODO: Route evidences state for future implementation
  // const [routeEvidences, setRouteEvidences] = useState<Record<string, any[]>>({}); // routeId -> evidences
  // const [newRouteEvidence, setNewRouteEvidence] = useState<Record<string, { type: 'TEXT' | 'IMAGE'; value: string }>>({}); // routeId -> evidence data

  // Grupo suggestions (used in editing mode)

  // Catalog process groups for searchable combo

  // Catalog process specs for searchable combo (filtered by selected group)

  // Catalog clients for searchable combo
  const [catalogClients, setCatalogClients] = useState<{id: number; code: string; name: string; email?: string | null; phone?: string | null}[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // ROT-206: Sucursales (branches) for the currently selected client —
  // most clients have none, in which case this just stays empty and the
  // field renders as a no-op single blank option.
  const [clientBranches, setClientBranches] = useState<{ id: number; code: string; name: string }[]>([]);
  const [clientBranchesLoading, setClientBranchesLoading] = useState(false);

  // Quick create client inline form
  const [showQuickClientForm, setShowQuickClientForm] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState({ code: '', name: '', rfc: '', phone: '', email: '' });
  const [quickClientLoading, setQuickClientLoading] = useState(false);
  const [quickClientError, setQuickClientError] = useState('');

  // Quick create product inline form
  const [showQuickProductForm, setShowQuickProductForm] = useState(false);
  const [quickProductForm, setQuickProductForm] = useState({ code: '', name: '', description: '' });
  const [quickProductLoading, setQuickProductLoading] = useState(false);
  const [quickProductError, setQuickProductError] = useState('');

  // Catalog products for searchable combo
  const [catalogProducts, setCatalogProducts] = useState<{id: number; code: string; name: string; description?: string | null}[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

  // Catalog units of measure for combo
  const [catalogUnits, setCatalogUnits] = useState<{id: number; code: string; name: string; abbreviation?: string}[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [unitSearchTerm, setUnitSearchTerm] = useState('');

  // Catalog print types for Orden de Grabado dropdown
  const [catalogPrintTypes, setCatalogPrintTypes] = useState<{ id: number; code: string; name: string }[]>([]);

  // Catalog photocell types for the Tipo Fotocelda dropdowns (ROT-172)
  const [catalogPhotocellTypes, setCatalogPhotocellTypes] = useState<{ id: number; code: string; name: string }[]>([]);

  // Catalog equipment for searchable combo
  const [catalogEquipment, setCatalogEquipment] = useState<{id: number; code: string; name: string; description?: string | null; mangas?: Array<{circunferencia: number; cantidad: number}> | null; equipoGemeloId?: number | null}[]>([]);
  const [, setEquipmentLoading] = useState(false);
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');

  // Catalog colors for Section 3 color dropdowns
  const [catalogColors, setCatalogColors] = useState<{id: number; code: string; name: string; hexColor?: string | null}[]>([]);

  // Catalog raw materials (Materia Prima) for materials section
  const [catalogRawMaterials, setCatalogRawMaterials] = useState<{id: number; code: string; name: string; description?: string | null; factor?: string | null}[]>([]);
  const [rawMaterialsLoading, setRawMaterialsLoading] = useState(false);
  const [showRawMaterialDropdown, setShowRawMaterialDropdown] = useState(false);
  const [rawMaterialSearchTerm, setRawMaterialSearchTerm] = useState('');
  const [editMaterialSearchTerm, setEditMaterialSearchTerm] = useState('');
  const [showEditMaterialDropdown, setShowEditMaterialDropdown] = useState(false);

  // Specification unit dropdown state (uses catalogUnits)
  // Edit mode specification unit dropdown

  // Catalog manufacturing routes for process dropdown
  const [catalogManufRoutes, setCatalogManufRoutes] = useState<{id: number; code: string; name: string; description?: string | null; manufacturingProcess?: string | null}[]>([]);
  const [manufRoutesLoading, setManufRoutesLoading] = useState(false);
  const [showManufRouteDropdown, setShowManufRouteDropdown] = useState(false);
  const [manufRouteSearchTerm, setManufRouteSearchTerm] = useState('');

  // Catalog manufacturing processes (Procesos de Fabricación) — used to detect Stand Pouch (ROT-164)
  const [catalogManufProcesses, setCatalogManufProcesses] = useState<{id: number; code: string; name: string; standPouch: boolean}[]>([]);

  // Catalog process types for process type dropdown
  const [catalogProcessTypes, setCatalogProcessTypes] = useState<{id: number; code: string; name: string; description?: string | null; enablesLamination: boolean; enablesExtrusion: boolean}[]>([]);

  // Route preview popover state
  const [showRoutePopover, setShowRoutePopover] = useState(false);
  const [routePreviewSteps, setRoutePreviewSteps] = useState<{id: number; name: string; sequence: number | null; manufacturingProcess?: string | null; description?: string | null}[]>([]);
  const [routePreviewLoading, setRoutePreviewLoading] = useState(false);
  const [routePreviewProcessType, setRoutePreviewProcessType] = useState('');
  const routePopoverRef = useRef<HTMLDivElement>(null);

  // Load catalog products - Only finished products (Producto Terminado) for production orders
  const loadCatalogProducts = async (search?: string) => {
    try {
      setProductsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('category', 'PRODUCTO_TERMINADO');
      params.append('limit', '25');
      if (search) params.append('search', search);
      const url = `/api/catalogs/products?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.data ?? []);
        setCatalogProducts(items.filter((p: any) => p.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  // Debounced product search
  useEffect(() => {
    if (showProductDropdown) {
      const timer = setTimeout(() => {
        loadCatalogProducts(productSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [productSearchTerm, showProductDropdown]);

  // Handle product selection
  const handleProductSelect = (product: {id: number; code: string; name: string}) => {
    setSelectedProductId(product.id);
    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
    
    // Update form fields - productName and productCode (code is readonly)
    handleEditFormChange('productName', product.name);
    handleEditFormChange('productCode', product.code);
  };

  // Load catalog clients
  const loadCatalogClients = async (search?: string) => {
    try {
      setClientsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '25');
      if (search) params.append('search', search);
      const url = `/api/catalogs/clients?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.data ?? []);
        setCatalogClients(items.filter((c: any) => c.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  // Debounced client search
  useEffect(() => {
    if (showClientDropdown) {
      const timer = setTimeout(() => {
        loadCatalogClients(clientSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [clientSearchTerm, showClientDropdown]);

  // Handle client selection
  const handleClientSelect = (client: {id: number; code: string; name: string}) => {
    setSelectedClientId(client.id);
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
    
    // Update form fields
    handleEditFormChange('customerName', client.name);
    handleEditFormChange('customerCode', client.code);
    // A different client's sucursales have nothing to do with whatever was
    // selected for the previous one.
    handleEditFormChange('branchName', '');
  };

  // ROT-206: reload sucursales whenever the order's customerCode changes
  // (client picked from the dropdown, an existing order loaded for edit, or
  // a REPETICION pre-fill) — a plain fetch-on-change rather than wiring a
  // call into every place customerCode can be set.
  useEffect(() => {
    const customerCode = editForm.customerCode?.trim();
    if (!showEditModal || !customerCode) {
      setClientBranches([]);
      return;
    }
    let cancelled = false;
    setClientBranchesLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ customerCode });
        const res = await fetch(`/api/catalogs/client-branches?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setClientBranches(Array.isArray(data) ? data.filter((b: any) => b.isActive !== false) : []);
        }
      } catch (error) {
        console.error('Error loading client branches:', error);
      } finally {
        if (!cancelled) setClientBranchesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editForm.customerCode, showEditModal]);

  // Quick create client
  const handleQuickCreateClient = async () => {
    if (!quickClientForm.code.trim() || !quickClientForm.name.trim()) {
      setQuickClientError('Código y nombre son requeridos');
      return;
    }
    
    setQuickClientLoading(true);
    setQuickClientError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/catalogs/clients', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: quickClientForm.code.trim(),
          name: quickClientForm.name.trim(),
          rfc: quickClientForm.rfc?.trim() || null,
          phone: quickClientForm.phone?.trim() || null,
          email: quickClientForm.email?.trim() || null,
          isActive: true,
        }),
      });
      
      if (response.ok) {
        const newClient = await response.json();
        await loadCatalogClients();
        setClientSearchTerm(newClient.name);
        setSelectedClientId(newClient.id);
        handleEditFormChange('customerName', newClient.name);
        handleEditFormChange('customerCode', newClient.code);
        setShowQuickClientForm(false);
        setQuickClientForm({ code: '', name: '', rfc: '', phone: '', email: '' });
      } else {
        let errorMsg = 'Error al crear cliente';
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch {}
        setQuickClientError(errorMsg);
      }
    } catch (error) {
      console.error('Error creating client:', error);
      setQuickClientError('Error al crear cliente');
    } finally {
      setQuickClientLoading(false);
    }
  };

  // Quick create product
  const handleQuickCreateProduct = async () => {
    if (!quickProductForm.code.trim() || !quickProductForm.name.trim()) {
      setQuickProductError('Código y nombre son requeridos');
      return;
    }
    
    setQuickProductLoading(true);
    setQuickProductError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/catalogs/products', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: quickProductForm.code.trim(),
          name: quickProductForm.name.trim(),
          description: quickProductForm.description?.trim() || null,
          category: 'PRODUCTO_TERMINADO',
          isActive: true,
        }),
      });
      
      if (response.ok) {
        const newProduct = await response.json();
        await loadCatalogProducts();
        setProductSearchTerm(newProduct.name);
        setSelectedProductId(newProduct.id);
        handleEditFormChange('productName', newProduct.name);
        handleEditFormChange('productCode', newProduct.code);
        setShowQuickProductForm(false);
        setQuickProductForm({ code: '', name: '', description: '' });
      } else {
        let errorMsg = 'Error al crear producto';
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch {}
        setQuickProductError(errorMsg);
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setQuickProductError('Error al crear producto');
    } finally {
      setQuickProductLoading(false);
    }
  };

  // Load catalog units of measure
  const loadCatalogUnits = async () => {
    try {
      setUnitsLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/catalogs/units?limit=50', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCatalogUnits(data.filter((u: any) => u.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog units:', error);
    } finally {
      setUnitsLoading(false);
    }
  };

  // Load catalog print types (Tipo de Impresión, Orden de Grabado)
  const loadCatalogPrintTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/print-types', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogPrintTypes(data.filter((t: any) => t.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog print types:', error);
    }
  };

  // Load catalog photocell types (Tipo Fotocelda, ROT-172)
  const loadCatalogPhotocellTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/photocell-types', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogPhotocellTypes(data.filter((t: any) => t.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog photocell types:', error);
    }
  };

  // Load catalog process types
  const loadCatalogProcessTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/process-types', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogProcessTypes(data.filter((pt: any) => pt.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog process types:', error);
    }
  };

  // Handle unit selection for edit form
  const handleUnitSelect = (unit: {id: number; code: string; name: string}) => {
    setUnitSearchTerm(unit.name);
    setShowUnitDropdown(false);
    handleEditFormChange('unit', unit.name);
  };

  // Load catalog equipment
  const loadCatalogEquipment = async (search?: string): Promise<typeof catalogEquipment> => {
    try {
      setEquipmentLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (search) params.append('search', search);
      const url = `/api/catalogs/equipment?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const filtered = data.filter((e: any) => e.isActive !== false);
        setCatalogEquipment(filtered);
        return filtered;
      }
    } catch (error) {
      console.error('Error loading catalog equipment:', error);
    } finally {
      setEquipmentLoading(false);
    }
    return [];
  };

  // Debounced equipment search
  useEffect(() => {
    if (showEquipmentDropdown) {
      const timer = setTimeout(() => {
        loadCatalogEquipment(equipmentSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [equipmentSearchTerm, showEquipmentDropdown]);

  // Load integration config to set default import date
  const loadIntegrationConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/production-orders/integration/config', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const days = data.daysToLookBack ?? 2;
        setImportFecha(addDaysToDateString(getMexicoDateString(), -days));
      }
    } catch {
      // keep the default (2 days ago)
    }
  };

  // Load catalog colors for Section 3 (loaded once on mount)
  const loadCatalogColors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/colors', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogColors(data);
      }
    } catch (error) {
      console.error('Error loading catalog colors:', error);
    }
  };

  // Load colors and integration config once on mount
  useEffect(() => {
    loadCatalogColors();
    loadIntegrationConfig();
  }, []);

  // Close route popover when clicking outside
  useEffect(() => {
    if (!showRoutePopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (routePopoverRef.current && !routePopoverRef.current.contains(e.target as Node)) {
        setShowRoutePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRoutePopover]);

  // Reset route popover when edit modal closes
  useEffect(() => {
    if (!showEditModal) {
      setShowRoutePopover(false);
      setRoutePreviewSteps([]);
      setRoutePreviewProcessType('');
    }
  }, [showEditModal]);

  // Load catalog raw materials (Materia Prima + Tintas) using optimized endpoint
  const loadCatalogRawMaterials = async (search?: string) => {
    try {
      setRawMaterialsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '25');
      if (search) params.append('search', search);
      
      const response = await fetch(`/api/catalogs/materials?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCatalogRawMaterials(data);
      }
    } catch (error) {
      console.error('Error loading catalog raw materials:', error);
    } finally {
      setRawMaterialsLoading(false);
    }
  };

  // Debounced raw material search
  useEffect(() => {
    if (showRawMaterialDropdown) {
      const timer = setTimeout(() => {
        loadCatalogRawMaterials(rawMaterialSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [rawMaterialSearchTerm, showRawMaterialDropdown]);

  // Debounced raw material search for the inline material-row editor — reuses
  // the same catalog list/loader as "+ Agregar material" above; only the
  // search term/dropdown-visibility state is separate, so the two searches
  // can't clobber each other's input (ROT-196).
  useEffect(() => {
    if (showEditMaterialDropdown) {
      const timer = setTimeout(() => {
        loadCatalogRawMaterials(editMaterialSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [editMaterialSearchTerm, showEditMaterialDropdown]);

  const handleRawMaterialSelect = (material: {id: number; code: string; name: string; description?: string | null; factor?: string | null; medida?: string | null}) => {
    setRawMaterialSearchTerm(material.name);
    setShowRawMaterialDropdown(false);
    const catalogFactor = material.factor && parseFloat(material.factor) ? material.factor : '1';
    const caliber = parseFloat(newMaterial.caliber) || 0;
    const grm2 = caliber * (parseFloat(catalogFactor) || 1);
    setNewMaterial({
      ...newMaterial,
      material: [material.code, material.name].filter(Boolean).join(' - '),
      factor: catalogFactor,
      grm2: caliber > 0 ? grm2.toFixed(4) : newMaterial.grm2,
      medida: material.medida || '',
      catalogProductId: material.id,
    });
  };

  // Same as handleRawMaterialSelect above, but for the inline material-row
  // editor (ROT-196) — writes into editingMaterialData instead of newMaterial.
  const handleEditMaterialSelect = (material: {id: number; code: string; name: string; description?: string | null; factor?: string | null; medida?: string | null}) => {
    setEditMaterialSearchTerm(material.name);
    setShowEditMaterialDropdown(false);
    const catalogFactor = material.factor && parseFloat(material.factor) ? material.factor : '1';
    const caliber = parseFloat(editingMaterialData?.caliber) || 0;
    const grm2 = caliber * (parseFloat(catalogFactor) || 1);
    setEditingMaterialData({
      ...editingMaterialData,
      material: [material.code, material.name].filter(Boolean).join(' - '),
      factor: catalogFactor,
      grm2: caliber > 0 ? grm2.toFixed(4) : editingMaterialData?.grm2,
      medida: material.medida || '',
      catalogProductId: material.id,
    });
  };

  // Load catalog manufacturing routes for process dropdown
  const loadCatalogManufRoutes = async (search?: string) => {
    try {
      setManufRoutesLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '25');
      if (search) params.append('search', search);
      
      const response = await fetch(`/api/catalogs/manufacturing-routes?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCatalogManufRoutes(data.filter((r: any) => r.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading catalog manufacturing routes:', error);
    } finally {
      setManufRoutesLoading(false);
    }
  };

  // Load catalog manufacturing processes (Procesos de Fabricación) — used to detect Stand Pouch (ROT-164)
  const loadCatalogManufProcesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/manufacturing-processes', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogManufProcesses(data);
      }
    } catch (error) {
      console.error('Error loading catalog manufacturing processes:', error);
    }
  };

  // ROT-164: Stand Pouch is enabled for the order when any of its manufacturing
  // routes (Section 4) is linked — via the route catalog's free-text
  // "Proceso de Fabricación" name — to a catalog process with standPouch=true.
  const isStandPouchEnabled = () => {
    return orderManufacturingRoutes.some((route: any) => {
      const catalogRoute = catalogManufRoutes.find(r => r.name === route.process);
      if (!catalogRoute?.manufacturingProcess) return false;
      const process = catalogManufProcesses.find(p => p.name === catalogRoute.manufacturingProcess);
      return !!process?.standPouch;
    });
  };

  // Debounced manufacturing route search
  useEffect(() => {
    if (showManufRouteDropdown) {
      const timer = setTimeout(() => {
        loadCatalogManufRoutes(manufRouteSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [manufRouteSearchTerm, showManufRouteDropdown]);

  // Auto-load catalog manufacturing routes for a given process type into Section 4
  const loadRoutePreview = async (processTypeName: string) => {
    if (!processTypeName) {
      setRoutePreviewSteps([]);
      setRoutePreviewProcessType('');
      return;
    }
    setRoutePreviewLoading(true);
    setRoutePreviewProcessType(processTypeName);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ processType: processTypeName, limit: '100' });
      const response = await fetch(`/api/catalogs/manufacturing-routes?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) { setRoutePreviewSteps([]); return; }
      const routes: any[] = await response.json();
      const active = routes.filter(r => r.isActive !== false);
      const sorted = [...active].sort((a, b) => {
        if (a.sequence == null && b.sequence == null) return 0;
        if (a.sequence == null) return 1;
        if (b.sequence == null) return -1;
        return a.sequence - b.sequence;
      });
      setRoutePreviewSteps(sorted);
    } catch { setRoutePreviewSteps([]); } finally { setRoutePreviewLoading(false); }
  };

  // Ref-based lock: prevents concurrent executions of loadRoutesForProcessType.
  // If a call is already in flight, any new call for the same order is discarded.
  const routeReplaceInProgress = useRef<string | null>(null);

  const loadRoutesForProcessType = async (processTypeName: string, orderId: string) => {
    if (!processTypeName || !orderId) return;

    // Discard concurrent calls — only the first one per order proceeds
    if (routeReplaceInProgress.current === orderId) return;
    routeReplaceInProgress.current = orderId;

    try {
      const token = localStorage.getItem('token');

      // Step 1: Fetch catalog routes for the selected process type
      const params = new URLSearchParams({ processType: processTypeName, limit: '100' });
      const catalogRes = await fetch(`/api/catalogs/manufacturing-routes?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!catalogRes.ok) {
        await loadOrderManufacturingRoutes(orderId);
        return;
      }
      const routes: any[] = await catalogRes.json();
      const activeRoutes = routes.filter(r => r.isActive !== false);

      // Step 2: Sort by catalog sequence (nulls last)
      const sorted = [...activeRoutes].sort((a, b) => {
        if (a.sequence == null && b.sequence == null) return 0;
        if (a.sequence == null) return 1;
        if (b.sequence == null) return -1;
        return a.sequence - b.sequence;
      });

      // Step 3: Single atomic call — backend deletes all existing and inserts new ones
      // in one transaction, eliminating any race condition
      await fetch(`/api/production-orders/${orderId}/manufacturing-routes/replace-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          routes: sorted.map((r, i) => ({
            sequence: r.sequence ?? (i + 1),
            process: r.name,
            machineName: null,
            recordType: 'TEXTO',
          })),
        }),
      });

      // Step 4: Single state refresh at the end
      await loadOrderManufacturingRoutes(orderId);
    } catch (error) {
      console.error('Error loading routes for process type:', error);
    } finally {
      routeReplaceInProgress.current = null;
    }
  };

  // Load current manufacturing process for each authorized order
  const loadCurrentProcesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/production-orders/current-processes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const rows: { orderId: string; currentProcess: string }[] = await res.json();
        const map: Record<string, string> = {};
        rows.forEach(r => { map[r.orderId] = r.currentProcess; });
        setCurrentProcessMap(map);
      }
    } catch (err) {
      console.error('Error loading current processes:', err);
    }
  };

  // Load orders
  const loadOrders = async (currentFilters: ProductionOrderFilters = filters) => {
    try {
      const response = await fetchProductionOrders(currentFilters);
      setOrders(Array.isArray(response.data) ? response.data : []);
      if (response.pagination) setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadSavedCoextrusionIds = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/coextrusion-orders/saved-ids', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const pairs: { id: string; productionOrderId: string; linkedOrders: any[] }[] = await res.json();
        setSavedCoextrusionMap(new Map(pairs.map(p => [p.productionOrderId, p.id])));
        setCoextLinkedOrdersMap(new Map(pairs.map(p => [p.productionOrderId, p.linkedOrders || []])));
      }
    } catch {}
  };

  useEffect(() => {
    loadOrders();
    loadCatalogUnits();
    loadSavedCoextrusionIds();
    loadCurrentProcesses();
  }, []);

  // Derived ID string — changes only when the set of order IDs changes, not when order data changes.
  // useEffect below depends on this string, so it won't fire on in-place data patches.
  const visibleOrderIds = useMemo(
    () => orders.map((o: ProductionOrder) => o.id).join(','),
    [orders]
  );

  // Load engraving orders for visible orders — only re-fetches when the page's order IDs change
  useEffect(() => {
    if (!visibleOrderIds) return;
    const token = localStorage.getItem('token');
    fetch(`/api/engraving-orders?orderIds=${visibleOrderIds}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then((list: any[]) => {
        if (!Array.isArray(list)) return;
        const map: Record<string, any> = {};
        list.forEach(eg => { map[eg.productionOrderId] = eg; });
        setEngravingOrdersMap(map);
      })
      .catch(err => console.error('Error loading engraving orders:', err));
  }, [visibleOrderIds]);

  // Helper function to get unit name from code
  const getUnitName = (unitCode: string | null | undefined): string => {
    if (!unitCode) return '-';
    const unit = catalogUnits.find(u => u.code === unitCode);
    return unit?.name || unitCode;
  };

  // Helper function to get unit abbreviation from code — used in the compact
  // materials table column, where "kg"/"gr"/"mm" reads better than the full
  // catalog name ("Kilogramos"). Falls back to the full name, then the raw
  // stored value, so ComercialGS-imported materials (whose stored unit
  // usually doesn't match any local catalog code) still show their
  // already-abbreviated raw value instead of going blank.
  const getUnitAbbreviation = (unitCode: string | null | undefined): string => {
    if (!unitCode) return '-';
    const unit = catalogUnits.find(u => u.code === unitCode);
    return unit?.abbreviation || unit?.name || unitCode;
  };

  // ROT-177: some rolls carry several designs/flavors, each with its own
  // barcode — the DB/notes representation stays a single comma-separated
  // string (no schema/migration needed, every existing single-code order is
  // just "a list of one"), but the edit form works with it as a real list
  // for the "+ Agregar" UI. A lone existing value with no comma parses to a
  // one-item array exactly like before this ticket.
  const parseCodigosBarras = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  };
  const joinCodigosBarras = (codes: string[]): string =>
    codes.map(c => c.trim()).filter(Boolean).join(', ');

  // ROT-176: order.unit isn't reliably a catalog code the way a material's
  // unit is — orders imported before ROT-169 can have it stored as the raw
  // external abbreviation OR the catalog's full name (same ambiguity
  // handleEdit's foundUnit lookup already handles when loading the edit
  // form). Match against code/abbreviation/name so the Lista de Órdenes
  // "Cantidad" column shows the short abbreviation ("kg") regardless of
  // which form the stored value happens to be in, instead of the full name.
  const getOrderUnitAbbreviation = (unitValue: string | null | undefined): string => {
    if (!unitValue) return '-';
    const v = unitValue.trim().toLowerCase();
    const unit = catalogUnits.find(u =>
      u.code?.toLowerCase() === v ||
      u.abbreviation?.toLowerCase() === v ||
      u.name?.toLowerCase() === v
    );
    return unit?.abbreviation || unit?.name || unitValue;
  };

  // Dynamic search effect — re-fetch on any filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrders({ ...filters, page: 1 });
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.search, filters.orderNumber, filters.status, filters.orderType, filters.priority, filters.customerName, filters.branchName, filters.productCode, filters.fromDate, filters.toDate]);

  const handleFilterChange = (key: keyof ProductionOrderFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (page: number) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    loadOrders(updatedFilters);
  };

  // Sort handler
  const handleSort = (field: keyof ProductionOrder) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted orders
  const getSortedOrders = () => {
    let base = orders;
    if (processFilter.trim()) {
      const lc = processFilter.trim().toLowerCase();
      base = base.filter(o => {
        const proc = currentProcessMap[o.id] || '';
        return proc.toLowerCase().includes(lc);
      });
    }
    if (!sortField) return base;
    
    return [...base].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Handle null/undefined values - nulls always go to the end
      const aIsNull = aValue === null || aValue === undefined;
      const bIsNull = bValue === null || bValue === undefined;
      
      if (aIsNull && bIsNull) return 0; // Both null, they're equal
      if (aIsNull) return 1; // a is null, put it after b
      if (bIsNull) return -1; // b is null, put it after a
      
      // Compare values
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Get sort icon
  const getSortIcon = (field: keyof ProductionOrder) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  // Action handlers
  const handleView = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setShowPrintModal(true);
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('print-content');
    if (!element) {
      alert('El documento aún se está cargando, espera un momento e intenta de nuevo.');
      return;
    }
    if (!selectedOrder) return;

    const opt = {
      margin: 0.3,
      filename: `Orden_Produccion_${selectedOrder.orderNumber}_${selectedOrder.lotCode || 'Sin_Lote'}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait' as const
      }
    };

    html2pdf().set(opt).from(element).save().catch((err: any) => {
      console.error('Error generating PDF:', err);
      alert('Error al generar el PDF. Por favor intenta de nuevo.');
    });
  };

  const handleSendEmail = (order: ProductionOrder) => {
    setSelectedOrder(order);
    setEmailRecipient('');
    setShowEmailModal(true);
  };

  const sendOrderEmail = async () => {
    if (!selectedOrder || !emailRecipient) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/production-orders/${selectedOrder.id}/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipientEmail: emailRecipient }),
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Email enviado exitosamente');
        setShowEmailModal(false);
        setEmailRecipient('');
      } else {
        alert(data.error || 'Error al enviar el email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error al enviar el email');
    }
  };

  // ============ ENGRAVING ORDER HANDLERS ============
  const handleOpenEngravingModal = (order: ProductionOrder, existing?: any) => {
    setEngravingModalOrder(order);
    setEngravingModalData(existing || null);
    const emptyColors = ['', '', '', '', '', '', '', ''];
    let _pn: any = {};
    try { if (order.notes) _pn = JSON.parse(order.notes); } catch {}
    const _ps = _pn.processSpecs || {};
    if (existing) {
      setEngravingFormData({
        lineaje: existing.lineaje || '',
        tipoPrint: existing.tipoPrint || 'FLEXO',
        espesorMaterial: existing.espesorMaterial || '',
        especFotocelda: existing.especFotocelda || (_ps.photocell?.width && _ps.photocell?.height ? `${_ps.photocell.width} × ${_ps.photocell.height} mm` : ''),
        fotoceldaColor: existing.fotoceldaColor || _ps.fotoceldaColor || '',
        fotoceldaColorNombre: existing.fotoceldaColorNombre || '',
        cobrarGrabados: existing.cobrarGrabados || false,
        colores: existing.colores?.length === 8 ? existing.colores : [...(existing.colores || []), ...emptyColors].slice(0, 8),
        observaciones: existing.observaciones || '',
        fechaEntrega: existing.fechaEntrega || '',
        anchoIndividual: existing.anchoIndividual || _ps.individualWidth || '',
        bobinasAlEje: existing.bobinasAlEje || _ps.bobinCount || '',
        repeticionIndividual: existing.repeticionIndividual || _ps.individualRepetition || '',
        bobinasAlDesarrollo: existing.bobinasAlDesarrollo || _ps.repetitionCount || '',
        rodilloFinal: existing.rodilloFinal || _ps.selectedMangaCircunferencia || _ps.cylinderCircumference || '',
        flexo: existing.flexo || '',
        codigoBarras: existing.codigoBarras || _ps.barCode || _ps.codigoBarras || (order as any).codigoBarras || _pn.codigoBarras || '',
        revisionNumero: existing.revisionNumero || '',
      });
    } else {
      // Map colorRows from production order to 8 engraving color slots
      const colorRowsFromPO = Array.from({ length: 8 }, (_, i) => {
        const row = _ps.colorRows?.[i];
        if (!row) return '';
        return [row.colorBase, row.pantone].filter(Boolean).join(' / ');
      });
      const hasColors = colorRowsFromPO.some(c => c !== '');

      setEngravingFormData({
        lineaje: '', tipoPrint: 'FLEXO', espesorMaterial: '',
        especFotocelda: (_ps.photocell?.width && _ps.photocell?.height ? `${_ps.photocell.width} × ${_ps.photocell.height} mm` : ''),
        fotoceldaColor: _ps.fotoceldaColor || '',
        fotoceldaColorNombre: '',
        cobrarGrabados: false,
        colores: hasColors ? colorRowsFromPO : emptyColors,
        observaciones: '', fechaEntrega: '',
        anchoIndividual: _ps.individualWidth || '',
        bobinasAlEje: _ps.bobinCount || '',
        repeticionIndividual: _ps.individualRepetition || '',
        bobinasAlDesarrollo: _ps.repetitionCount || '',
        rodilloFinal: _ps.selectedMangaCircunferencia || _ps.cylinderCircumference || '',
        flexo: '',
        codigoBarras: _ps.barCode || _ps.codigoBarras || (order as any).codigoBarras || _pn.codigoBarras || '',
        revisionNumero: '',
      });
    }
    setEngravingMissingFields([]);
    setEngravingSaveError(null);
    setShowEngravingModal(true);
    if (!catalogPrintTypes.length) loadCatalogPrintTypes();
    if (!catalogPhotocellTypes.length) loadCatalogPhotocellTypes();
  };

  const toggleExpandEngraving = (orderId: string) => {
    setExpandedEngravingOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) { next.delete(orderId); } else { next.add(orderId); }
      return next;
    });
  };

  const toggleInventoryMovements = async (orderId: string) => {
    const isExpanded = expandedInventoryOrders.has(orderId);
    setExpandedInventoryOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) { next.delete(orderId); } else { next.add(orderId); }
      return next;
    });
    if (!isExpanded && !inventoryMovementsMap[orderId]) {
      setInventoryMovementsLoading(prev => new Set(prev).add(orderId));
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/production-orders/${orderId}/inventory-movements`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInventoryMovementsMap(prev => ({ ...prev, [orderId]: Array.isArray(data) ? data : [] }));
        }
      } catch (err) {
        console.error('Error loading inventory movements:', err);
      } finally {
        setInventoryMovementsLoading(prev => { const next = new Set(prev); next.delete(orderId); return next; });
      }
    }
  };

  const defaultInvFilter = () => ({
    fecha: '', tipoDoc: '', concepto: '', codigo: '', descripcion: '',
    catalogo: '', cantidad: '', unidad: '', lote: '', almacen: '',
    departamento: '', empleado: '', tipo: '',
  });

  const getInvFilter = (orderId: string) =>
    inventoryMovementsFilters[orderId] || defaultInvFilter();

  const setInvFilterField = (orderId: string, field: string, value: string) => {
    setInventoryMovementsFilters(prev => ({
      ...prev,
      [orderId]: { ...getInvFilter(orderId), [field]: value },
    }));
  };

  const clearInvFilters = (orderId: string) => {
    setInventoryMovementsFilters(prev => ({ ...prev, [orderId]: defaultInvFilter() }));
  };

  const applyInvFilters = (movs: any[], orderId: string): any[] => {
    const f = getInvFilter(orderId);
    return movs.filter(m => {
      const fechaStr = m.fecha ? new Date(m.fecha).toLocaleDateString('es-MX') : '';
      if (f.fecha && !fechaStr.includes(f.fecha)) return false;
      if (f.tipoDoc && (m.tipoDoc || '') !== f.tipoDoc) return false;
      if (f.concepto && !(m.concepto || '').toLowerCase().includes(f.concepto.toLowerCase())) return false;
      if (f.codigo && !(m.codigo || '').toLowerCase().includes(f.codigo.toLowerCase())) return false;
      if (f.descripcion && !(m.descripcion || '').toLowerCase().includes(f.descripcion.toLowerCase())) return false;
      if (f.catalogo && !(m.catalogProductName || '').toLowerCase().includes(f.catalogo.toLowerCase())) return false;
      if (f.cantidad && m.cantidad != null && !String(parseFloat(m.cantidad).toFixed(2)).includes(f.cantidad)) return false;
      if (f.unidad && (m.unidad || '') !== f.unidad) return false;
      if (f.lote && !(m.lote || '').toLowerCase().includes(f.lote.toLowerCase())) return false;
      if (f.almacen && (m.almacen || '') !== f.almacen) return false;
      if (f.departamento && (m.departamento || '') !== f.departamento) return false;
      if (f.empleado && !(m.empleado || '').toLowerCase().includes(f.empleado.toLowerCase())) return false;
      if (f.tipo === 'entrada' && !m.esEntrada) return false;
      if (f.tipo === 'salida' && !m.esSalida) return false;
      return true;
    });
  };

  const hasActiveInvFilters = (orderId: string): boolean => {
    const f = getInvFilter(orderId);
    return Object.values(f).some(v => v !== '');
  };

  const handleSaveEngravingOrder = async () => {
    if (!engravingModalOrder) return;
    const missing: string[] = [];
    if (!engravingFormData.lineaje.trim()) missing.push('Lineaje');
    if (!engravingFormData.espesorMaterial) missing.push('Espesor del Material');
    if (engravingFormData.fotoceldaColor === 'COLOR' && !engravingFormData.fotoceldaColorNombre.trim()) {
      missing.push('¿Qué Color?');
    }
    setEngravingMissingFields(missing);
    if (missing.length > 0) return;

    setEngravingSaveError(null);
    setEngravingFormSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      let result: any;
      if (engravingModalData?.id) {
        const res = await fetch(`/api/engraving-orders/${engravingModalData.id}`, {
          method: 'PUT', headers, body: JSON.stringify(engravingFormData),
        });
        result = await res.json();
        // ROT-173: this request's response was never checked for success —
        // a failed save (e.g. server error) still fell through to closing
        // the modal and caching `result` (the error body, not the updated
        // row) as if it were the saved engraving order, so the user saw no
        // indication their edits (including Tipo de Impresión) never
        // persisted, and the next print kept showing whatever was already
        // in the database.
        if (!res.ok) {
          setEngravingSaveError(result?.error || 'No se pudo guardar la Orden de Grabado. Intenta de nuevo.');
          return;
        }
      } else {
        const res = await fetch('/api/engraving-orders', {
          method: 'POST', headers,
          body: JSON.stringify({ ...engravingFormData, productionOrderId: engravingModalOrder.id }),
        });
        if (res.status === 409) {
          const data = await res.json();
          result = data.existing;
        } else {
          result = await res.json();
          if (!res.ok) {
            setEngravingSaveError(result?.error || 'No se pudo guardar la Orden de Grabado. Intenta de nuevo.');
            return;
          }
        }
      }
      setEngravingOrdersMap(prev => ({ ...prev, [engravingModalOrder.id]: result }));
      setShowEngravingModal(false);
    } catch (err) {
      console.error('Error saving engraving order:', err);
      setEngravingSaveError('Error de conexión al guardar la Orden de Grabado. Intenta de nuevo.');
    } finally {
      setEngravingFormSaving(false);
    }
  };

  // Get next sequential order number from backend
  const getNextOrderNumber = async (): Promise<string> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/production-orders/next-number', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate order number');
      }

      const data = await response.json();
      return data.orderNumber;
    } catch (error) {
      console.error('Error getting next order number:', error);
      throw error;
    }
  };

  const handleNewOrder = async () => {
    try {
      // Reset selectedOrder to null (indicates new order)
      setSelectedOrder(null);
      
      // Generate unique order number from backend
      const newOrderNumber = await getNextOrderNumber();
    
    // Reset editForm to empty state
    setEditForm({
      orderNumber: newOrderNumber,
      customerName: '',
      productCode: '',
      productName: '',
      codigoBarras: [] as string[],
      orderType: '',
      sourceOrderNumber: '',
      processType: '',
      customerCode: '',
      branchName: '',
      purchaseOrder: '',
      purchaseDate: '',
      productionDate: '',
      commitmentDate: '',
      deliveryDate: '',
      machineName: '',
      equipoPreprensa: '',
      quantityPlanned: '',
      unit: 'kg',
      lotCode: '',
      priority: '2',
      dueDate: '',
      printConfigImage: undefined,
      materials: [{
        itemName: '',
        caliber: '',
        factor: '1',
        grm2: '',
        percentage: '',
        quantity: '',
        dispersionFactor: '',
        notes: ''
      }],
      processSpecs: {
        printType: 'FLEXO',
        individualWidth: '',
        bobinCount: '',
        printWidth: '',
        reelWidth: '',
        baggingCutWidth: '',
        substrateWidth: '',
        barCode: '',
        individualRepetition: '',
        repetitionCount: '',
        cylinderCircumference: '',
        cylinderCount: '',
        authorizedRevision: '',
        cutLine: false,
        footHouse: false,
        photocell: { width: '', height: '' },
        pantoneColors: [] as string[],
        adhesiveLamination: '',
        laminationCatalyst: '',
        trilaminationAdhesive: '',
        trilaminationCatalyst: '',
        innerDiameter: '',
        innerDiameterUnit: 'mm',
        outerDiameter: '',
        outerDiameterUnit: 'mm',
        windingDirection: '',
        figureNumber: '',
        photocellSide: 'CENTRADA',
      fotoceldaColor: '',
        packaging: '',
        continuousRepetition: false,
        colorRows: normalizeColorRows([]),
      },
      observations: '',
      label: '',
      designUrl: '',
    });
    
    setEditFormErrors({});
    setCreateServerError('');
    setHistoryMatchInfo(null);
    setHistoryMatchAlert(null);
    setHistoryMatchLoading(false);
    
    // Clear material/specification data
    setOrderMaterials([]);
    setEditingMaterialId(null); // ROT-220: don't carry over a stale edit from a previous new-order session
    setEditingMaterialData(null);
    setExpectedResults(null);
    setOrderSpecifications([]);
    setOrderManufacturingRoutes([]);
    setPendingClonedRoutes([]); // ROT-220: don't carry over a route staged by a previous, abandoned Clonar
    
    // Clear client selection state
    setClientSearchTerm('');
    setSelectedClientId(null);
    setShowClientDropdown(false);
    
    // Clear product selection state
    setProductSearchTerm('');
    setSelectedProductId(null);
    setShowProductDropdown(false);
    
    // Clear unit selection state
    setUnitSearchTerm('Kilogramos');
    setShowUnitDropdown(false);
    
    // Clear equipment selection state
    setEquipmentSearchTerm('');
    setShowEquipmentDropdown(false);
      
    // Open modal immediately for better UX
    setShowEditModal(true);
    
    // Load catalogs in parallel (only if not already loaded)
    await Promise.all([
      catalogClients.length === 0 ? loadCatalogClients() : Promise.resolve(),
      catalogProducts.length === 0 ? loadCatalogProducts() : Promise.resolve(),
      catalogUnits.length === 0 ? loadCatalogUnits() : Promise.resolve(),
      catalogEquipment.length === 0 ? loadCatalogEquipment() : Promise.resolve(),
      catalogProcessTypes.length === 0 ? loadCatalogProcessTypes() : Promise.resolve(),
    ]);
    } catch (error) {
      console.error('Error creating new order:', error);
      alert('Error al generar número de orden. Por favor, intente de nuevo.');
    }
  };

  const handleEdit = async (order: ProductionOrder) => {
    setSelectedOrder(order);
    loadOrderIntoEditForm(order);
    
    // Set search terms immediately
    setClientSearchTerm(order.customerName || '');
    setProductSearchTerm(order.productName || '');
    // Open modal immediately for better UX
    setShowEditModal(true);
    
    // Load all data in parallel for faster loading
    const [materialsData, expectedResultsData] = await Promise.all([
      // Order-specific data
      loadOrderMaterials(order.id),
      loadExpectedResults(order.id),
      loadOrderSpecifications(order.id),
      loadOrderManufacturingRoutes(order.id),
      // Catalogs (only if not already loaded)
      catalogClients.length === 0 ? loadCatalogClients() : Promise.resolve(),
      catalogProducts.length === 0 ? loadCatalogProducts() : Promise.resolve(),
      catalogUnits.length === 0 ? loadCatalogUnits() : Promise.resolve(),
      catalogProcessTypes.length === 0 ? loadCatalogProcessTypes() : Promise.resolve(),
      catalogEquipment.length === 0 ? loadCatalogEquipment() : Promise.resolve(catalogEquipment),
      catalogManufRoutes.length === 0 ? loadCatalogManufRoutes() : Promise.resolve(),
      catalogManufProcesses.length === 0 ? loadCatalogManufProcesses() : Promise.resolve(),
    ]);

    // Materials imported from ComercialGS always arrive with percentage
    // forced to 0 (the external payload never includes one) — recalculating
    // here, right on open, means the % column reflects reality immediately
    // instead of only after the user happens to touch Factor Desperdicio or
    // a material (the previously reported ROT-156 behavior). Only do this
    // when something actually needs fixing: recalculateExpectedResults always
    // re-saves expected results unconditionally, and that PUT bumps the
    // parent order's updated_at (see ROT-163) — merely viewing an
    // already-correct order must not count as an edit for that ranking.
    // Pass the freshly loaded values explicitly instead of relying on
    // `expectedResults`/`editForm` state, which haven't been updated in this
    // closure yet.
    const hasStuckPercentage = (materialsData || []).some(
      (mat: any) => (parseFloat(mat.quantity) || 0) > 0 && (parseFloat(mat.percentage) || 0) === 0
    );
    if (hasStuckPercentage) {
      await recalculateExpectedResults(order.id, materialsData, false, {
        wasteFactor: parseFloat(expectedResultsData?.expectedFactor) || 0,
        plannedQty: parseFloat(order.quantityPlanned) || 0,
      });
    }

    // Find unit name for display after catalogs are loaded. Orders imported
    // from Comercia before the ROT-169 fix have `order.unit` stored as the
    // raw external abbreviation (e.g. "KG") rather than the catalog's full
    // name — matching only `code` missed those, since the abbreviation
    // typically lives in the catalog's `abbreviation` field instead. Falling
    // back through `name` too covers orders where `unit` already holds the
    // full name (the normal case for manually-created orders).
    const orderUnitLower = (order.unit || '').trim().toLowerCase();
    const foundUnit = catalogUnits.find(u =>
      u.code?.toLowerCase() === orderUnitLower ||
      u.abbreviation?.toLowerCase() === orderUnitLower ||
      u.name?.toLowerCase() === orderUnitLower
    );
    setUnitSearchTerm(foundUnit?.name || order.unit || 'Kilogramos');
  };

  const handleCloneOrder = async (order: ProductionOrder) => {
    try {
      // Default processSpecs structure
      const defaultProcessSpecs = {
        printType: 'FLEXO',
        individualWidth: '',
        bobinCount: '',
        printWidth: '',
        reelWidth: '',
        baggingCutWidth: '',
        substrateWidth: '',
        barCode: '',
        individualRepetition: '',
        repetitionCount: '',
        cylinderCircumference: '',
        cylinderCount: '',
        authorizedRevision: '',
        cutLine: false,
        footHouse: false,
        photocell: { width: '', height: '' },
        pantoneColors: [] as string[],
        adhesiveLamination: '',
        laminationCatalyst: '',
        trilaminationAdhesive: '',
        trilaminationCatalyst: '',
        innerDiameter: '',
        outerDiameter: '',
        windingDirection: '',
        figureNumber: '',
        photocellSide: 'CENTRADA',
      fotoceldaColor: '',
        packaging: '',
      };

      // Parse notes to get original data
      let parsedNotes = {
        orderType: '',
        processType: '',
        purchaseOrder: '',
        purchaseDate: '',
        productionDate: '',
        commitmentDate: '',
        deliveryDate: '',
        processSpecs: defaultProcessSpecs,
        observations: '',
      };

      if (order.notes) {
        try {
          parsedNotes = JSON.parse(order.notes);
        } catch (e) {
          console.log('Could not parse notes');
        }
      }

      // Generate new order number from backend
      const newOrderNumber = await getNextOrderNumber();

    // ROT-220: fetch the source order's real materials structure and
    // manufacturing route so the clone actually carries them forward,
    // instead of starting empty (the bug this ticket fixes). Values are
    // copied verbatim, not recalculated — unlike Repetición, Clonar also
    // copies quantityPlanned verbatim, so the source's percentage/quantity
    // ratios still apply as-is to the new order.
    const token = localStorage.getItem('token');
    let clonedMaterials: any[] = [];
    let clonedRoutes: any[] = [];
    let clonedExpectedResults: any = null;
    try {
      const [materialsRes, routesRes, expectedRes] = await Promise.all([
        fetch(`/api/production-orders/${order.id}/materials`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/production-orders/${order.id}/manufacturing-routes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/production-orders/${order.id}/expected-results`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (materialsRes.ok) {
        const sourceMaterials = await materialsRes.json();
        clonedMaterials = sourceMaterials.map((mat: any) => ({
          id: crypto.randomUUID(), // ROT-220: see syncNewOrderMaterialsDisplay
          itemName: mat.material,
          caliber: mat.caliber ?? '',
          factor: mat.factor ?? '1',
          grm2: mat.grm2 ?? '',
          percentage: mat.percentage ?? '',
          quantity: mat.quantity ?? '',
          unit: mat.unit || '-',
          medida: mat.medida || '',
          isMain: mat.isMain ?? false,
          catalogProductId: mat.catalogProductId ?? null,
          dispersionFactor: '',
          notes: '',
        }));
      }
      if (routesRes.ok) {
        const sourceRoutes = await routesRes.json();
        clonedRoutes = sourceRoutes.map((r: any, i: number) => ({
          sequence: r.sequence ?? (i + 1),
          process: r.process,
          // Structural fields only — machine assignment is left for the
          // cloned order to set fresh, same as Repetición already does.
          machineName: null,
          recordType: r.recordType || 'TEXTO',
        }));
      }
      // ROT-220: without this, the clone's copied materials (percentage/
      // quantity computed against the SOURCE order's Factor Desperdicio)
      // would show alongside a blank Factor Desperdicio field, and the
      // first edit to anything would silently re-derive everything at a
      // factor of 0.
      if (expectedRes.ok) {
        clonedExpectedResults = await expectedRes.json();
      }
    } catch (e) {
      console.error('Error loading source order materials/routes for clone:', e);
    }

    // Clone the form with new order number
    const clonedForm = {
      orderNumber: newOrderNumber,
      customerName: order.customerName || '',
      productCode: order.productCode,
      productName: order.productName,
      codigoBarras: parseCodigosBarras((order as any).codigoBarras),
      orderType: parsedNotes.orderType === 'NUEVO' ? 'PRODUCTO_NUEVO' : (parsedNotes.orderType || ''),
      sourceOrderNumber: (parsedNotes as any).sourceOrderNumber || '',
      processType: parsedNotes.processType || '',
      customerCode: order.customerCode || '',
      branchName: (order as any).branchName || '',
      purchaseOrder: parsedNotes.purchaseOrder || '',
      purchaseDate: parsedNotes.purchaseDate || '',
      productionDate: parsedNotes.productionDate || '',
      commitmentDate: parsedNotes.commitmentDate || '',
      deliveryDate: parsedNotes.deliveryDate || '',
      machineName: order.machineName || '',
      equipoPreprensa: (order as any).equipoPreprensa || '',
      quantityPlanned: order.quantityPlanned || '',
      unit: order.unit || 'kg',
      lotCode: order.lotCode || '',
      priority: order.priority || '2',
      dueDate: '',
      printConfigImage: order.printConfigImage,
      materials: clonedMaterials,
      processSpecs: {
        ...parsedNotes.processSpecs,
        continuousRepetition: (parsedNotes.processSpecs as any)?.continuousRepetition ?? false,
        innerDiameterUnit: (parsedNotes.processSpecs as any)?.innerDiameterUnit ?? 'mm',
        outerDiameterUnit: (parsedNotes.processSpecs as any)?.outerDiameterUnit ?? 'mm',
        colorRows: normalizeColorRows((parsedNotes.processSpecs as any)?.colorRows),
      },
      // ROT-207/ROT-208: a REPETICIÓN clone carries the source order's
      // general plant-floor and closure notes forward into its own
      // Observaciones — appended rather than replacing, so any observations
      // already on the source order aren't lost.
      observations: [
        parsedNotes.observations,
        (parsedNotes as any).plantExecutionObservations
          ? `Observaciones Generales de Producción (orden anterior): ${(parsedNotes as any).plantExecutionObservations}`
          : '',
        (parsedNotes as any).closureGeneralObservations
          ? `Observaciones Generales al Cierre de Producción (orden anterior): ${(parsedNotes as any).closureGeneralObservations}`
          : '',
      ].filter(Boolean).join('\n\n'),
      label: order.label || '',
      designUrl: order.designUrl || '',
    };

    // Set this as a new order (selectedOrder = null)
    setSelectedOrder(null);
    setEditForm(clonedForm);
    setEditFormErrors({});
    setCreateServerError('');

    // ROT-220: show the cloned materials immediately (see
    // syncNewOrderMaterialsDisplay) and stage the cloned route to be
    // attached once the new order has an id (see handleUpdateOrder).
    // editingMaterialId/Data are reset too — a client-side id generated for
    // this clone's rows could otherwise collide with whatever a previous,
    // abandoned edit on a different order left behind.
    syncNewOrderMaterialsDisplay(clonedMaterials);
    setEditingMaterialId(null);
    setEditingMaterialData(null);
    setExpectedResults(clonedExpectedResults);
    setOrderSpecifications([]);
    setOrderManufacturingRoutes([]);
    setPendingClonedRoutes(clonedRoutes);

      // Open edit modal
      setShowEditModal(true);
    } catch (error) {
      console.error('Error cloning order:', error);
      alert('Error al clonar la orden. Por favor, intente de nuevo.');
    }
  };

  const handleDelete = async (orderId: string) => {
    if (deleteConfirm === orderId) {
      try {
        await deleteProductionOrder(orderId);
        loadOrders(); // Refresh list
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    } else {
      setDeleteConfirm(orderId);
    }
  };

  const handleStatusChange = async (orderId: string, status: 'CREATED' | 'AUTHORIZED') => {
    if (statusChangeConfirm?.orderId === orderId) {
      try {
        await changeOrderStatus(orderId, status, `Estado cambiado a ${status}`);
        loadOrders(); // Refresh list
        setStatusChangeConfirm(null);
      } catch (error) {
        console.error('Error changing status:', error);
      }
    } else {
      setStatusChangeConfirm({ orderId, status });
    }
  };

  // Image Zoom and Pan Functions
  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.5, 5)); // Max 5x zoom
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.5, 0.5)); // Min 0.5x zoom
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };

  // Reset zoom when modal closes
  const closeDesignModal = () => {
    setShowDesignImageModal(false);
    handleResetZoom();
    setCatalogDesignMeta(null);
  };

  // Fetch catalog design metadata when design modal opens
  useEffect(() => {
    if (!showDesignImageModal || !selectedOrder?.productCode) return;
    const tkn = localStorage.getItem('access_token') || '';
    fetch(`/api/catalog-designs/by-product-code/${encodeURIComponent(selectedOrder.productCode)}/info`, {
      headers: { Authorization: `Bearer ${tkn}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.design) setCatalogDesignMeta(data.design); })
      .catch(() => {});
  }, [showDesignImageModal, selectedOrder?.productCode]);

  // Material Structure Management Functions
  const loadOrderMaterials = async (orderId: string): Promise<any[]> => {
    try {
      setMaterialsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/materials`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded materials:', data);
        setOrderMaterials(data);
        return data; // Return materials for direct use
      }
      return [];
    } catch (error) {
      console.error('Error loading materials:', error);
      return [];
    } finally {
      setMaterialsLoading(false);
    }
  };

  // ROT-220: mirrors editForm.materials into the visible orderMaterials
  // table. editForm.materials is the only representation that exists for a
  // brand-new (unsaved) order, but the rendered materials table (further
  // below in this component) always reads orderMaterials — without this
  // sync, anything staged in editForm.materials for a new order (manual
  // entry, Repetición, or Clonar) is invisible until the order is saved and
  // reopened, which is exactly the "phantom materials" bug this fixes.
  // Filters out blank rows (e.g. the empty placeholder handleNewOrder seeds
  // editForm.materials with) so a new order's table never shows a phantom
  // empty row. Relies on every caller having already stamped a stable `id`
  // on each material when it was first staged (handleAddMaterial,
  // Repetición, Clonar) — reusing that id here (instead of generating a new
  // one on every sync) keeps row identity stable across re-syncs, so an
  // in-progress edit or the isMain radio's `name` never jumps to a
  // different row mid-session.
  const syncNewOrderMaterialsDisplay = (materials: any[]) => {
    setOrderMaterials(
      materials
        .filter(mat => (mat.itemName ?? mat.material ?? '').trim())
        .map(mat => ({
          id: mat.id,
          material: mat.itemName ?? mat.material ?? '',
          caliber: mat.caliber ?? '',
          factor: mat.factor ?? '1',
          grm2: mat.grm2 ?? '',
          percentage: mat.percentage ?? '',
          quantity: mat.quantity ?? '',
          unit: mat.unit ?? '-',
          medida: mat.medida ?? '',
          isMain: mat.isMain ?? false,
          catalogProductId: mat.catalogProductId ?? null,
        }))
    );
  };

  // ROT-220: persists editForm.materials into the real materials-structure
  // table (production_order_materials) for a NEW order right after it's
  // created — mirrors the existing ROT-207 fix that does the same for
  // manufacturing routes. Before this, a new order's materials were only
  // ever included in the order-creation POST's `items` array, which stores
  // them (lossily, structure fields crammed into a notes JSON blob) in the
  // unrelated production_order_items table — nothing that actually reads
  // material structure (Post-Mortem, expected-results) ever saw them.
  //
  // Percentage/quantity are recomputed here from the final Cantidad
  // Planificada and Factor Desperdicio (same formula as
  // recalculateExpectedResults) rather than persisted as whatever was
  // staged — a manually-added material's %/quantity inputs are read-only
  // previews the add form never actually populates, so persisting them
  // as-is would silently write 0%/0 quantity. Expected results are saved
  // too, since nothing else on the create path ever does — without this a
  // brand-new order's Factor Desperdicio/Peso Estructura/Rendimiento were
  // silently discarded on save (and, for a Clonar'd order, never carried
  // over from the source in the first place either — see handleCloneOrder).
  //
  // Returns false (without throwing) on any request failure, so the caller
  // can surface a non-fatal warning instead of aborting a create that
  // already succeeded.
  const persistMaterialsForNewOrder = async (orderId: string, materials: any[]): Promise<boolean> => {
    const named = materials.filter(mat => (mat.itemName ?? mat.material ?? '').trim());
    if (named.length === 0) return true;

    const sumGrm2 = named.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
    const wasteFactor = parseFloat(expectedResults?.expectedFactor) || 0;
    const plannedQty = parseFloat(editForm.quantityPlanned) || 0;

    try {
      const token = localStorage.getItem('token');
      for (const mat of named) {
        const grm2 = parseFloat(mat.grm2) || 0;
        const percentage = sumGrm2 > 0 ? grm2 / sumGrm2 : 0;
        const quantity = plannedQty * (1 + wasteFactor / 100) * percentage;
        const res = await fetch(`/api/production-orders/${orderId}/materials`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            material: (mat.itemName ?? mat.material ?? '').trim(),
            caliber: parseFloat(mat.caliber) || 0,
            factor: parseFloat(mat.factor) || 1,
            grm2,
            percentage: sumGrm2 > 0 ? percentage * 100 : 0,
            quantity: sumGrm2 > 0 ? quantity : 0,
            unit: mat.unit || '-',
            isMain: mat.isMain ?? false,
            medida: mat.medida || null,
            catalogProductId: mat.catalogProductId ?? null,
          }),
        });
        if (!res.ok) return false;
      }
      await loadOrderMaterials(orderId);
      await saveExpectedResults(orderId, {
        expectedFactor: wasteFactor,
        structureWeight: sumGrm2,
        yield: sumGrm2 > 0 ? 1000 / sumGrm2 : 0,
      });
      return true;
    } catch (error) {
      console.error('Error persisting materials for new order:', error);
      return false;
    }
  };

  const loadExpectedResults = async (orderId: string): Promise<any | null> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/expected-results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setExpectedResults(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error loading expected results:', error);
      return null;
    }
  };

  const saveOrderMaterial = async (orderId: string, material: any): Promise<any[] | false> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(material),
      });
      if (response.ok) {
        // Load and return the materials for immediate use
        const materials = await loadOrderMaterials(orderId);
        return materials;
      } else {
        const errorData = await response.json();
        console.error('Error saving material:', errorData);
        alert(`Error al guardar material: ${errorData.error || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error saving material:', error);
      alert('Error al guardar material. Por favor intente de nuevo.');
      return false;
    }
  };

  const handleAddMaterial = async () => {
    if (!newMaterial.material.trim()) {
      alert('Por favor ingrese el material');
      return;
    }
    
    // Calculate gr/m² = Caliber × Factor
    const caliber = parseFloat(newMaterial.caliber) || 0;
    const factor = parseFloat(newMaterial.factor) || 1;
    const calculatedGrm2 = caliber * factor;
    
    // If no selectedOrder, we're creating a new order - add to editForm
    if (!selectedOrder) {
      const updatedMaterials = [...editForm.materials, {
        // ROT-220: stable client-side id, generated once here (never
        // regenerated by syncNewOrderMaterialsDisplay) — see that
        // function's comment for why this matters for row identity.
        id: crypto.randomUUID(),
        itemName: newMaterial.material,
        caliber: newMaterial.caliber,
        factor: newMaterial.factor,
        grm2: calculatedGrm2.toString(),
        percentage: newMaterial.percentage,
        quantity: newMaterial.quantity,
        unit: newMaterial.unit,
        medida: newMaterial.medida || '',
        isMain: newMaterial.isMain ?? false,
        catalogProductId: newMaterial.catalogProductId ?? null,
        dispersionFactor: '',
        notes: ''
      }];
      setEditForm(prev => ({ ...prev, materials: updatedMaterials }));
      // ROT-220: make it visible immediately — see syncNewOrderMaterialsDisplay.
      syncNewOrderMaterialsDisplay(updatedMaterials);

      // Clear form
      setNewMaterial({
        material: '',
        caliber: '',
        factor: '1',
        grm2: '',
        percentage: '',
        quantity: '',
        unit: 'KG',
        medida: '',
        catalogProductId: null,
        isMain: false,
      });
      setRawMaterialSearchTerm('');
      return;
    }
    
    // Otherwise, save to API for existing order
    const savedMaterials = await saveOrderMaterial(selectedOrder.id, {
      material: newMaterial.material,
      caliber: caliber,
      factor: factor,
      grm2: calculatedGrm2,
      percentage: 0, // Will be calculated after save
      quantity: 0, // Will be calculated after save
      unit: newMaterial.unit,
      isMain: newMaterial.isMain ?? false,
      medida: newMaterial.medida || null,
      catalogProductId: newMaterial.catalogProductId,
    });

    if (savedMaterials !== false) {
      // Clear form
      setNewMaterial({
        material: '',
        caliber: '',
        factor: '1',
        grm2: '',
        percentage: '',
        quantity: '',
        unit: 'KG',
        medida: '',
        catalogProductId: null,
        isMain: false,
      });
      setRawMaterialSearchTerm('');
      
      // Recalculate expected results immediately with the loaded materials
      await recalculateExpectedResults(selectedOrder.id, savedMaterials);
    }
  };
  
  // Function to recalculate expected results and update materials percentages/quantities locally
  const recalculateExpectedResults = async (
    orderId: string,
    materialsData?: any[],
    skipApiUpdate = false,
    overrides?: { wasteFactor?: number; plannedQty?: number }
  ) => {
    // Get the current materials from parameter or state
    const materials = materialsData || orderMaterials;
    
    // If no materials, just reset expected results
    if (!materials || materials.length === 0) {
      console.log('=== RECALCULATE: No materials, resetting ===');
      setExpectedResults((prev: any) => ({
        ...prev,
        structureWeight: '0.0000',
        yield: '0.0000',
      }));
      return;
    }
    
    // Calculate B = Σ(G) - sum of all gr/m² values
    const sumGrm2 = materials.reduce((sum, mat) => {
      const grm2 = parseFloat(mat.grm2) || 0;
      return sum + grm2;
    }, 0);
    
    // Calculate C = 1000 / B (Rendimiento m²/kg)
    const yieldValue = sumGrm2 > 0 ? 1000 / sumGrm2 : 0;
    
    // Get current expectedFactor (A - Factor Desperdicio). Prefer the
    // explicit override — right after loading an order, `expectedResults`
    // state may still hold the previous order's (stale, closure-captured)
    // value, since setState from the load doesn't take effect within the
    // same synchronous/async call — over the closure state.
    const wasteFactor = overrides?.wasteFactor ?? (parseFloat(expectedResults?.expectedFactor) || 0);

    // Get Cantidad Planificada — same staleness concern as above.
    const plannedQty = overrides?.plannedQty ?? (parseFloat(editForm.quantityPlanned) || 0);
    
    // DEBUG: Log all input values
    console.log('=== RECALCULATE EXPECTED RESULTS ===');
    console.log('editForm.quantityPlanned (raw):', editForm.quantityPlanned);
    console.log('plannedQty (parsed):', plannedQty);
    console.log('expectedResults?.expectedFactor (raw):', expectedResults?.expectedFactor);
    console.log('wasteFactor (A - Factor Desperdicio):', wasteFactor);
    console.log('Materials count:', materials.length);
    console.log('Materials gr/m² values:', materials.map(m => ({ material: m.material, grm2: m.grm2 })));
    console.log('sumGrm2 (B - Peso Estructura):', sumGrm2);
    console.log('yieldValue (C - Rendimiento = 1000/B):', yieldValue);
    
    // Update expected results state immediately
    setExpectedResults((prev: any) => ({
      ...prev,
      structureWeight: sumGrm2.toFixed(4),
      yield: yieldValue.toFixed(4),
    }));
    
    // Percentage AND quantity are both always recalculated from the current
    // material set — neither has an authoritative external source that
    // overrides this formula. ROT-196 used to freeze quantity once nonzero,
    // to protect ComercialGS-imported values from being silently
    // overwritten; ROT-155/ROT-199 explicitly reversed that decision: the
    // business requirement is that Cantidad of every row (including
    // ComercialGS-imported ones) always reflects each material's current
    // share of Peso Estructura and the current Factor de Desperdicio, so the
    // sum always reconciles against Cantidad Planificada. Freezing it was
    // exactly why adding a material or editing Factor Desperdicio silently
    // did nothing to already-set rows.
    if (sumGrm2 > 0 && materials.length > 0) {
      const updatedMaterials = materials.map(mat => {
        const grm2 = parseFloat(mat.grm2) || 0;
        // H = G / B (percentage as decimal) — always derived from the CURRENT
        // material set, never frozen after the first calculation.
        const percentage = grm2 / sumGrm2;
        // I = D × (1 + A/100) × H
        const calculatedQuantity = plannedQty * (1 + wasteFactor / 100) * percentage;

        return {
          ...mat,
          percentage: (percentage * 100).toFixed(2), // H = (G/B) × 100
          quantity: calculatedQuantity.toFixed(4),
        };
      });

      // Update local state immediately
      setOrderMaterials(updatedMaterials);

      // Persist every material whose percentage or quantity actually changed.
      // Compare by VALUE, not by object identity — every material above now
      // gets a freshly-computed percentage object, so an identity check would
      // always mark everything as "changed" even when the number is the same.
      if (!skipApiUpdate) {
        const token = localStorage.getItem('token');
        const changedMaterials = updatedMaterials.filter((mat, i) => {
          const before = materials[i];
          return mat.percentage !== before.percentage || mat.quantity !== before.quantity;
        });
        for (const mat of changedMaterials) {
          if (mat.id) {
            try {
              await fetch(`/api/production-orders/${orderId}/materials/${mat.id}`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  material: mat.material,
                  caliber: parseFloat(mat.caliber) || 0,
                  factor: parseFloat(mat.factor) || 1,
                  grm2: parseFloat(mat.grm2) || 0,
                  percentage: parseFloat(mat.percentage) || 0,
                  quantity: parseFloat(mat.quantity) || 0,
                  unit: mat.unit,
                  isMain: mat.isMain ?? false,
                  medida: mat.medida ?? null,
                  catalogProductId: mat.catalogProductId ?? null,
                }),
              });
            } catch (err) {
              console.log('Material update skipped:', mat.id);
            }
          }
        }
      }
    }
    
    // Save expected results to API
    await saveExpectedResults(orderId, {
      expectedFactor: wasteFactor,
      structureWeight: sumGrm2,
      yield: yieldValue,
    });
  };

  // ROT-220: same percentage/quantity formula as recalculateExpectedResults
  // above, but for a NEW (unsaved) order — no orderId to call the API
  // against yet, so this only updates editForm.materials (the staging state
  // that gets persisted once the order is created) and mirrors it into the
  // visible orderMaterials table. Without this, a new order's materials
  // never reflected Cantidad Planificada typed in AFTER Repetición/Clonar
  // pre-filled them (recalculation only ever ran for existing orders).
  const recalculateNewOrderMaterials = () => {
    const materials = editForm.materials;
    if (!materials || materials.length === 0) return;

    const sumGrm2 = materials.reduce((sum: number, mat: any) => sum + (parseFloat(mat.grm2) || 0), 0);
    if (sumGrm2 <= 0) return;

    const plannedQty = parseFloat(editForm.quantityPlanned) || 0;
    const wasteFactor = parseFloat(expectedResults?.expectedFactor) || 0;

    const updatedMaterials = materials.map((mat: any) => {
      const grm2 = parseFloat(mat.grm2) || 0;
      const percentage = grm2 / sumGrm2;
      const quantity = plannedQty * (1 + wasteFactor / 100) * percentage;
      return {
        ...mat,
        percentage: (percentage * 100).toFixed(2),
        quantity: quantity.toFixed(4),
      };
    });

    setEditForm(prev => ({ ...prev, materials: updatedMaterials }));
    syncNewOrderMaterialsDisplay(updatedMaterials);
  };

  const deleteOrderMaterial = async (orderId: string, materialId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        // Filter out deleted material locally first
        const remainingMaterials = orderMaterials.filter(m => m.id !== materialId);
        setOrderMaterials(remainingMaterials);
        
        // Recalculate with remaining materials (skip API update since we just deleted)
        await recalculateExpectedResults(orderId, remainingMaterials, false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting material:', error);
      return false;
    }
  };

  const startEditingMaterial = (material: any) => {
    setEditingMaterialId(material.id);
    const caliberVal = parseFloat(material.caliber) || 0;
    const factorVal = parseFloat(material.factor) || 1;
    // Always recalculate grm2 = caliber × factor to fix any incorrect stored values
    const recalcGrm2 = (caliberVal * factorVal).toFixed(4);
    setEditingMaterialData({
      material: material.material,
      caliber: material.caliber?.toString() || '',
      factor: material.factor?.toString() || '1',
      grm2: recalcGrm2,
      percentage: material.percentage?.toString() || '',
      quantity: material.quantity?.toString() || '',
      unit: material.unit || 'KG',
      isMain: material.isMain ?? false,
      medida: material.medida || '',
      catalogProductId: material.catalogProductId ?? null,
    });
    // Search-term state (drives the dropdown's filter/fetch) starts BLANK,
    // not pre-filled with the stored "CODE - Name" string — that concatenated
    // form matches neither the catalog's separate code nor name columns, so
    // seeding it would make the dropdown for an already-linked material come
    // up empty instead of showing an initial browsable list (ROT-196 review
    // finding). Mirrors "+ Agregar material"'s rawMaterialSearchTerm, which
    // also always starts empty. The visible Input value is unaffected — it
    // still shows editingMaterialData.material as before.
    setEditMaterialSearchTerm('');
  };

  const cancelEditingMaterial = () => {
    setEditingMaterialId(null);
    setEditingMaterialData(null);
    setEditMaterialSearchTerm('');
    setShowEditMaterialDropdown(false);
  };

  // Computes what one material's percentage/quantity WOULD be if its grm2
  // changed, given the rest of the order's CURRENT materials — shared by the
  // inline editor's live preview (JSX, below) and saveEditingMaterial's
  // persisted values, so the two can never silently diverge again (ROT-196:
  // the preview used to show the correct recalculated number while the save
  // button submitted the stale pre-edit one).
  const computeMaterialShare = (
    materialIdBeingEdited: string,
    editGrm2: number,
    wasteFactor: number,
    plannedQty: number
  ): { percentage: number; quantity: number } => {
    const otherSum = orderMaterials
      .filter(m => m.id !== materialIdBeingEdited)
      .reduce((s, m) => s + (parseFloat(m.grm2) || 0), 0);
    const totalB = otherSum + editGrm2;
    const H = totalB > 0 ? editGrm2 / totalB : 0;
    return {
      percentage: H * 100,
      // Quantity is always recalculated from the current share — see the
      // matching note in recalculateExpectedResults (ROT-155/ROT-199: the
      // business requirement is that every row's Cantidad, including
      // ComercialGS-imported ones, always reflects its current % and Factor
      // de Desperdicio, so the sum reconciles against Cantidad Planificada).
      quantity: plannedQty * (1 + wasteFactor / 100) * H,
    };
  };

  const handleSetMaterialMain = async (targetMaterial: any) => {
    if (!selectedOrder) return;
    const token = localStorage.getItem('token');
    setOrderMaterials(prev => prev.map(m => ({ ...m, isMain: m.id === targetMaterial.id })));
    for (const mat of orderMaterials) {
      const newIsMain = mat.id === targetMaterial.id;
      if ((mat.isMain ?? false) !== newIsMain) {
        await fetch(`/api/production-orders/${selectedOrder.id}/materials/${mat.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            material: mat.material,
            caliber: parseFloat(mat.caliber) || 0,
            factor: parseFloat(mat.factor) || 1,
            grm2: parseFloat(mat.grm2) || 0,
            percentage: parseFloat(mat.percentage) || 0,
            quantity: parseFloat(mat.quantity) || 0,
            unit: mat.unit,
            isMain: newIsMain,
            medida: mat.medida ?? null,
            catalogProductId: mat.catalogProductId ?? null,
          }),
        });
      }
    }
  };

  const updateOrderMaterial = async (orderId: string, materialId: string, data: any): Promise<any[] | false> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/materials/${materialId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const materials = await loadOrderMaterials(orderId);
        return materials;
      }
      return false;
    } catch (error) {
      console.error('Error updating material:', error);
      return false;
    }
  };

  const saveEditingMaterial = async () => {
    if (!selectedOrder || !editingMaterialId || !editingMaterialData) return;
    
    if (!editingMaterialData.material.trim()) {
      alert('Por favor ingrese el material');
      return;
    }

    // Recompute this material's own percentage/quantity against the CURRENT
    // full material set — the exact same formula already shown in the
    // read-only % and Cantidad preview columns below, which used to silently
    // diverge from what actually got saved (ROT-196).
    const editGrm2 = parseFloat(editingMaterialData.grm2) || 0;
    const wasteFactor = parseFloat(expectedResults?.expectedFactor) || 0;
    const plannedQty = parseFloat(editForm.quantityPlanned) || 0;
    const { percentage: recalculatedPercentage, quantity: recalculatedQuantity } =
      computeMaterialShare(editingMaterialId, editGrm2, wasteFactor, plannedQty);

    const updatedMaterials = await updateOrderMaterial(selectedOrder.id, editingMaterialId, {
      material: editingMaterialData.material,
      caliber: parseFloat(editingMaterialData.caliber) || 0,
      factor: parseFloat(editingMaterialData.factor) || 1,
      grm2: editGrm2,
      percentage: recalculatedPercentage,
      quantity: recalculatedQuantity,
      unit: editingMaterialData.unit,
      isMain: editingMaterialData.isMain ?? false,
      medida: editingMaterialData.medida || null,
      catalogProductId: editingMaterialData.catalogProductId ?? null,
    });

    if (updatedMaterials !== false) {
      setEditingMaterialId(null);
      setEditingMaterialData(null);
      setEditMaterialSearchTerm('');
      setShowEditMaterialDropdown(false);
      // Recalculate expected results with the updated materials — this also
      // corrects every OTHER material's percentage now that the total (B)
      // changed, since recalculateExpectedResults always re-derives % from
      // the current material set (Task 1).
      await recalculateExpectedResults(selectedOrder.id, updatedMaterials);
    }
  };

  const saveExpectedResults = async (orderId: string, results: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/expected-results`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(results),
      });
      if (response.ok) {
        await loadExpectedResults(orderId);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving expected results:', error);
      return false;
    }
  };

  // Specification Management Functions
  const loadOrderSpecifications = async (orderId: string) => {
    try {
      setSpecificationsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/specifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded specifications:', data);
        setOrderSpecifications(data);
      }
    } catch (error) {
      console.error('Error loading specifications:', error);
    } finally {
      setSpecificationsLoading(false);
    }
  };




  // Manufacturing Routes Management Functions
  const loadOrderManufacturingRoutes = async (orderId: string) => {
    try {
      setManufacturingRoutesLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/manufacturing-routes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded manufacturing routes:', data);
        setOrderManufacturingRoutes(data);
      }
    } catch (error) {
      console.error('Error loading manufacturing routes:', error);
    } finally {
      setManufacturingRoutesLoading(false);
    }
  };

  const saveOrderManufacturingRoute = async (orderId: string, route: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/manufacturing-routes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(route),
      });
      if (response.ok) {
        await loadOrderManufacturingRoutes(orderId);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Error saving manufacturing route:', errorData);
        alert(`Error al guardar ruta: ${errorData.error || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error saving manufacturing route:', error);
      alert('Error al guardar ruta. Por favor intente de nuevo.');
      return false;
    }
  };

  const handleAddManufacturingRoute = async () => {
    if (!newManufacturingRoute.process.trim()) {
      alert('Por favor ingrese el proceso');
      return;
    }
    
    // If no selectedOrder, we're creating a new order - add to local state
    if (!selectedOrder) {
      alert('Para nuevas órdenes, las rutas de fabricación se guardarán con la orden');
      return;
    }
    
    // Calculate next sequence number based on existing routes
    const nextSequence = orderManufacturingRoutes.length > 0 
      ? Math.max(...orderManufacturingRoutes.map(r => r.sequence || 0)) + 1 
      : 1;
    
    // Save to API for existing order
    const success = await saveOrderManufacturingRoute(selectedOrder.id, {
      sequence: nextSequence,
      process: newManufacturingRoute.process,
      machineName: newManufacturingRoute.machineName || null,
      recordType: newManufacturingRoute.recordType || null,
    });
    
    if (success) {
      setNewManufacturingRoute({
        process: '',
        machineName: '',
        recordType: 'TEXTO',
      });
      setManufRouteSearchTerm('');
    }
  };

  // Handle drag and drop reordering of manufacturing routes
  const handleManufacturingRouteDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return;
    }

    const reorderedRoutes = Array.from(orderManufacturingRoutes);
    const [movedItem] = reorderedRoutes.splice(result.source.index, 1);
    reorderedRoutes.splice(result.destination.index, 0, movedItem);

    // Update sequences based on new order (1-indexed)
    const updatedRoutes = reorderedRoutes.map((route, index) => ({
      ...route,
      sequence: index + 1,
    }));

    // Optimistic update
    setOrderManufacturingRoutes(updatedRoutes);

    // Save reordered sequences to backend
    if (selectedOrder) {
      try {
        const token = localStorage.getItem('token');
        const reorderData = updatedRoutes.map(r => ({ id: r.id, sequence: r.sequence }));
        
        const response = await fetch(`/api/production-orders/${selectedOrder.id}/manufacturing-routes/reorder`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ routes: reorderData }),
        });

        if (!response.ok) {
          console.error('Error reordering routes');
          // Revert on error
          await loadOrderManufacturingRoutes(selectedOrder.id);
        }
      } catch (error) {
        console.error('Error reordering manufacturing routes:', error);
        // Revert on error
        if (selectedOrder) {
          await loadOrderManufacturingRoutes(selectedOrder.id);
        }
      }
    }
  };

  const deleteOrderManufacturingRoute = async (orderId: string, routeId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/manufacturing-routes/${routeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        await loadOrderManufacturingRoutes(orderId);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting manufacturing route:', error);
      return false;
    }
  };

  const startEditingManufacturingRoute = (route: any) => {
    setEditingManufacturingRouteId(route.id);
    setEditingManufacturingRouteData({
      sequence: route.sequence.toString(),
      process: route.process,
      machineName: route.machineName || '',
      recordType: route.recordType || '',
    });
  };

  const cancelEditingManufacturingRoute = () => {
    setEditingManufacturingRouteId(null);
    setEditingManufacturingRouteData(null);
  };

  const updateOrderManufacturingRoute = async (orderId: string, routeId: string, data: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/manufacturing-routes/${routeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        await loadOrderManufacturingRoutes(orderId);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating manufacturing route:', error);
      return false;
    }
  };

  const saveEditingManufacturingRoute = async () => {
    if (!selectedOrder || !editingManufacturingRouteId || !editingManufacturingRouteData) return;
    
    if (!editingManufacturingRouteData.sequence.trim()) {
      alert('Por favor ingrese la secuencia');
      return;
    }
    if (!editingManufacturingRouteData.process.trim()) {
      alert('Por favor ingrese el proceso');
      return;
    }
    
    const success = await updateOrderManufacturingRoute(selectedOrder.id, editingManufacturingRouteId, {
      sequence: parseInt(editingManufacturingRouteData.sequence),
      process: editingManufacturingRouteData.process,
      machineName: editingManufacturingRouteData.machineName || null,
      recordType: editingManufacturingRouteData.recordType || null,
    });
    
    if (success) {
      setEditingManufacturingRouteId(null);
      setEditingManufacturingRouteData(null);
    }
  };

  // Get priority label
  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case '1': return 'Alta';
      case '2': return 'Media';
      case '3': return 'Baja';
      default: return priority;
    }
  };

  // Get status label — uses actualStartDate/actualEndDate to detect "En Producción"
  // since the DB enum only has CREATED/AUTHORIZED/COMPLETED/CLOSED
  const getStatusLabel = (status: string, order?: { machineName?: string | null; plannedStartDate?: string | null; actualStartDate?: string | null; actualEndDate?: string | null; manuallyModified?: boolean | null }) => {
    if (order?.actualStartDate && !order?.actualEndDate) return 'En Producción';
    if (order?.manuallyModified && status === 'CREATED') return 'Modificada';
    switch (status) {
      case 'CREATED': return 'Creada';
      case 'AUTHORIZED': return 'Autorizada';
      case 'COMPLETED': return 'Finalizada';
      case 'CLOSED': return 'Cerrada';
      default: return status;
    }
  };

  // Get status color
  const getStatusColor = (status: string, order?: { machineName?: string | null; plannedStartDate?: string | null; actualStartDate?: string | null; actualEndDate?: string | null; manuallyModified?: boolean | null }) => {
    if (order?.actualStartDate && !order?.actualEndDate) return 'bg-blue-100 text-blue-800';
    if (order?.manuallyModified && status === 'CREATED') return 'bg-orange-100 text-orange-800';
    switch (status) {
      case 'CREATED': return 'bg-yellow-100 text-yellow-800';
      case 'AUTHORIZED': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case '1': return 'bg-red-100 text-red-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get order type (Tipo de Proceso) label/color — uses the real, normalized
  // order.orderType column (not the raw notes.orderType, which can still hold
  // the legacy 'NUEVO' value on historical orders)
  const getOrderTypeLabel = (orderType?: string | null) => {
    switch (orderType) {
      case 'PRODUCTO_NUEVO': return 'Producto Nuevo';
      case 'REPETICION': return 'Repetición';
      default: return 'Sin Definir';
    }
  };

  const getOrderTypeColor = (orderType?: string | null) => {
    switch (orderType) {
      case 'PRODUCTO_NUEVO': return 'bg-purple-100 text-purple-800';
      case 'REPETICION': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Import handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleFileImport = async () => {
    if (!importFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('options', JSON.stringify(importOptions));
      
      // TODO: Call import endpoint through useProductionOrders hook
      console.log('Importing file:', importFile.name, 'with options:', importOptions);
      
      // Reset and close modal
      setImportFile(null);
      setShowImportModal(false);
      
      // Reload orders
      loadOrders();
    } catch (error) {
      console.error('Import error:', error);
    }
  };

  const handleApiImport = async () => {
    try {
      // TODO: Call API import endpoint
      console.log('Importing from API:', apiConfig, 'with options:', importOptions);
      
      // Reset and close modal
      setApiConfig({ url: '', method: 'GET', authType: 'bearer', credentials: '' });
      setShowImportModal(false);
      
      // Reload orders
      loadOrders();
    } catch (error) {
      console.error('API import error:', error);
    }
  };

  const handleTestConnection = async () => {
    try {
      console.log('Testing connection to:', apiConfig.url);
      // TODO: Implement connection test
      alert('Conexión exitosa');
    } catch (error) {
      console.error('Connection test error:', error);
      alert('Error de conexión');
    }
  };

  // Import from configured external service
  const [importingExternal, setImportingExternal] = useState(false);
  const [importExternalResult, setImportExternalResult] = useState<{
    success: boolean;
    message: string;
    summary?: any;
  } | null>(null);
  const [importFecha, setImportFecha] = useState<string>(() => addDaysToDateString(getMexicoDateString(), -2));

  const [medidaPreview, setMedidaPreview] = useState<{
    orderNumber: string;
    fechaUsed: string;
    materials: Array<{ material: string; catalogCode: string | null; localMedida: string | null; externalMedida: string | null; changed: boolean }>;
  } | null>(null);
  const [medidaPreviewLoading, setMedidaPreviewLoading] = useState(false);
  const [medidaPreviewError, setMedidaPreviewError] = useState<string | null>(null);
  const [medidaApplyLoading, setMedidaApplyLoading] = useState(false);

  const [refreshPreview, setRefreshPreview] = useState<{
    orderNumber: string;
    fechaUsed: string;
    fieldDiffs: Array<{ field: string; label: string; currentValue: any; newValue: any }>;
    materials: Array<{
      material: string;
      catalogCode: string | null;
      current: { caliber: string | null; factor: string | null; medida: string | null; quantity: string | null; percentage: string | null } | null;
      incoming: { caliber: string | null; factor: string | null; medida: string | null; quantity: string | null; percentage: string | null } | null;
      changed: boolean;
    }>;
    counts: {
      specifications: { current: number; incoming: number };
      routes: { current: number; incoming: number };
      inventoryMovements: { current: number; incoming: number };
    };
    hasChanges: boolean;
  } | null>(null);
  const [refreshPreviewLoading, setRefreshPreviewLoading] = useState(false);
  const [refreshPreviewError, setRefreshPreviewError] = useState<string | null>(null);
  const [refreshApplyLoading, setRefreshApplyLoading] = useState(false);

  const handleImportFromExternal = async () => {
    setImportingExternal(true);
    setImportExternalResult(null);

    try {
      // Create AbortController with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 300 second timeout (5 minutes)

      const response = await fetch('/api/production-orders/import-from-external', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({ fecha: importFecha }),
      });

      clearTimeout(timeoutId);
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Error parsing response JSON:', jsonError);
        setImportExternalResult({
          success: false,
          message: `Error al procesar la respuesta del servidor (HTTP ${response.status})`,
        });
        return;
      }

      if (response.ok) {
        setImportExternalResult({
          success: true,
          message: data.message,
          summary: data.summary,
        });
        
        // Reload orders to show new/updated ones
        loadOrders();
      } else {
        const errorDetails = data.details ? `\nDetalles: ${JSON.stringify(data.details)}` : '';
        setImportExternalResult({
          success: false,
          message: `${data.error || 'Error al importar desde el servicio externo'}${errorDetails}`,
        });
      }
    } catch (error: any) {
      console.error('Import from external service error:', error);
      let errorMessage = 'Error desconocido al conectar con el servicio de importación';
      
      if (error.name === 'AbortError') {
        errorMessage = 'La importación tardó demasiado tiempo (timeout después de 5 minutos). Por favor intente nuevamente.';
      } else if (error.message) {
        errorMessage = `Error al conectar con el servicio de importación: ${error.message}`;
      }
      
      setImportExternalResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setImportingExternal(false);
    }
  };

  const handlePreviewMedidaSync = async () => {
    if (!selectedOrder) return;
    setMedidaPreviewLoading(true);
    setMedidaPreviewError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/medida-sync-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setMedidaPreviewError(data.error || 'Error al comparar con Comercia');
        return;
      }
      setMedidaPreview(data);
    } catch (err) {
      setMedidaPreviewError('Error de conexión al comparar con Comercia');
    } finally {
      setMedidaPreviewLoading(false);
    }
  };

  const handleApplyMedidaSync = async () => {
    if (!selectedOrder) return;
    setMedidaApplyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/medida-sync-apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setMedidaPreviewError(data.error || 'Error al aplicar la actualización');
        return;
      }
      setMedidaPreview(null);
      // Reload materials so the Estructura de Materiales table reflects the change
      await loadOrderMaterials(selectedOrder.id);
      alert(`✅ Medida actualizada en ${data.updated.length} material(es).`);
    } catch (err) {
      setMedidaPreviewError('Error de conexión al aplicar la actualización');
    } finally {
      setMedidaApplyLoading(false);
    }
  };

  const handlePreviewComerciaFullRefresh = async () => {
    if (!selectedOrder) return;
    setRefreshPreviewLoading(true);
    setRefreshPreviewError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/comercia-full-refresh-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setRefreshPreviewError(data.error || 'Error al comparar con Comercia');
        return;
      }
      setRefreshPreview(data);
    } catch (err) {
      setRefreshPreviewError('Error de conexión al comparar con Comercia');
    } finally {
      setRefreshPreviewLoading(false);
    }
  };

  const handleApplyComerciaFullRefresh = async () => {
    if (!selectedOrder) return;
    setRefreshApplyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/comercia-full-refresh-apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setRefreshPreviewError(data.error || 'Error al actualizar la orden');
        return;
      }
      setRefreshPreview(null);
      setSelectedOrder(prev => prev ? { ...prev, manuallyModified: false } : prev);
      await loadOrderMaterials(selectedOrder.id);
      await loadOrders();
      alert('✅ Orden actualizada desde Comercia.');
    } catch (err) {
      setRefreshPreviewError('Error de conexión al actualizar la orden');
    } finally {
      setRefreshApplyLoading(false);
    }
  };

  // Edit form handlers
  const handleOrderTypeChange = async (newType: string) => {
    setHistoryMatchAlert(null);
    setHistoryMatchInfo(null);
    handleEditFormChange('orderType', newType);

    if (newType !== 'REPETICION') return;
    if (!editForm.customerCode || !editForm.productCode) return;

    setHistoryMatchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        customerCode: editForm.customerCode,
        productCode: editForm.productCode,
        // ROT-206: when this order already has a sucursal picked, match the
        // history to that specific plant instead of whichever plant's order
        // happens to be most recent.
        ...(editForm.branchName ? { branchName: editForm.branchName } : {}),
        ...(selectedOrder?.id ? { excludeId: selectedOrder.id } : {}),
      });
      const res = await fetch(`/api/production-orders/history-match?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 404) {
        setHistoryMatchAlert('No se encontraron órdenes históricas para este Cliente y Producto. Capture como Producto Nuevo.');
        return;
      }
      if (!res.ok) throw new Error('Error del servidor');

      const data = await res.json();
      const td = data.technicalData || {};

      // Pre-fill historical data. Each field is applied independently of the
      // others — processType, observations and codigoBarras used to be
      // nested inside the processSpecs-non-empty check below, so a source
      // order whose processSpecs happened to be empty silently lost all of
      // them too, even though they have nothing to do with processSpecs.
      const hasProcessSpecs = td.processSpecs && Object.keys(td.processSpecs).length > 0;
      // ROT-207/ROT-208: the source order's general plant-floor and closure
      // notes ride along into this REPETICIÓN's own Observaciones, appended
      // rather than replacing whatever historical `observations` already applies.
      const mergedObservations = [
        td.observations,
        td.plantExecutionObservations
          ? `Observaciones Generales de Producción (orden anterior): ${td.plantExecutionObservations}`
          : '',
        td.closureGeneralObservations
          ? `Observaciones Generales al Cierre de Producción (orden anterior): ${td.closureGeneralObservations}`
          : '',
      ].filter(Boolean).join('\n\n');
      if (hasProcessSpecs || td.processType || mergedObservations || td.codigoBarras || td.printConfigImage) {
        setEditForm(prev => ({
          ...prev,
          ...(td.printConfigImage ? { printConfigImage: td.printConfigImage } : {}),
          ...(hasProcessSpecs ? { processSpecs: { ...prev.processSpecs, ...td.processSpecs } } : {}),
          ...(td.processType ? { processType: td.processType } : {}),
          ...(mergedObservations ? { observations: mergedObservations } : {}),
          ...(td.codigoBarras ? { codigoBarras: parseCodigosBarras(td.codigoBarras) } : {}),
        }));
      }

      // Replace materials with historical materials. Copy only the
      // structural fields (material, caliber, factor, grm2 — intrinsic to
      // the recipe, independent of order size) — NOT percentage/quantity.
      // Those depend on THIS order's own Cantidad Planificada, which can
      // legitimately differ from the source order's (ROT-168: copying them
      // verbatim silently carried over the source order's absolute kilos
      // regardless of what the target order actually needed). Leaving them
      // at 0 here means the recalculation below derives them fresh from
      // this order's plannedQty/wasteFactor and each material's grm2 share
      // of the total — the same math already used everywhere else in this
      // file for that purpose.
      const historicalMaterials: any[] = data.materials || [];
      const historicalExpected = data.expectedResults;
      const historicalWasteFactor = parseFloat(historicalExpected?.expectedFactor) || 0;

      if (historicalMaterials.length > 0 && selectedOrder) {
        const token2 = localStorage.getItem('token');
        // Delete existing materials
        for (const mat of orderMaterials) {
          if (mat.id) {
            await fetch(`/api/production-orders/${selectedOrder.id}/materials/${mat.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token2}` },
            });
          }
        }
        // Insert historical materials — structural fields only
        for (const mat of historicalMaterials) {
          await fetch(`/api/production-orders/${selectedOrder.id}/materials`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token2}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              material: mat.material,
              caliber: parseFloat(mat.caliber) || 0,
              factor: parseFloat(mat.factor) || 1,
              grm2: parseFloat(mat.grm2) || 0,
              percentage: 0,
              quantity: 0,
              unit: mat.unit || '-',
              // ROT-205: was missing — the raw material's medida never made
              // it into a REPETICION order, so it always came up blank.
              medida: mat.medida || null,
            }),
          });
        }
        // Reload materials, then recalculate % and quantity for THIS order's
        // own plannedQty, using the source order's Factor Desperdicio (a
        // process property, safe to carry over) — this also persists the
        // recalculated expected results (Factor Desperdicio/Peso
        // Estructura/Rendimiento), so no separate save call is needed here.
        const freshMaterials = await loadOrderMaterials(selectedOrder.id);
        await recalculateExpectedResults(selectedOrder.id, freshMaterials, false, {
          wasteFactor: historicalWasteFactor,
          plannedQty: parseFloat(editForm.quantityPlanned) || 0,
        });
      } else if (historicalExpected && selectedOrder) {
        // No materials to recalculate against, but still carry over Factor
        // Desperdicio on its own.
        const newExpected = { expectedFactor: historicalWasteFactor, structureWeight: 0, yield: 0 };
        await saveExpectedResults(selectedOrder.id, newExpected);
        setExpectedResults((prev: any) => ({ ...prev, ...newExpected }));
      }

      if (historicalMaterials.length > 0 && !selectedOrder) {
        // New order (not saved yet): pre-fill editForm.materials, computing
        // percentage/quantity client-side from THIS order's own plannedQty —
        // same reasoning as the existing-order branch above, since there's
        // no order id yet to run the API-backed recalculation against.
        const plannedQty = parseFloat(editForm.quantityPlanned) || 0;
        const sumGrm2 = historicalMaterials.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
        const newMaterials = historicalMaterials.map((mat: any) => {
          const grm2 = parseFloat(mat.grm2) || 0;
          const percentage = sumGrm2 > 0 ? grm2 / sumGrm2 : 0;
          const quantity = plannedQty * (1 + historicalWasteFactor / 100) * percentage;
          return {
            id: crypto.randomUUID(), // ROT-220: see syncNewOrderMaterialsDisplay
            itemName: mat.material,
            caliber: mat.caliber || '',
            factor: mat.factor || '1',
            grm2: mat.grm2 || '',
            percentage: sumGrm2 > 0 ? (percentage * 100).toFixed(2) : '',
            quantity: sumGrm2 > 0 ? quantity.toFixed(4) : '',
            unit: mat.unit || '-',
            medida: mat.medida || '',
            dispersionFactor: '',
            notes: '',
          };
        });
        setEditForm(prev => ({ ...prev, materials: newMaterials }));
        // ROT-220/ROT-219 follow-up: make this visible immediately instead
        // of leaving it staged invisibly in editForm.materials — see
        // syncNewOrderMaterialsDisplay.
        syncNewOrderMaterialsDisplay(newMaterials);
        // ROT-220: without this, persistMaterialsForNewOrder (which reads
        // expectedResults.expectedFactor at save time) would recompute every
        // quantity at factor 0, diverging from the historicalWasteFactor
        // used to compute what's shown here — same bug this fix already
        // closed for Clonar, applies to Repetición too.
        setExpectedResults((prev: any) => ({ ...prev, expectedFactor: historicalWasteFactor }));
      }

      // ROT-205: history-match never returned the matched order's manufacturing
      // route, so REPETICION orders always started with an empty route and had
      // to be built by hand. Mirrors the materials replace above — structural
      // fields only, machine assignment is left for this order to set fresh.
      if (data.routes?.length > 0 && selectedOrder) {
        const token3 = localStorage.getItem('token');
        await fetch(`/api/production-orders/${selectedOrder.id}/manufacturing-routes/replace-all`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token3}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            routes: data.routes.map((r: any, i: number) => ({
              sequence: r.sequence ?? (i + 1),
              process: r.process,
              machineName: null,
              recordType: r.recordType || 'TEXTO',
            })),
          }),
        });
        await loadOrderManufacturingRoutes(selectedOrder.id);
      }

      const matchedOrderNumber = data.matchedOrder?.orderNumber || '';
      const matchedOrderDate = data.matchedOrder?.createdAt ? new Date(data.matchedOrder.createdAt).toLocaleDateString('es-MX') : '';
      const matchedOrderUpdatedDate = data.matchedOrder?.updatedAt ? new Date(data.matchedOrder.updatedAt).toLocaleDateString('es-MX') : '';
      setHistoryMatchInfo({ orderNumber: matchedOrderNumber, createdAt: matchedOrderDate, updatedAt: matchedOrderUpdatedDate });
      // Persist source order number in the form so it gets saved to notes
      if (matchedOrderNumber) {
        setEditForm(prev => ({ ...prev, sourceOrderNumber: matchedOrderNumber }));
      }
    } catch (err) {
      setHistoryMatchAlert('Error al buscar órdenes históricas. Intente de nuevo.');
    } finally {
      setHistoryMatchLoading(false);
    }
  };

  const handleEditFormChange = (field: string, value: any) => {
    // Handle nested fields with multiple levels (e.g., "processSpecs.photocell.width")
    if (field.includes('.')) {
      const keys = field.split('.');
      
      setEditForm(prev => {
        const newForm = { ...prev } as any;
        let current = newForm;
        
        // Navigate to the parent of the final key
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key]) {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        return newForm;
      });
    } else {
      setEditForm(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Edit material handlers (currently not used in simplified edit form)
  // const addEditMaterial = () => {
  //   setEditForm(prev => ({
  //     ...prev,
  //     materials: [...prev.materials, {
  //       itemName: '',
  //       caliber: '',
  //       grm2: '',
  //       percentage: '',
  //       quantity: '',
  //       dispersionFactor: '',
  //       notes: ''
  //     }]
  //   }));
  // };

  // const removeEditMaterial = (index: number) => {
  //   setEditForm(prev => ({
  //     ...prev,
  //     materials: prev.materials.filter((_, i) => i !== index)
  //   }));
  // };

  // const updateEditMaterial = (index: number, field: string, value: string) => {
  //   setEditForm(prev => ({
  //     ...prev,
  //     materials: prev.materials.map((material, i) => 
  //       i === index ? { ...material, [field]: value } : material
  //     )
  //   }));
  // };

  // Load order data into edit form
  const loadOrderIntoEditForm = (order: ProductionOrder) => {
    // Reset history match state on each form load
    setHistoryMatchInfo(null);
    setHistoryMatchAlert(null);
    setHistoryMatchLoading(false);

    // Parse notes JSON if it exists
    let parsedNotes: any = {};
    try {
      if (order.notes) {
        parsedNotes = JSON.parse(order.notes);
        console.log('Parsed notes for order', order.orderNumber, ':', parsedNotes);
      }
    } catch (e) {
      console.warn('Failed to parse order notes as JSON:', e);
    }
    
    console.log('Loading order into edit form:', {
      orderNumber: order.orderNumber,
      rawNotes: order.notes,
      parsedNotes,
      purchaseDate: parsedNotes.purchaseDate,
      productionDate: parsedNotes.productionDate,
      commitmentDate: parsedNotes.commitmentDate,
    });
    
    setEditForm({
      // Datos del Cliente y Solicitud de Producción
      orderNumber: order.orderNumber,
      customerName: order.customerName || '',
      productCode: order.productCode,
      productName: order.productName,
      codigoBarras: parseCodigosBarras((order as any).codigoBarras || parsedNotes.codigoBarras),
      // orderType is a real column (order.orderType) AND mirrored in notes — read the
      // column first, same precedence codigoBarras already uses above. The column is
      // the more reliable source: the save path protects it from ever being written
      // blank (`editForm.orderType || undefined`), while the notes copy has no such
      // guard, so a save made before Tipo de Pedido was selected could leave
      // notes.orderType empty even though the column still holds the real value.
      orderType: (() => {
        const raw = order.orderType || parsedNotes.orderType || '';
        return raw === 'NUEVO' ? 'PRODUCTO_NUEVO' : raw; // legacy 'NUEVO' normalized to 'PRODUCTO_NUEVO'
      })(),
      sourceOrderNumber: (parsedNotes as any).sourceOrderNumber || '',
      processType: parsedNotes.processType || '',
      customerCode: order.customerCode || '',
      branchName: (order as any).branchName || '',
      purchaseOrder: parsedNotes.purchaseOrder || '',
      purchaseDate: parsedNotes.purchaseDate || '', // Fecha O. Compra desde notes
      productionDate: parsedNotes.productionDate || order.plannedStartDate || '', // Fecha O. Producción desde notes
      commitmentDate: parsedNotes.commitmentDate || order.dueDate || '', // Fecha Compromiso desde notes
      deliveryDate: parsedNotes.deliveryDate || order.dueDate || '',
      machineName: order.machineName || '',
      equipoPreprensa: (order as any).equipoPreprensa || '',
      quantityPlanned: order.quantityPlanned,
      unit: order.unit,
      lotCode: order.lotCode || '',
      
      // Básicos
      priority: order.priority,
      dueDate: order.dueDate || '',
      
      // Configuración de Impresión
      printConfigImage: order.printConfigImage,
      
      // Materiales (inicialmente vacío, se cargarían de la orden completa)
      materials: [{
        itemName: '',
        caliber: '',
        factor: '1',
        grm2: '',
        percentage: '',
        quantity: '',
        dispersionFactor: '',
        notes: ''
      }],
      
      // Especificaciones del Proceso (desde notes si existen)
      processSpecs: parsedNotes.processSpecs || {
        printType: 'FLEXO',
        individualWidth: '',
        bobinCount: '',
        printWidth: '',
        reelWidth: '',
        baggingCutWidth: '',
        substrateWidth: '',
        barCode: '',
        individualRepetition: '',
        repetitionCount: '',
        cylinderCircumference: '',
        cylinderCount: '',
        authorizedRevision: '',
        cutLine: false,
        footHouse: false,
        photocell: { width: '', height: '' },
        pantoneColors: [] as string[],
        adhesiveLamination: '',
        laminationCatalyst: '',
        trilaminationAdhesive: '',
        trilaminationCatalyst: '',
        innerDiameter: '',
        outerDiameter: '',
        windingDirection: '',
        figureNumber: '',
        photocellSide: 'CENTRADA',
      fotoceldaColor: '',
        packaging: '',
        continuousRepetition: (parsedNotes.processSpecs as any)?.continuousRepetition ?? false,
        colorRows: normalizeColorRows(parsedNotes.processSpecs?.colorRows),
      },
      
      // Observaciones (desde notes si existen)
      observations: parsedNotes.observations || '',
      
      // Etiqueta
      label: order.label || '',
      
      // URL del diseño
      designUrl: order.designUrl || '',
    });
    
    // Clear any previous errors
    setEditFormErrors({});
  };


  const validateEditForm = () => {
    const errors: {[key: string]: string} = {};
    
    // Datos básicos requeridos (siempre validar)
    if (!editForm.orderNumber.trim()) {
      errors.orderNumber = 'Número de orden es requerido';
    }
    if (!editForm.customerName.trim()) {
      errors.customerName = 'Nombre del cliente es requerido';
    }
    if (!editForm.productCode.trim()) {
      errors.productCode = 'Código del producto es requerido';
    }
    if (!editForm.productName.trim()) {
      errors.productName = 'Nombre del producto es requerido';
    }
    if (!editForm.quantityPlanned.trim()) {
      errors.quantityPlanned = 'Cantidad planificada es requerida';
    } else if (isNaN(Number(editForm.quantityPlanned)) || Number(editForm.quantityPlanned) <= 0) {
      errors.quantityPlanned = 'Cantidad debe ser un número mayor que 0';
    }
    
    // Para órdenes nuevas (selectedOrder === null), NO validar dueDate
    // Para órdenes existentes que se están clonando, tampoco validar todo lo anterior
    // Solo validar lo básico para poder guardar

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUploadDesignImage = async (orderId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/production-orders/${orderId}/design-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al subir la imagen');
      }

      const result = await response.json();
      
      // Update editForm with new designUrl
      setEditForm(prev => ({
        ...prev,
        designUrl: result.path,
      }));

      // CRITICAL FIX: Also update selectedOrder so the preview modal shows the image immediately
      if (selectedOrder) {
        setSelectedOrder({
          ...selectedOrder,
          designUrl: result.path,
        });
      }

      alert('Imagen subida exitosamente');
    } catch (error) {
      console.error('Error uploading design image:', error);
      alert(error instanceof Error ? error.message : 'Error al subir la imagen');
    }
  };

  const handleUpdateOrder = async () => {
    console.log('handleUpdateOrder called, selectedOrder:', selectedOrder);
    
    if (!validateEditForm()) {
      console.log('Form validation failed');
      return;
    }

    // Clear previous server error
    setCreateServerError('');

    try {
      // Prepare order data - INCLUDE ALL FIELDS (same for both create and update)
      const dueDate = editForm.commitmentDate || editForm.dueDate;
      const orderData = {
        orderNumber: editForm.orderNumber,
        customerName: editForm.customerName,
        productCode: editForm.productCode,
        productName: editForm.productName,
        codigoBarras: joinCodigosBarras(editForm.codigoBarras) || undefined,
        quantityPlanned: Number(editForm.quantityPlanned),
        unit: editForm.unit,
        priority: editForm.priority as '1' | '2' | '3',
        ...(dueDate ? { dueDate } : { dueDate: undefined }), // Only include if not empty
        plannedStartDate: editForm.productionDate || undefined,
        lotCode: editForm.lotCode || undefined,
        machineName: editForm.machineName || undefined,
        equipoPreprensa: editForm.equipoPreprensa || undefined,
        customerCode: editForm.customerCode || undefined,
        branchName: editForm.branchName || undefined,
        
        // CRITICAL FIX: Include designUrl in the payload
        designUrl: editForm.designUrl || undefined,
        
        // Etiqueta
        label: editForm.label || undefined,
        
        // Configuración de Impresión
        printConfigImage: editForm.printConfigImage || undefined,
        
        orderType: editForm.orderType || undefined, // Tipo de Pedido — now also a real column, in addition to notes below

        // Store complex data as JSON in notes field (includes all sections)
        notes: JSON.stringify({
          orderType: editForm.orderType, // Tipo de Pedido
          sourceOrderNumber: editForm.sourceOrderNumber || undefined, // OP origen (Repetición)
          processType: editForm.processType,
          purchaseOrder: editForm.purchaseOrder,
          purchaseDate: editForm.purchaseDate, // Fecha O. Compra
          productionDate: editForm.productionDate, // Fecha O. Producción
          commitmentDate: editForm.commitmentDate, // Fecha Compromiso Entrega
          deliveryDate: editForm.deliveryDate,
          processSpecs: editForm.processSpecs,
          observations: editForm.observations,
        }),
        
        // Materials will be stored in production_order_items (in addition
        // to production_order_materials, persisted separately post-create —
        // see persistMaterialsForNewOrder/ROT-220 above)
        items: editForm.materials
          .filter(material => (material.itemName || '').trim())
          .map(material => ({
            itemCode: material.itemName.substring(0, 20), // Use first part as code
            itemName: material.itemName,
            quantity: Number(material.quantity || 0),
            unit: editForm.unit,
            notes: JSON.stringify({
              caliber: material.caliber,
              grm2: material.grm2,
              percentage: material.percentage,
              dispersionFactor: material.dispersionFactor,
              notes: material.notes,
            }),
          })),
      };

      // Detect if this is a new order (no selectedOrder) or existing order (has selectedOrder.id)
      if (selectedOrder && selectedOrder.id) {
        // UPDATE existing order — patch in state immediately, skip full list reload
        const updatedOrder = await updateProductionOrder(selectedOrder.id, orderData);
        setOrders(prev =>
          prev.map(o =>
            o.id === updatedOrder.id
              ? { ...o, ...updatedOrder, createdByName: o.createdByName }
              : o
          )
        );
      } else {
        // CREATE new order — need a full reload to get correct position + pagination
        const newOrder = await createProductionOrder(orderData);
        // ROT-207: selecting "Tipo de Proceso" while creating a brand-new
        // order can't load its manufacturing route the normal way —
        // loadRoutesForProcessType (the dropdown's onChange handler) needs
        // an order id, which doesn't exist yet at that point. Do it here
        // instead, now that the order has one, so a manual order created
        // with a process type doesn't end up with an empty route (which
        // then blocks scheduling once authorized — nothing to program).
        if (editForm.processType && newOrder?.id) {
          await loadRoutesForProcessType(editForm.processType, newOrder.id);
        }
        // ROT-220: same reasoning as ROT-207 above — materials staged in
        // editForm.materials (manual entry, Repetición, or Clonar) can only
        // be persisted into the real materials-structure table now that the
        // order has an id. The order itself already exists at this point,
        // so a failure here is surfaced as a non-fatal warning rather than
        // thrown into the outer catch (which would otherwise show the
        // generic create-error banner for an order that actually saved).
        const postCreateWarnings: string[] = [];
        if (newOrder?.id && editForm.materials.some((m: any) => (m.itemName || m.material || '').trim())) {
          const materialsOk = await persistMaterialsForNewOrder(newOrder.id, editForm.materials);
          if (!materialsOk) postCreateWarnings.push('los materiales');
        }
        // ROT-220: Clonar stages the source order's manufacturing route here
        // (see handleCloneOrder) since, like materials, it can't be attached
        // until the new order has an id.
        if (newOrder?.id && pendingClonedRoutes.length > 0) {
          try {
            const token = localStorage.getItem('token');
            const routesRes = await fetch(`/api/production-orders/${newOrder.id}/manufacturing-routes/replace-all`, {
              method: 'PUT',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ routes: pendingClonedRoutes }),
            });
            if (!routesRes.ok) postCreateWarnings.push('la ruta de fabricación');
          } catch (e) {
            console.error('Error persisting cloned route for new order:', e);
            postCreateWarnings.push('la ruta de fabricación');
          }
          setPendingClonedRoutes([]);
        }
        if (postCreateWarnings.length > 0) {
          alert(`La orden ${newOrder.orderNumber} se creó, pero no se pudo guardar ${postCreateWarnings.join(' ni ')}. Ábrela y agrégalo(s) manualmente.`);
        }
        loadOrders();
      }
      
      // Success: Close modal
      setShowEditModal(false);
      setSelectedOrder(null);
      setEditFormErrors({});
    } catch (error) {
      // Show server error to user
      const errorMessage = error instanceof Error ? error.message : 'Error al guardar la orden de producción';
      setCreateServerError(errorMessage);
    }
  };

  if (loading.list.loading && orders.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Cargando órdenes de producción...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Órdenes de Producción</h1>
          <p className="text-gray-600">Gestiona las órdenes de producción de tu empresa</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1 border border-gray-300 rounded-md px-2 bg-white">
            <span className="text-xs text-gray-500 whitespace-nowrap">Actualiza desde:</span>
            <input
              type="date"
              value={importFecha}
              onChange={(e) => setImportFecha(e.target.value)}
              disabled={importingExternal}
              className="text-sm border-none outline-none bg-transparent py-1"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={handleImportFromExternal}
            disabled={importingExternal}
          >
            {importingExternal ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Actualizando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Actualizar
              </>
            )}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
          <Button onClick={() => handleNewOrder()}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Orden
          </Button>
        </div>
      </div>

      {/* Import External Result Notification */}
      {importExternalResult && (
        <div className={`p-4 rounded-md ${importExternalResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex justify-between items-start">
            <div>
              <h4 className={`font-medium ${importExternalResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {importExternalResult.message}
              </h4>
              {importExternalResult.summary && (
                <>
                  <p className="text-sm text-gray-600 mt-1">
                    Total: {importExternalResult.summary.total} |
                    Creadas: {importExternalResult.summary.created} |
                    Actualizadas: {importExternalResult.summary.updated} |
                    Omitidas: {importExternalResult.summary.skipped}
                  </p>
                  {importExternalResult.summary.skippedOrders && importExternalResult.summary.skippedOrders.length > 0 && (
                    <details className="mt-2 text-sm">
                      <summary className="cursor-pointer text-amber-700 font-medium">
                        Ver {importExternalResult.summary.skippedOrders.length} órdenes omitidas (ya autorizadas — el ERP no puede pisar una orden en curso)
                      </summary>
                      <ul className="mt-2 space-y-1 pl-4">
                        {importExternalResult.summary.skippedOrders.map((o: any) => (
                          <li key={o.orderNumber} className="text-gray-600">
                            OP {o.orderNumber} — status actual: {o.status}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </div>
            <button
              onClick={() => setImportExternalResult(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Búsqueda y Filtros</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por número de orden, nombre de cliente, producto o lote..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
                className="pl-10"
              />
            </div>

            {/* Advanced filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  >
                    <option value="">Todos los estados</option>
                    <option value="CREATED">Creada</option>
                    <option value="MODIFIED">Modificada</option>
                    <option value="AUTHORIZED">Autorizada</option>
                    <option value="COMPLETED">Finalizada</option>
                    <option value="CLOSED">Cerrada</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Proceso
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={filters.orderType || ''}
                    onChange={(e) => handleFilterChange('orderType', e.target.value || undefined)}
                  >
                    <option value="">Todos los tipos</option>
                    <option value="PRODUCTO_NUEVO">Producto Nuevo</option>
                    <option value="REPETICION">Repetición</option>
                    <option value="SIN_DEFINIR">Sin Definir</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridad
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={filters.priority || ''}
                    onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                  >
                    <option value="">Todas las prioridades</option>
                    <option value="1">Alta</option>
                    <option value="2">Media</option>
                    <option value="3">Baja</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente
                  </label>
                  <Input
                    placeholder="Nombre del cliente"
                    value={filters.customerName || ''}
                    onChange={(e) => handleFilterChange('customerName', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sucursal
                  </label>
                  <Input
                    placeholder="Nombre de la sucursal"
                    value={filters.branchName || ''}
                    onChange={(e) => handleFilterChange('branchName', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código de Producto
                  </label>
                  <Input
                    placeholder="Código del producto"
                    value={filters.productCode || ''}
                    onChange={(e) => handleFilterChange('productCode', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proceso Actual
                  </label>
                  <Input
                    placeholder="Filtrar por proceso..."
                    value={processFilter}
                    onChange={(e) => setProcessFilter(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Desde
                  </label>
                  <Input
                    type="date"
                    value={filters.fromDate || ''}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Hasta
                  </label>
                  <Input
                    type="date"
                    value={filters.toDate || ''}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error message */}
      {loading.list.error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-800">{loading.list.error}</p>
          </CardContent>
        </Card>
      )}

      {/* Orders list */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Órdenes</CardTitle>
              <CardDescription>
                Mostrando {orders.length} de {pagination.total} órdenes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No se encontraron órdenes de producción</p>
              <Button className="mt-4" onClick={() => setShowEditModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera orden
              </Button>
            </div>
          ) : (
            <div className="space-y-4 overflow-x-auto">
              {/* Table header */}
              <div className="hidden lg:grid lg:grid-cols-10 gap-3 p-4 bg-gray-50 rounded-lg font-medium text-sm text-gray-700 min-w-[1350px]">
                <div>Acciones</div>
                <button 
                  onClick={() => handleSort('orderNumber')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Número {getSortIcon('orderNumber')}
                </button>
                <button 
                  onClick={() => handleSort('customerName')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Cliente {getSortIcon('customerName')}
                </button>
                <button 
                  onClick={() => handleSort('productCode')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Producto {getSortIcon('productCode')}
                </button>
                <button 
                  onClick={() => handleSort('quantityPlanned')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Cantidad {getSortIcon('quantityPlanned')}
                </button>
                <button 
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Estado {getSortIcon('status')}
                </button>
                <div>Tipo de Proceso</div>
                <div>Proceso</div>
                <button 
                  onClick={() => handleSort('priority')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Prioridad {getSortIcon('priority')}
                </button>
                <button 
                  onClick={() => handleSort('dueDate')}
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors text-left"
                >
                  Fecha Venc. {getSortIcon('dueDate')}
                </button>
              </div>

              {/* Table rows */}
              {getSortedOrders().map((order) => {
                const _pn = (() => { try { return order.notes ? JSON.parse(order.notes) : {}; } catch { return {}; } })();
                const _isNew = _pn.orderType === 'PRODUCTO_NUEVO';
                const _eng = engravingOrdersMap[order.id];
                const _isMrp = order.customerCode === 'MRP' || order.customerName === 'MRP';
                const _processTypeObj = catalogProcessTypes.find(pt => pt.name === _pn.processType);
                const _hasCoextPermission = user?.permissions?.includes('coextrusion:view') ?? false;
                const _showCoext = _hasCoextPermission && (_isMrp || (_processTypeObj?.enablesExtrusion === true));
                const _isSavedCoext = _showCoext && savedCoextrusionMap.has(order.id);
                const _coextPrintId = _isSavedCoext ? savedCoextrusionMap.get(order.id) : undefined;
                return (<div key={order.id}>
                <div
                  className="lg:grid lg:grid-cols-10 gap-3 p-4 border rounded-lg hover:bg-gray-50 space-y-2 lg:space-y-0 min-w-[1350px]"
                >
                  {/* ── Actions column: every action as its own icon button ──
                      Grid of 4 per row (ROT-197 follow-up) instead of one long
                      vertical column or a horizontal row — keeps the column
                      narrow while staying readable as the action count grows;
                      icon-only buttons with a tooltip (title) carry the label. */}
                  <div className="grid grid-cols-4 gap-1 w-fit">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(order)}
                      disabled={_isSavedCoext}
                      title={_isSavedCoext ? 'No disponible (orden con Co-extrusión guardada)' : 'Ver detalle'}
                      className="px-2"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(order)}
                      disabled={_isSavedCoext}
                      title={_isSavedCoext ? 'No disponible (orden con Co-extrusión guardada)' : 'Editar'}
                      className="px-2"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>

                    {/* ROT-197 (user follow-up): every action gets its own icon-only
                        button with a tooltip, stacked with Ver/Editar above — no
                        "Más acciones" submenu anymore. Same click handlers and
                        conditional-render rules as the dropdown they replace. */}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={_isSavedCoext}
                      title={_isSavedCoext ? 'No disponible (orden con Co-extrusión guardada)' : 'Clonar orden'}
                      className="px-2"
                      onClick={() => handleCloneOrder(order)}
                    >
                      <Copy className="w-3.5 h-3.5 text-orange-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={_isSavedCoext}
                      title={_isSavedCoext ? 'No disponible (orden con Co-extrusión guardada)' : 'Enviar correo'}
                      className="px-2"
                      onClick={() => handleSendEmail(order)}
                    >
                      <Mail className="w-3.5 h-3.5 text-blue-500" />
                    </Button>

                    {!_isSavedCoext && (order.productCode || order.designUrl) && (
                      <Button
                        size="sm"
                        variant="outline"
                        title="Ver diseño"
                        className="px-2"
                        onClick={() => { setSelectedOrder(order); setProductDesignFailed(false); setShowDesignImageModal(true); }}
                      >
                        <FileImage className="w-3.5 h-3.5 text-purple-500" />
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      title={expandedInventoryOrders.has(order.id) ? 'Ocultar inventario' : 'Ver inventario'}
                      className="px-2"
                      onClick={() => toggleInventoryMovements(order.id)}
                    >
                      <Package className="w-3.5 h-3.5 text-teal-500" />
                    </Button>

                    {/* Grabado stamp (solo PRODUCTO_NUEVO) */}
                    {_isNew && !_isSavedCoext && (
                      <Button
                        size="sm"
                        variant="outline"
                        title="Orden de grabado"
                        className="px-2"
                        onClick={() => handleOpenEngravingModal(order, _eng)}
                      >
                        <Stamp className="w-3.5 h-3.5 text-amber-500" />
                      </Button>
                    )}

                    {/* Expandir grabado guardado */}
                    {_eng && !_isSavedCoext && (
                      <Button
                        size="sm"
                        variant="outline"
                        title={expandedEngravingOrders.has(order.id) ? 'Ocultar grabado' : 'Ver grabado'}
                        className="px-2"
                        onClick={() => toggleExpandEngraving(order.id)}
                      >
                        {expandedEngravingOrders.has(order.id)
                          ? <ChevronUp className="w-3.5 h-3.5 text-amber-500" />
                          : <ChevronDown className="w-3.5 h-3.5 text-amber-500" />}
                      </Button>
                    )}

                    {_showCoext && (
                      <Button
                        size="sm"
                        variant="outline"
                        title="Orden Co-extrusión"
                        className="px-2"
                        onClick={() => setCoextrusionOrderId(order.id)}
                      >
                        <Layers className="w-3.5 h-3.5 text-blue-700" />
                      </Button>
                    )}

                    {_isSavedCoext && _coextPrintId && (
                      <Button
                        size="sm"
                        variant="outline"
                        title="Imprimir Co-extrusión"
                        className="px-2"
                        onClick={() => window.open(`/app/coextrusion-orders/${_coextPrintId}/print`, '_blank')}
                      >
                        <Printer className="w-3.5 h-3.5 text-indigo-700" />
                      </Button>
                    )}

                    {_isSavedCoext && (coextLinkedOrdersMap.get(order.id) || []).length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        title={expandedCoextLinks.has(order.id) ? 'Ocultar OP vinculadas' : `Ver OP vinculadas (${(coextLinkedOrdersMap.get(order.id) || []).length})`}
                        className="px-2"
                        onClick={() => {
                          setExpandedCoextLinks(prev => {
                            const next = new Set(prev);
                            next.has(order.id) ? next.delete(order.id) : next.add(order.id);
                            return next;
                          });
                        }}
                      >
                        <Link className="w-3.5 h-3.5 text-blue-700" />
                      </Button>
                    )}

                    {/* Autorizar — no debe bloquearse por _isSavedCoext: a diferencia de
                        Ver/Editar/Clonar (bloqueados para evitar ediciones conflictivas
                        tras guardar la ficha de co-extrusión), autorizar es la única forma
                        de que la orden llegue a Planeación, y no existe ningún otro camino
                        para autorizar una orden de co-extrusión (ROT-181). */}
                    {order.status === 'CREATED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        title={statusChangeConfirm?.orderId === order.id ? 'Confirmar autorización' : 'Autorizar'}
                        className={`px-2 ${statusChangeConfirm?.orderId === order.id ? 'bg-green-100 border-green-400' : ''}`}
                        onClick={() => handleStatusChange(order.id, 'AUTHORIZED')}
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-green-700" />
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      title={deleteConfirm === order.id ? 'Confirmar borrado' : 'Borrar'}
                      className={`px-2 ${deleteConfirm === order.id ? 'bg-red-100 border-red-400' : ''}`}
                      onClick={() => handleDelete(order.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </Button>
                  </div>
                  <div className="font-medium">
                    {order.orderNumber}
                    {order.externalKey ? (
                      <span className="ml-2 inline-block text-xs px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700 border border-purple-200" title={`Importada del sistema externo: ${order.externalKey}`}>
                        ↓ Imp.
                      </span>
                    ) : (
                      <span className="ml-2 inline-block text-xs px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-500 border border-gray-200">
                        Manual
                      </span>
                    )}
                    {_pn.sourceOrderNumber && (
                      <div className="mt-1">
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-300 rounded px-1.5 py-0.5" title={`Repetición basada en la OP ${_pn.sourceOrderNumber}`}>
                          <span className="text-green-500">↩</span>
                          <span>OP Origen: <span className="font-mono font-bold">{_pn.sourceOrderNumber}</span></span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="text-gray-600">{order.customerName || '-'}</div>
                  <div>
                    <div className="font-medium">{order.productCode}</div>
                    <div className="text-sm text-gray-500">{order.productName}</div>
                  </div>
                  <div>
                    {parseFloat(order.quantityPlanned).toLocaleString()} {getOrderUnitAbbreviation(order.unit)}
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status, order)}`}>
                      {getStatusLabel(order.status, order)}
                    </span>
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOrderTypeColor(order.orderType)}`}>
                      {getOrderTypeLabel(order.orderType)}
                    </span>
                  </div>
                  <div>
                    {currentProcessMap[order.id] ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 max-w-[160px] truncate" title={currentProcessMap[order.id]}>
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 animate-pulse"></span>
                        {currentProcessMap[order.id]}
                      </span>
                    ) : (!order.actualStartDate || order.actualEndDate) && order.status !== 'COMPLETED' && order.status !== 'CLOSED' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0"></span>
                        Sin Iniciar
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                      {getPriorityLabel(order.priority)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : '-'}
                  </div>
                </div>
                {expandedInventoryOrders.has(order.id) && (
                  <div className="ml-4 -mt-1 mb-2 bg-teal-50 border border-teal-200 border-t-0 rounded-b-lg px-4 py-3 min-w-[1200px]">
                    {(() => {
                      const allMovs = inventoryMovementsMap[order.id] || [];
                      const filteredMovs = applyInvFilters(allMovs, order.id);
                      const invFilter = getInvFilter(order.id);
                      const isFiltering = hasActiveInvFilters(order.id);
                      const uniqueValues = (field: string) => [...new Set(allMovs.map((m: any) => m[field]).filter(Boolean))].sort() as string[];
                      const inputCls = "w-full border border-teal-300 rounded px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-teal-400 focus:border-teal-400 bg-white placeholder-gray-400";
                      const selectCls = "w-full border border-teal-300 rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-teal-400 bg-white";
                      return (
                        <>
                          {/* Header bar */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-teal-700" />
                              <span className="text-teal-800 font-bold text-sm">Movimientos de Inventario</span>
                              {inventoryMovementsLoading.has(order.id) && (
                                <span className="text-xs text-teal-600 animate-pulse">Cargando...</span>
                              )}
                              {allMovs.length > 0 && (
                                <span className="text-xs text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">
                                  {isFiltering ? `${filteredMovs.length} de ${allMovs.length}` : `${allMovs.length}`} registros
                                </span>
                              )}
                              {isFiltering && (
                                <button
                                  onClick={() => clearInvFilters(order.id)}
                                  className="text-xs text-orange-600 hover:text-orange-800 underline"
                                >
                                  Limpiar filtros
                                </button>
                              )}
                            </div>
                            {allMovs.length > 0 && (
                              <button
                                onClick={() => {
                                  const movs = filteredMovs.length > 0 ? filteredMovs : allMovs;
                                  const headers = ['Fecha','Tipo Doc.','Concepto','Código','Descripción','Catálogo','Cantidad','Unidad','Lote','Almacén','Departamento','Empleado','Tipo'];
                                  const rows = movs.map((m: any) => [
                                    m.fecha ? new Date(m.fecha).toLocaleDateString('es-MX') : '',
                                    m.tipoDoc || '',
                                    m.concepto || '',
                                    m.codigo || '',
                                    m.descripcion || '',
                                    m.catalogProductName || '',
                                    m.cantidad != null ? parseFloat(m.cantidad).toFixed(2) : '',
                                    m.unidad || '',
                                    m.lote || '',
                                    m.almacen || '',
                                    m.departamento || '',
                                    m.empleado || '',
                                    m.esEntrada ? 'Entrada' : m.esSalida ? 'Salida' : '',
                                  ]);
                                  const csvContent = '\uFEFF' + [headers, ...rows]
                                    .map(row => row.map((cell: any) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
                                    .join('\n');
                                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `movimientos_${order.orderNumber}_${getMexicoDateString()}.csv`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }}
                                className="flex items-center gap-1 text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                title="Descargar en Excel"
                              >
                                <FileDown className="w-3 h-3" />
                                Excel
                              </button>
                            )}
                          </div>

                          {/* Filter bar — visible when there's data */}
                          {!inventoryMovementsLoading.has(order.id) && allMovs.length > 0 && (
                            <div className="mb-2 p-2 bg-white border border-teal-200 rounded">
                              <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                                <input className={inputCls} placeholder="Fecha" value={invFilter.fecha}
                                  onChange={e => setInvFilterField(order.id, 'fecha', e.target.value)} />
                                <select className={selectCls} value={invFilter.tipoDoc}
                                  onChange={e => setInvFilterField(order.id, 'tipoDoc', e.target.value)}>
                                  <option value="">Tipo Doc.</option>
                                  {uniqueValues('tipoDoc').map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <input className={inputCls} placeholder="Concepto" value={invFilter.concepto}
                                  onChange={e => setInvFilterField(order.id, 'concepto', e.target.value)} />
                                <input className={inputCls} placeholder="Código" value={invFilter.codigo}
                                  onChange={e => setInvFilterField(order.id, 'codigo', e.target.value)} />
                                <input className={inputCls} placeholder="Descripción" value={invFilter.descripcion}
                                  onChange={e => setInvFilterField(order.id, 'descripcion', e.target.value)} />
                                <input className={inputCls} placeholder="Catálogo" value={invFilter.catalogo}
                                  onChange={e => setInvFilterField(order.id, 'catalogo', e.target.value)} />
                                <input className={inputCls} placeholder="Cantidad" value={invFilter.cantidad}
                                  onChange={e => setInvFilterField(order.id, 'cantidad', e.target.value)} />
                              </div>
                              <div className="grid grid-cols-7 gap-1.5">
                                <select className={selectCls} value={invFilter.unidad}
                                  onChange={e => setInvFilterField(order.id, 'unidad', e.target.value)}>
                                  <option value="">Unidad</option>
                                  {uniqueValues('unidad').map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <input className={inputCls} placeholder="Lote" value={invFilter.lote}
                                  onChange={e => setInvFilterField(order.id, 'lote', e.target.value)} />
                                <select className={selectCls} value={invFilter.almacen}
                                  onChange={e => setInvFilterField(order.id, 'almacen', e.target.value)}>
                                  <option value="">Almacén</option>
                                  {uniqueValues('almacen').map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <select className={selectCls} value={invFilter.departamento}
                                  onChange={e => setInvFilterField(order.id, 'departamento', e.target.value)}>
                                  <option value="">Departamento</option>
                                  {uniqueValues('departamento').map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <input className={inputCls} placeholder="Empleado" value={invFilter.empleado}
                                  onChange={e => setInvFilterField(order.id, 'empleado', e.target.value)} />
                                <select className={selectCls} value={invFilter.tipo}
                                  onChange={e => setInvFilterField(order.id, 'tipo', e.target.value)}>
                                  <option value="">Tipo (todos)</option>
                                  <option value="entrada">Entrada</option>
                                  <option value="salida">Salida</option>
                                </select>
                                <button
                                  onClick={() => clearInvFilters(order.id)}
                                  className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-200 transition-colors"
                                >
                                  Limpiar
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Table */}
                          {!inventoryMovementsLoading.has(order.id) && (
                            allMovs.length > 0 ? (
                              <>
                                {isFiltering && filteredMovs.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic py-2">Sin resultados con los filtros aplicados.</p>
                                ) : (() => {
                                  // Sort by codigo then descripcion
                                  const sortedMovs = [...filteredMovs].sort((a: any, b: any) => {
                                    const ca = (a.codigo || '').toLowerCase();
                                    const cb = (b.codigo || '').toLowerCase();
                                    if (ca !== cb) return ca.localeCompare(cb);
                                    return (a.descripcion || '').toLowerCase().localeCompare((b.descripcion || '').toLowerCase());
                                  });

                                  // Build product summary map
                                  type ProdSummary = { codigo: string; descripcion: string; catalogName: string; unidad: string; entradas: number; salidas: number; };
                                  const prodMap: Record<string, ProdSummary> = {};
                                  for (const m of sortedMovs) {
                                    const key = `${m.codigo || ''}||${m.descripcion || ''}`;
                                    if (!prodMap[key]) {
                                      prodMap[key] = { codigo: m.codigo || '—', descripcion: m.descripcion || '—', catalogName: m.catalogProductName || '', unidad: m.unidad || '', entradas: 0, salidas: 0 };
                                    }
                                    const qty = parseFloat(m.cantidad) || 0;
                                    if (m.esEntrada) prodMap[key].entradas += qty;
                                    else if (m.esSalida) prodMap[key].salidas += qty;
                                  }
                                  const prodSummaries = Object.values(prodMap);

                                  // Group rows by product key for subtotal rows
                                  let lastKey = '';

                                  return (
                                    <>
                                      {/* Summary panel */}
                                      <div className="mb-3 overflow-x-auto">
                                        <div className="text-xs font-bold text-teal-800 mb-1 flex items-center gap-1">
                                          <span>📊</span> Sumatoria por Producto
                                        </div>
                                        <table className="text-xs border-collapse w-full">
                                          <thead>
                                            <tr className="bg-teal-700 text-white">
                                              <th className="px-2 py-1 text-left font-medium border border-teal-600">Código</th>
                                              <th className="px-2 py-1 text-left font-medium border border-teal-600">Descripción / Catálogo</th>
                                              <th className="px-2 py-1 text-right font-medium border border-teal-600">Entradas</th>
                                              <th className="px-2 py-1 text-right font-medium border border-teal-600">Salidas</th>
                                              <th className="px-2 py-1 text-right font-medium border border-teal-600">Neto</th>
                                              <th className="px-2 py-1 text-left font-medium border border-teal-600">Unidad</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {prodSummaries.map((ps, idx) => {
                                              const neto = ps.entradas - ps.salidas;
                                              return (
                                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-teal-50'}>
                                                  <td className="px-2 py-1 border border-teal-200 font-mono font-medium">{ps.codigo}</td>
                                                  <td className="px-2 py-1 border border-teal-200">
                                                    <div className="font-medium">{ps.descripcion}</div>
                                                    {ps.catalogName && <div className="text-green-700 text-xs flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>{ps.catalogName}</div>}
                                                  </td>
                                                  <td className="px-2 py-1 border border-teal-200 text-right text-green-700 font-semibold">
                                                    {ps.entradas > 0 ? `+${ps.entradas.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                                  </td>
                                                  <td className="px-2 py-1 border border-teal-200 text-right text-red-600 font-semibold">
                                                    {ps.salidas > 0 ? `-${ps.salidas.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                                  </td>
                                                  <td className={`px-2 py-1 border border-teal-200 text-right font-bold ${neto >= 0 ? 'text-teal-800' : 'text-red-700'}`}>
                                                    {neto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </td>
                                                  <td className="px-2 py-1 border border-teal-200 text-gray-600">{ps.unidad}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Detail table grouped by product */}
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs border-collapse">
                                          <thead>
                                            <tr className="bg-teal-100 text-teal-800">
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Fecha</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Tipo Doc.</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Concepto</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Código</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Descripción</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Catálogo</th>
                                              <th className="border border-teal-200 px-2 py-1 text-right font-medium">Cantidad</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Unidad</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Lote</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Almacén</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Departamento</th>
                                              <th className="border border-teal-200 px-2 py-1 text-left font-medium">Empleado</th>
                                              <th className="border border-teal-200 px-2 py-1 text-center font-medium">Tipo</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sortedMovs.map((mov: any) => {
                                              const key = `${mov.codigo || ''}||${mov.descripcion || ''}`;
                                              const isGroupStart = key !== lastKey;
                                              lastKey = key;
                                              const ps = prodMap[key];
                                              const neto = ps ? ps.entradas - ps.salidas : 0;
                                              return (
                                                <React.Fragment key={mov.id}>
                                                  {isGroupStart && (
                                                    <tr className="bg-teal-600 text-white">
                                                      <td colSpan={6} className="px-2 py-1 font-semibold">
                                                        <span className="font-mono mr-2">{mov.codigo || '—'}</span>
                                                        <span>{mov.descripcion || '—'}</span>
                                                        {mov.catalogProductName && <span className="ml-2 text-teal-200 text-xs">↳ {mov.catalogProductName}</span>}
                                                      </td>
                                                      <td className="px-2 py-1 text-right font-bold text-xs" colSpan={2}>
                                                        <span className="text-green-300">+{(ps?.entradas || 0).toFixed(2)}</span>
                                                        <span className="mx-1 text-teal-300">/</span>
                                                        <span className="text-red-300">-{(ps?.salidas || 0).toFixed(2)}</span>
                                                        <span className="mx-1 text-teal-300">=</span>
                                                        <span className={neto >= 0 ? 'text-white' : 'text-red-300'}>{neto.toFixed(2)}</span>
                                                        <span className="ml-1 text-teal-300">{mov.unidad || ''}</span>
                                                      </td>
                                                      <td colSpan={5}></td>
                                                    </tr>
                                                  )}
                                                  <tr className="hover:bg-teal-50 bg-white">
                                                    <td className="border border-teal-200 px-2 py-1 whitespace-nowrap">
                                                      {mov.fecha ? new Date(mov.fecha).toLocaleDateString('es-MX') : '—'}
                                                    </td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.tipoDoc || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1 max-w-[200px] truncate" title={mov.concepto || ''}>{mov.concepto || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1 font-mono font-medium">{mov.codigo || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1 max-w-[200px] truncate" title={mov.descripcion || ''}>{mov.descripcion || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1">
                                                      {mov.catalogProductName ? (
                                                        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                                                          <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                                                          {mov.catalogProductName}
                                                        </span>
                                                      ) : (
                                                        <span className="text-gray-400 text-xs">Sin coincidencia</span>
                                                      )}
                                                    </td>
                                                    <td className={`border border-teal-200 px-2 py-1 text-right font-medium ${mov.esEntrada ? 'text-green-700' : mov.esSalida ? 'text-red-600' : ''}`}>
                                                      {mov.cantidad != null ? `${mov.esEntrada ? '+' : mov.esSalida ? '-' : ''}${parseFloat(mov.cantidad).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                                    </td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.unidad || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.lote || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.almacen || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.departamento || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1">{mov.empleado || '—'}</td>
                                                    <td className="border border-teal-200 px-2 py-1 text-center">
                                                      {mov.esEntrada ? (
                                                        <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Entrada</span>
                                                      ) : mov.esSalida ? (
                                                        <span className="inline-block px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Salida</span>
                                                      ) : '—'}
                                                    </td>
                                                  </tr>
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              <p className="text-sm text-teal-600 italic">No hay movimientos de inventario registrados para esta orden.</p>
                            )
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {/* Panel de OP vinculadas a la orden de co-extrusión */}
                {_isSavedCoext && expandedCoextLinks.has(order.id) && (() => {
                  const linkedOrders = coextLinkedOrdersMap.get(order.id) || [];
                  return (
                    <div className="ml-4 -mt-1 mb-2 bg-blue-50 border border-blue-200 border-t-0 rounded-b-lg px-4 py-3 min-w-[1200px]">
                      <div className="flex items-center gap-2 mb-3">
                        <Link className="w-4 h-4 text-blue-700" />
                        <span className="text-blue-800 font-bold text-sm">Órdenes de Producción Vinculadas</span>
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">{linkedOrders.length} orden{linkedOrders.length !== 1 ? 'es' : ''}</span>
                      </div>
                      {linkedOrders.length === 0 ? (
                        <p className="text-sm text-blue-500 italic">No hay órdenes vinculadas.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-blue-100 text-blue-800">
                                <th className="px-3 py-1.5 text-left font-semibold border border-blue-200 rounded-tl">Núm. Orden</th>
                                <th className="px-3 py-1.5 text-left font-semibold border border-blue-200">Cliente</th>
                                <th className="px-3 py-1.5 text-left font-semibold border border-blue-200 rounded-tr">Producto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {linkedOrders.map((lo: any, idx: number) => (
                                <tr key={idx} className="bg-white hover:bg-blue-50">
                                  <td className="px-3 py-1.5 border border-blue-100 font-mono font-medium text-blue-700">{lo.orderNumber || '-'}</td>
                                  <td className="px-3 py-1.5 border border-blue-100 text-gray-700">{lo.customerName || '-'}</td>
                                  <td className="px-3 py-1.5 border border-blue-100 text-gray-700">{lo.productName || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {_eng && expandedEngravingOrders.has(order.id) && (
                  <div className="ml-4 -mt-1 mb-2 bg-amber-50 border border-amber-200 border-t-0 rounded-b-lg px-4 py-3 flex items-center justify-between min-w-[1200px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-amber-800 font-bold text-sm">↳ Orden de Grabado</span>
                      {_eng.lineaje && <span className="text-xs text-gray-600 bg-white border rounded px-2 py-0.5">Lineaje: {_eng.lineaje}</span>}
                      {_eng.espesorMaterial && <span className="text-xs text-gray-600 bg-white border rounded px-2 py-0.5">Espesor: {_eng.espesorMaterial} μm</span>}
                      {_eng.cobrarGrabados && <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded font-medium">Cobrar grabados</span>}
                      {_eng.revisionNumero && <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded">Rev: {_eng.revisionNumero}</span>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="text-amber-600 hover:text-amber-700" onClick={() => handleOpenEngravingModal(order, _eng)} title="Editar Orden de Grabado">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-blue-600 hover:text-blue-700" onClick={() => navigate(`/app/engraving-orders/${_eng.id}/print`)} title="Imprimir Orden de Grabado">
                        <FileDown className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                </div>);
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Página {pagination.page} de {pagination.totalPages} (Total: {pagination.total} órdenes)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1 || loading.list.loading}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Anterior
                </Button>
                
                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = pagination.page <= 3 
                      ? i + 1 
                      : pagination.page + i - 2;
                    
                    if (pageNum < 1 || pageNum > pagination.totalPages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "default" : "outline"}
                        size="sm"
                        disabled={loading.list.loading}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages || loading.list.loading}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="edit-order-form bg-white rounded-lg max-w-7xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
              <h3 className="text-xl font-semibold">{selectedOrder ? `Editar Orden: ${selectedOrder.orderNumber}` : 'Nueva Orden de Producción'}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
                className="p-2"
              >
                ✕
              </Button>
            </div>

            <div className="p-6">
              {/* Server Error Display */}
              {createServerError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">
                    <strong>Error:</strong> {createServerError}
                  </p>
                </div>
              )}

              {/* ROT-200: once an order leaves CREATED (Autorizada, En Producción,
                  Finalizada, Cerrada), the admin edit form is locked — only
                  Observaciones (Section 6, outside this fieldset) stays editable.
                  Plant Execution is a completely separate set of endpoints/screens
                  and is untouched by this. */}
              {!!selectedOrder && selectedOrder.status !== 'CREATED' && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  🔒 Esta orden está <strong>{getStatusLabel(selectedOrder.status, selectedOrder)}</strong> — los datos ya no se pueden editar. Solo puedes agregar texto en Observaciones (sección 6).
                </div>
              )}

              <fieldset disabled={!!selectedOrder && selectedOrder.status !== 'CREATED'}>
              {/* Section 1: Cliente y Solicitud de Producción */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b text-blue-800">📋 1. Cliente y Solicitud de Producción</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Orden *</label>
                    <Input
                      placeholder="OP-2024-001"
                      value={editForm.orderNumber}
                      onChange={(e) => handleEditFormChange('orderNumber', e.target.value)}
                      className={editFormErrors.orderNumber ? 'border-red-500' : ''}
                      disabled={loading.update?.loading}
                    />
                    {editFormErrors.orderNumber && (
                      <p className="text-red-500 text-sm mt-1">{editFormErrors.orderNumber}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Pedido</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleOrderTypeChange('PRODUCTO_NUEVO')}
                        disabled={loading.update?.loading || historyMatchLoading}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                          editForm.orderType === 'PRODUCTO_NUEVO'
                            ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
                        }`}
                      >
                        <span className="text-base">✨</span> Producto Nuevo
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOrderTypeChange('REPETICION')}
                        disabled={loading.update?.loading || historyMatchLoading}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                          editForm.orderType === 'REPETICION'
                            ? 'bg-green-600 border-green-600 text-white shadow-md'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-green-400 hover:text-green-600'
                        }`}
                      >
                        {historyMatchLoading ? (
                          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="text-base">🔁</span>
                        )}
                        Repetición
                      </button>
                    </div>
                    {/* Feedback messages */}
                    {historyMatchLoading && (
                      <div className="mt-2 text-xs text-gray-500 animate-pulse">
                        Buscando orden histórica para este cliente y producto...
                      </div>
                    )}
                    {historyMatchInfo && !historyMatchLoading && (
                      <div className="mt-2 bg-green-50 border-2 border-green-400 rounded-lg px-4 py-3 text-sm text-green-900">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-green-600 text-base">✓</span>
                          <span className="font-bold text-green-800">Especificaciones cargadas desde OP anterior</span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-xs text-green-700">OP Origen:</span>
                          <span className="font-mono font-extrabold text-base text-green-900 bg-green-100 border border-green-400 px-2 py-0.5 rounded">
                            {historyMatchInfo.orderNumber}
                          </span>
                          {historyMatchInfo.updatedAt && (
                            <span className="text-xs text-green-600">(última edición: {historyMatchInfo.updatedAt})</span>
                          )}
                        </div>
                        <p className="text-xs text-green-700 mt-1">Puedes editar los campos antes de guardar.</p>
                      </div>
                    )}
                    {historyMatchAlert && !historyMatchLoading && (
                      <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                        <span className="text-amber-500 mt-0.5">⚠</span>
                        <span>{historyMatchAlert}</span>
                      </div>
                    )}
                  </div>
                  <div className="relative" ref={routePopoverRef}>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-sm font-medium text-gray-700">Tipo de Proceso</label>
                      <button
                        type="button"
                        title="Ver ruta de fabricación"
                        onClick={() => {
                          if (showRoutePopover) {
                            setShowRoutePopover(false);
                          } else {
                            setShowRoutePopover(true);
                            loadRoutePreview(editForm.processType);
                          }
                        }}
                        className={`p-0.5 rounded transition-colors ${showRoutePopover ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                    <select
                      value={editForm.processType}
                      onChange={(e) => {
                        const newProcessType = e.target.value;
                        handleEditFormChange('processType', newProcessType);
                        if (newProcessType && selectedOrder) {
                          loadRoutesForProcessType(newProcessType, selectedOrder.id);
                        }
                        if (showRoutePopover) {
                          loadRoutePreview(newProcessType);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={loading.update?.loading}
                      onFocus={() => {
                        if (!catalogProcessTypes.length) {
                          loadCatalogProcessTypes();
                        }
                      }}
                    >
                      <option value="">Seleccionar...</option>
                      {/* ROT-192: handleEdit opens this modal immediately, before
                          catalogProcessTypes finishes loading ("Open modal
                          immediately for better UX") — editForm.processType is
                          already correct at that point (read from the order's
                          notes), but the <select> has no matching <option> yet,
                          so it renders as "Seleccionar..." until the catalog
                          arrives a moment later. Render the current value as its
                          own option immediately so the field never shows blank
                          for a value it actually has; once the real catalog
                          entry loads, this duplicate is skipped. */}
                      {editForm.processType && !catalogProcessTypes.some(pt => pt.name === editForm.processType) && (
                        <option value={editForm.processType}>{editForm.processType}</option>
                      )}
                      {catalogProcessTypes.map((pt) => (
                        <option key={pt.id} value={pt.name}>{pt.name}</option>
                      ))}
                    </select>
                    {showRoutePopover && (
                      <div
                        className="absolute z-50 left-0 top-full mt-2 w-80 bg-white border border-blue-200 rounded-lg shadow-xl"
                      >
                        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border-b border-blue-200 rounded-t-lg">
                          <span className="text-sm font-semibold text-blue-800">
                            Ruta: {routePreviewProcessType || '—'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowRoutePopover(false)}
                            className="text-blue-400 hover:text-blue-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-4 py-3 max-h-64 overflow-y-auto">
                          {routePreviewLoading ? (
                            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                              <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full" />
                              Cargando ruta...
                            </div>
                          ) : routePreviewSteps.length === 0 ? (
                            <p className="text-gray-400 text-sm py-2 text-center">
                              {editForm.processType ? 'Sin pasos definidos en el catálogo' : 'Selecciona un Tipo de Proceso'}
                            </p>
                          ) : (
                            <ol className="space-y-2">
                              {routePreviewSteps.map((step, idx) => (
                                <li key={step.id} className="flex items-start gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                                    {idx + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 leading-snug">{step.name}</p>
                                    {step.manufacturingProcess && (
                                      <p className="text-xs text-gray-500 truncate">{step.manufacturingProcess}</p>
                                    )}
                                    {step.description && (
                                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{step.description}</p>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                          <p className="text-xs text-gray-400">{routePreviewSteps.length} paso(s) en esta ruta</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Cliente *
                      {canCreateCatalogs && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowQuickClientForm(!showQuickClientForm);
                            setQuickClientError('');
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded p-0.5"
                          title="Crear cliente rápido"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                    </label>
                    {showQuickClientForm && (
                      <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm font-medium text-blue-800 mb-2">Nuevo Cliente</div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Código *"
                            value={quickClientForm.code}
                            onChange={(e) => setQuickClientForm(prev => ({ ...prev, code: e.target.value }))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Nombre *"
                            value={quickClientForm.name}
                            onChange={(e) => setQuickClientForm(prev => ({ ...prev, name: e.target.value }))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="RFC"
                            value={quickClientForm.rfc}
                            onChange={(e) => setQuickClientForm(prev => ({ ...prev, rfc: e.target.value }))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Teléfono"
                            value={quickClientForm.phone}
                            onChange={(e) => setQuickClientForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="text-sm"
                          />
                        </div>
                        {quickClientError && <p className="text-red-600 text-xs mt-1">{quickClientError}</p>}
                        <div className="flex gap-2 mt-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleQuickCreateClient}
                            disabled={quickClientLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                          >
                            {quickClientLoading ? 'Guardando...' : 'Guardar'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowQuickClientForm(false);
                              setQuickClientForm({ code: '', name: '', rfc: '', phone: '', email: '' });
                              setQuickClientError('');
                            }}
                            className="text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="relative">
                      <Input
                        placeholder="Buscar cliente por nombre o código..."
                        value={clientSearchTerm}
                        onChange={(e) => {
                          setClientSearchTerm(e.target.value);
                          setShowClientDropdown(true);
                          if (!e.target.value) {
                            handleEditFormChange('customerName', '');
                            handleEditFormChange('customerCode', '');
                            setSelectedClientId(null);
                          }
                        }}
                        onFocus={() => {
                          setShowClientDropdown(true);
                          // Always refetch, not just when empty — a client
                          // created elsewhere (Catálogos → Clientes, or in a
                          // different tab/session) since this list was last
                          // loaded would otherwise stay invisible here until
                          // a full page reload (R2-1).
                          loadCatalogClients();
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowClientDropdown(false), 200);
                        }}
                        className={editFormErrors.customerName ? 'border-red-500' : ''}
                        disabled={loading.update?.loading}
                      />
                      {clientsLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {showClientDropdown && catalogClients.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {catalogClients
                            .filter(client => 
                              !clientSearchTerm || 
                              client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                              client.code.toLowerCase().includes(clientSearchTerm.toLowerCase())
                            )
                            .slice(0, 20)
                            .map((client) => (
                              <div
                                key={client.id}
                                onClick={() => handleClientSelect(client)}
                                className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                                  selectedClientId === client.id ? 'bg-blue-100' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{client.name}</div>
                                <div className="text-sm text-gray-500">Código: {client.code}</div>
                              </div>
                            ))
                          }
                          {catalogClients.filter(client => 
                            !clientSearchTerm || 
                            client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                            client.code.toLowerCase().includes(clientSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              No se encontraron clientes. Puede crear uno en Catálogos.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {editFormErrors.customerName && (
                      <p className="text-red-500 text-sm mt-1">{editFormErrors.customerName}</p>
                    )}
                    {selectedClientId && editForm.customerCode && (
                      <p className="text-gray-500 text-xs mt-1">Código: {editForm.customerCode}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sucursal
                    </label>
                    <select
                      value={editForm.branchName}
                      onChange={(e) => handleEditFormChange('branchName', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50 disabled:text-gray-400"
                      disabled={!editForm.customerCode || clientBranchesLoading}
                    >
                      <option value="">
                        {!editForm.customerCode
                          ? 'Selecciona un cliente primero'
                          : clientBranchesLoading
                          ? 'Cargando sucursales...'
                          : clientBranches.length === 0
                          ? 'Este cliente no tiene sucursales'
                          : '-- Sin sucursal --'}
                      </option>
                      {/* Keep the order's current value selectable even if it's since
                          been deactivated/removed from the catalog. */}
                      {editForm.branchName && !clientBranches.some(b => b.name === editForm.branchName) && (
                        <option value={editForm.branchName}>{editForm.branchName}</option>
                      )}
                      {clientBranches.map((b) => (
                        <option key={b.id} value={b.name}>{b.name}</option>
                      ))}
                    </select>
                    <p className="text-gray-400 text-xs mt-1">Solo aplica a clientes que distribuyen el mismo producto desde más de una planta.</p>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Producto *
                      {canCreateCatalogs && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowQuickProductForm(!showQuickProductForm);
                            setQuickProductError('');
                          }}
                          className="text-green-600 hover:text-green-800 hover:bg-green-100 rounded p-0.5"
                          title="Crear producto rápido"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                    </label>
                    {showQuickProductForm && (
                      <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="text-sm font-medium text-green-800 mb-2">Nuevo Producto</div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Código *"
                            value={quickProductForm.code}
                            onChange={(e) => setQuickProductForm(prev => ({ ...prev, code: e.target.value }))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Nombre *"
                            value={quickProductForm.name}
                            onChange={(e) => setQuickProductForm(prev => ({ ...prev, name: e.target.value }))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Descripción"
                            value={quickProductForm.description}
                            onChange={(e) => setQuickProductForm(prev => ({ ...prev, description: e.target.value }))}
                            className="text-sm col-span-2"
                          />
                        </div>
                        {quickProductError && <p className="text-red-600 text-xs mt-1">{quickProductError}</p>}
                        <div className="flex gap-2 mt-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleQuickCreateProduct}
                            disabled={quickProductLoading}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                          >
                            {quickProductLoading ? 'Guardando...' : 'Guardar'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowQuickProductForm(false);
                              setQuickProductForm({ code: '', name: '', description: '' });
                              setQuickProductError('');
                            }}
                            className="text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="relative">
                      <Input
                        placeholder="Buscar producto por nombre o código..."
                        value={productSearchTerm}
                        onChange={(e) => {
                          setProductSearchTerm(e.target.value);
                          setShowProductDropdown(true);
                          if (!e.target.value) {
                            handleEditFormChange('productName', '');
                            handleEditFormChange('productCode', '');
                            setSelectedProductId(null);
                          }
                        }}
                        onFocus={() => {
                          setShowProductDropdown(true);
                          if (!catalogProducts.length) {
                            loadCatalogProducts();
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowProductDropdown(false), 200);
                        }}
                        className={editFormErrors.productName ? 'border-red-500' : ''}
                        disabled={loading.update?.loading}
                      />
                      {productsLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {showProductDropdown && catalogProducts.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {catalogProducts
                            .filter(product => 
                              !productSearchTerm || 
                              product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
                              product.code.toLowerCase().includes(productSearchTerm.toLowerCase())
                            )
                            .slice(0, 20)
                            .map((product) => (
                              <div
                                key={product.id}
                                onClick={() => handleProductSelect(product)}
                                className={`px-4 py-2 cursor-pointer hover:bg-green-50 ${
                                  selectedProductId === product.id ? 'bg-green-100' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{product.name}</div>
                                <div className="text-sm text-gray-500">Código: {product.code}</div>
                              </div>
                            ))
                          }
                          {catalogProducts.filter(product => 
                            !productSearchTerm || 
                            product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
                            product.code.toLowerCase().includes(productSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              No se encontraron productos. Puede crear uno en Catálogos.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {editFormErrors.productName && (
                      <p className="text-red-500 text-sm mt-1">{editFormErrors.productName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código Producto *</label>
                    <Input
                      placeholder="Se llena automáticamente"
                      value={editForm.productCode}
                      readOnly
                      className={`bg-gray-100 ${editFormErrors.productCode ? 'border-red-500' : ''}`}
                    />
                    {editFormErrors.productCode && (
                      <p className="text-red-500 text-sm mt-1">{editFormErrors.productCode}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código{editForm.codigoBarras.length > 1 ? 's' : ''} de Barras
                      <span className="text-gray-400 font-normal text-xs ml-1">(una bobina puede tener varios diseños/sabores)</span>
                    </label>
                    <div className="space-y-2">
                      {editForm.codigoBarras.map((code, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            placeholder="Ej: 750300 9080217"
                            value={code}
                            onChange={(e) => {
                              const next = [...editForm.codigoBarras];
                              next[idx] = e.target.value;
                              handleEditFormChange('codigoBarras', next);
                            }}
                            disabled={loading.update?.loading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditFormChange('codigoBarras', editForm.codigoBarras.filter((_, i) => i !== idx))}
                            disabled={loading.update?.loading}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditFormChange('codigoBarras', [...editForm.codigoBarras, ''])}
                        disabled={loading.update?.loading}
                      >
                        + Agregar código
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lote</label>
                    <Input
                      placeholder="1840-251020"
                      value={editForm.lotCode}
                      onChange={(e) => handleEditFormChange('lotCode', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad Planificada *</label>
                    <Input
                      placeholder="1000"
                      value={editForm.quantityPlanned}
                      onChange={(e) => handleEditFormChange('quantityPlanned', e.target.value)}
                      onBlur={async () => {
                        if (selectedOrder) {
                          await recalculateExpectedResults(selectedOrder.id);
                        } else {
                          // ROT-220: new order — no orderId yet, recompute locally.
                          recalculateNewOrderMaterials();
                        }
                      }}
                      className={editFormErrors.quantityPlanned ? 'border-red-500' : ''}
                      disabled={loading.update?.loading}
                    />
                    {editFormErrors.quantityPlanned && (
                      <p className="text-red-500 text-sm mt-1">{editFormErrors.quantityPlanned}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidad de Medida *</label>
                    <div className="relative">
                      <Input
                        placeholder="Buscar unidad..."
                        value={unitSearchTerm}
                        onChange={(e) => {
                          setUnitSearchTerm(e.target.value);
                          setShowUnitDropdown(true);
                        }}
                        onFocus={() => {
                          setShowUnitDropdown(true);
                          if (!catalogUnits.length) {
                            loadCatalogUnits();
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowUnitDropdown(false), 200);
                        }}
                        disabled={loading.update?.loading}
                      />
                      {unitsLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {showUnitDropdown && catalogUnits.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {catalogUnits
                            .filter(unit => 
                              !unitSearchTerm || 
                              unit.name.toLowerCase().includes(unitSearchTerm.toLowerCase()) ||
                              unit.code.toLowerCase().includes(unitSearchTerm.toLowerCase())
                            )
                            .slice(0, 20)
                            .map((unit) => (
                              <div
                                key={unit.id}
                                onClick={() => handleUnitSelect(unit)}
                                className={`px-4 py-2 cursor-pointer hover:bg-purple-50 ${
                                  editForm.unit === unit.name ? 'bg-purple-100' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{unit.name}</div>
                                <div className="text-sm text-gray-500">Código: {unit.code}</div>
                              </div>
                            ))
                          }
                          {catalogUnits.filter(unit => 
                            !unitSearchTerm || 
                            unit.name.toLowerCase().includes(unitSearchTerm.toLowerCase()) ||
                            unit.code.toLowerCase().includes(unitSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              No se encontraron unidades. Puede crear una en Catálogos.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Equipo Preprensa</label>
                    <Input
                      type="text"
                      value={editForm.equipoPreprensa}
                      onChange={(e) => handleEditFormChange('equipoPreprensa', e.target.value)}
                      placeholder="Nombre del equipo de preprensa"
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">O. de Compra / Solicitud</label>
                    <Input
                      type="text"
                      value={editForm.purchaseOrder}
                      onChange={(e) => handleEditFormChange('purchaseOrder', e.target.value)}
                      placeholder="Número de orden de compra o solicitud"
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha O. Compra</label>
                    <Input
                      type="date"
                      value={editForm.purchaseDate}
                      onChange={(e) => handleEditFormChange('purchaseDate', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha O. Producción</label>
                    <Input
                      type="date"
                      value={editForm.productionDate}
                      onChange={(e) => handleEditFormChange('productionDate', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Compromiso Entrega</label>
                    <Input
                      type="date"
                      value={editForm.commitmentDate}
                      onChange={(e) => {
                        handleEditFormChange('commitmentDate', e.target.value);
                        // Auto-calculate priority when commitment date changes
                        if (e.target.value) {
                          const commitmentDate = new Date(e.target.value);
                          const today = new Date();
                          const daysUntilDue = Math.ceil((commitmentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          
                          let newPriority = '2'; // Default: Medium
                          if (daysUntilDue <= 7) {
                            newPriority = '1'; // High priority: 7 days or less
                          } else if (daysUntilDue <= 14) {
                            newPriority = '2'; // Medium priority: 8-14 days
                          } else {
                            newPriority = '3'; // Low priority: more than 14 days
                          }
                          handleEditFormChange('priority', newPriority);
                        }
                      }}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad (Auto-calculada)</label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => handleEditFormChange('priority', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      disabled={loading.update?.loading}
                    >
                      <option value="1">🔴 Alta (≤7 días)</option>
                      <option value="2">🟡 Media (8-14 días)</option>
                      <option value="3">🟢 Baja (&gt;14 días)</option>
                    </select>
                  </div>
                </div>
              </div>
              </fieldset>

              {selectedOrder && (
              <>
              <fieldset disabled={selectedOrder.status !== 'CREATED'}>
              {/* Section 2: Estructura de Materiales */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4 pb-2 border-b">
                  <h3 className="text-lg font-semibold text-blue-800">🧾 2. Estructura de Materiales</h3>
                  {selectedOrder.externalKey && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewMedidaSync}
                        disabled={medidaPreviewLoading}
                      >
                        {medidaPreviewLoading ? 'Comparando...' : '🔄 Comparar medida con Comercia'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewComerciaFullRefresh}
                        disabled={refreshPreviewLoading}
                      >
                        {refreshPreviewLoading ? 'Comparando...' : '📋 Actualizar orden desde Comercia'}
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Resultados Esperados - moved here per user request */}
                <div className="bg-green-50 p-4 rounded-lg mb-4 border border-green-200">
                  <h4 className="text-md font-semibold mb-3 text-green-800">📊 Resultados Esperados</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Factor Desperdicio (%)</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={expectedResults?.expectedFactor || ''}
                        onChange={(e) => {
                          setExpectedResults((prev: any) => ({
                            ...prev,
                            expectedFactor: e.target.value,
                          }));
                        }}
                        onBlur={async () => {
                          if (selectedOrder && expectedResults) {
                            await saveExpectedResults(selectedOrder.id, {
                              expectedFactor: parseFloat(expectedResults.expectedFactor) || 0,
                              structureWeight: parseFloat(expectedResults.structureWeight) || 0,
                              yield: parseFloat(expectedResults.yield) || 0,
                            });
                            // Recalculate quantities with new factor
                            await recalculateExpectedResults(selectedOrder.id);
                          }
                        }}
                        disabled={loading.update?.loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Peso Estructura (gr/m²)</label>
                      <Input
                        type="number"
                        step="0.0001"
                        placeholder="0.0000"
                        value={(() => {
                          // Calculate real-time preview: saved materials + current form gr/m²
                          const savedMaterialsSum = orderMaterials.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
                          const currentFormGrm2 = parseFloat(newMaterial.grm2) || 0;
                          const totalPreview = savedMaterialsSum + currentFormGrm2;
                          return totalPreview.toFixed(4);
                        })()}
                        className="bg-gray-100"
                        readOnly
                        disabled={loading.update?.loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rendimiento (m²/kg)</label>
                      <Input
                        type="number"
                        step="0.0001"
                        placeholder="0.0000"
                        value={(() => {
                          // Calculate real-time preview: C = 1000 / B
                          const savedMaterialsSum = orderMaterials.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
                          const currentFormGrm2 = parseFloat(newMaterial.grm2) || 0;
                          const totalPesoEstructura = savedMaterialsSum + currentFormGrm2;
                          const rendimiento = totalPesoEstructura > 0 ? 1000 / totalPesoEstructura : 0;
                          return rendimiento.toFixed(4);
                        })()}
                        className="bg-gray-100"
                        readOnly
                        disabled={loading.update?.loading}
                      />
                    </div>
                  </div>
                </div>
                
                {materialsLoading ? (
                  <div className="text-center py-4">Cargando materiales...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Inline Form for Adding Materials */}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-2 relative">
                        <Input
                          placeholder="Buscar materia prima..."
                          value={rawMaterialSearchTerm}
                          onChange={(e) => {
                            setRawMaterialSearchTerm(e.target.value);
                            setShowRawMaterialDropdown(true);
                            if (!e.target.value) {
                              setNewMaterial({ ...newMaterial, material: '', catalogProductId: null, medida: '' });
                            }
                          }}
                          onFocus={() => {
                            setShowRawMaterialDropdown(true);
                            if (!catalogRawMaterials.length) {
                              loadCatalogRawMaterials();
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowRawMaterialDropdown(false), 200);
                          }}
                          disabled={loading.update?.loading}
                        />
                        {rawMaterialsLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                          </div>
                        )}
                        {showRawMaterialDropdown && catalogRawMaterials.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {catalogRawMaterials
                              .filter(mat => 
                                !rawMaterialSearchTerm || 
                                mat.name.toLowerCase().includes(rawMaterialSearchTerm.toLowerCase()) ||
                                mat.code.toLowerCase().includes(rawMaterialSearchTerm.toLowerCase())
                              )
                              .slice(0, 20)
                              .map((mat) => (
                                <div
                                  key={mat.id}
                                  onClick={() => handleRawMaterialSelect(mat)}
                                  className={`px-4 py-2 cursor-pointer hover:bg-amber-50 ${
                                    newMaterial.catalogProductId === mat.id ? 'bg-amber-100' : ''
                                  }`}
                                >
                                  <div className="font-medium text-gray-900">{mat.name}</div>
                                  <div className="text-sm text-gray-500">Código: {mat.code}</div>
                                </div>
                              ))
                            }
                            {catalogRawMaterials.filter(mat => 
                              !rawMaterialSearchTerm || 
                              mat.name.toLowerCase().includes(rawMaterialSearchTerm.toLowerCase()) ||
                              mat.code.toLowerCase().includes(rawMaterialSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                No se encontraron materias primas. Puede crear una en Catálogos.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          placeholder="Calibre"
                          value={newMaterial.caliber}
                          onChange={(e) => {
                            const newCaliber = e.target.value;
                            const factor = parseFloat(newMaterial.factor) || 1;
                            const grm2 = (parseFloat(newCaliber) || 0) * factor;
                            setNewMaterial({ 
                              ...newMaterial, 
                              caliber: newCaliber,
                              grm2: grm2.toFixed(4)
                            });
                          }}
                          onBlur={() => {
                            // Focus on Factor field after leaving Calibre
                            setTimeout(() => factorInputRef.current?.focus(), 50);
                          }}
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          ref={factorInputRef}
                          type="number"
                          step="0.01"
                          placeholder="Factor"
                          value={newMaterial.factor}
                          onChange={(e) => {
                            const newFactor = e.target.value;
                            const caliber = parseFloat(newMaterial.caliber) || 0;
                            const grm2 = caliber * (parseFloat(newFactor) || 1);
                            setNewMaterial({ 
                              ...newMaterial, 
                              factor: newFactor,
                              grm2: grm2.toFixed(4)
                            });
                          }}
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          placeholder="gr/m²"
                          value={newMaterial.grm2}
                          className="bg-gray-100"
                          readOnly
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          placeholder="%"
                          value={(() => {
                            // Calculate real-time percentage: H = (G / B) × 100
                            const savedMaterialsSum = orderMaterials.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
                            const currentFormGrm2 = parseFloat(newMaterial.grm2) || 0;
                            const totalPesoEstructura = savedMaterialsSum + currentFormGrm2;
                            const percentage = totalPesoEstructura > 0 ? (currentFormGrm2 / totalPesoEstructura) * 100 : 0;
                            return currentFormGrm2 > 0 ? percentage.toFixed(2) : '';
                          })()}
                          className="bg-gray-100"
                          readOnly
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          placeholder="Cantidad"
                          value={(() => {
                            // Calculate real-time quantity: I = D × (1 + A/100) × (H/100)
                            // Where H = (G/B) × 100 (percentage)
                            const savedMaterialsSum = orderMaterials.reduce((sum, mat) => sum + (parseFloat(mat.grm2) || 0), 0);
                            const currentFormGrm2 = parseFloat(newMaterial.grm2) || 0;
                            const totalPesoEstructura = savedMaterialsSum + currentFormGrm2;
                            const percentageDecimal = totalPesoEstructura > 0 ? (currentFormGrm2 / totalPesoEstructura) : 0;
                            const quantityPlanned = parseFloat(editForm.quantityPlanned) || 0;
                            const wasteFactor = parseFloat(expectedResults?.expectedFactor) || 0;
                            const quantity = quantityPlanned * (1 + wasteFactor / 100) * percentageDecimal;
                            return currentFormGrm2 > 0 ? quantity.toFixed(4) : '';
                          })()}
                          className="bg-gray-100"
                          readOnly
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          placeholder="Medida"
                          value={newMaterial.medida}
                          onChange={(e) => setNewMaterial({ ...newMaterial, medida: e.target.value })}
                          title="Ancho del sustrato (medida)"
                          disabled={loading.update?.loading}
                        />
                      </div>
                      <div className="col-span-2 relative">
                        <select
                          value={newMaterial.unit}
                          onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          disabled={loading.update?.loading}
                        >
                          {catalogUnits.length > 0 ? (
                            catalogUnits.map((unit) => (
                              <option key={unit.id} value={unit.abbreviation || unit.code}>{unit.name}</option>
                            ))
                          ) : (
                            <>
                              <option value="KG">Kilogramos</option>
                              <option value="LT">Litros</option>
                              <option value="PZA">Piezas</option>
                              <option value="M">Metros</option>
                              <option value="M2">Metros²</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div className="col-span-1 flex items-center justify-center gap-1" title="Marcar como material principal">
                        <input
                          type="checkbox"
                          checked={newMaterial.isMain}
                          onChange={(e) => setNewMaterial({ ...newMaterial, isMain: e.target.checked })}
                          className="w-4 h-4 accent-green-600 cursor-pointer"
                          id="newMaterialIsMain"
                        />
                        <label htmlFor="newMaterialIsMain" className="text-xs text-green-700 cursor-pointer">Principal</label>
                      </div>
                      <div className="col-span-1">
                        <Button
                          onClick={handleAddMaterial}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                          disabled={loading.update?.loading}
                        >
                          + Agregar
                        </Button>
                      </div>
                    </div>

                    {/* Materials Table */}
                    {orderMaterials.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        No hay materiales registrados
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Material</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Calibre</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Factor</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">gr/m²</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">%</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Cantidad</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Unidad</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-blue-700" title="Ancho del sustrato (medida)">Medida</th>
                              <th className="px-4 py-3 text-center text-sm font-medium text-green-700" title="Marque el material principal para el cálculo de tiempo de producción">Principal ⭐</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {orderMaterials.map((material, index) => {
                              const isEditing = editingMaterialId === material.id;
                              return (
                                <tr key={material.id || index} className={isEditing ? "bg-blue-50" : "hover:bg-gray-50"}>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <div className="relative min-w-[200px]">
                                        <Input
                                          value={editingMaterialData?.material || ''}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Free typing still works exactly as before — this only
                                            // ADDS a catalog search on top, it never requires picking
                                            // from the dropdown. Typing manually clears catalogProductId
                                            // since we no longer know it still matches what's typed.
                                            setEditingMaterialData({ ...editingMaterialData, material: value, catalogProductId: null });
                                            setEditMaterialSearchTerm(value);
                                            setShowEditMaterialDropdown(true);
                                          }}
                                          onFocus={() => {
                                            setShowEditMaterialDropdown(true);
                                            if (!catalogRawMaterials.length) {
                                              loadCatalogRawMaterials();
                                            }
                                          }}
                                          onBlur={() => {
                                            setTimeout(() => setShowEditMaterialDropdown(false), 200);
                                          }}
                                          className="min-w-[200px]"
                                        />
                                        {rawMaterialsLoading && (
                                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                                          </div>
                                        )}
                                        {showEditMaterialDropdown && catalogRawMaterials.length > 0 && (
                                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {catalogRawMaterials
                                              .filter(mat =>
                                                !editMaterialSearchTerm ||
                                                mat.name.toLowerCase().includes(editMaterialSearchTerm.toLowerCase()) ||
                                                mat.code.toLowerCase().includes(editMaterialSearchTerm.toLowerCase())
                                              )
                                              .slice(0, 20)
                                              .map((mat) => (
                                                <div
                                                  key={mat.id}
                                                  onClick={() => handleEditMaterialSelect(mat)}
                                                  className={`px-4 py-2 cursor-pointer hover:bg-amber-50 ${
                                                    editingMaterialData?.catalogProductId === mat.id ? 'bg-amber-100' : ''
                                                  }`}
                                                >
                                                  <div className="font-medium text-gray-900">{mat.name}</div>
                                                  <div className="text-sm text-gray-500">Código: {mat.code}</div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      material.material
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={editingMaterialData?.caliber || ''}
                                        onChange={(e) => {
                                          const newCaliber = e.target.value;
                                          const factor = parseFloat(editingMaterialData?.factor) || 1;
                                          const grm2 = (parseFloat(newCaliber) || 0) * factor;
                                          setEditingMaterialData({ 
                                            ...editingMaterialData, 
                                            caliber: newCaliber,
                                            grm2: grm2.toFixed(4)
                                          });
                                        }}
                                        onBlur={() => {
                                          setTimeout(() => editingFactorInputRef.current?.focus(), 50);
                                        }}
                                        className="min-w-[80px]"
                                      />
                                    ) : (
                                      material.caliber
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        ref={editingFactorInputRef}
                                        type="number"
                                        step="0.01"
                                        value={editingMaterialData?.factor || '1'}
                                        onChange={(e) => {
                                          const newFactor = e.target.value;
                                          const caliber = parseFloat(editingMaterialData?.caliber) || 0;
                                          const grm2 = caliber * (parseFloat(newFactor) || 1);
                                          setEditingMaterialData({ 
                                            ...editingMaterialData, 
                                            factor: newFactor,
                                            grm2: grm2.toFixed(4)
                                          });
                                        }}
                                        className="min-w-[60px]"
                                      />
                                    ) : (
                                      material.factor || '1'
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={editingMaterialData?.grm2 || ''}
                                        className="min-w-[80px] bg-gray-100"
                                        readOnly
                                      />
                                    ) : (
                                      material.grm2
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={(() => {
                                          const editGrm2 = parseFloat(editingMaterialData?.grm2) || 0;
                                          if (editGrm2 === 0) return '';
                                          const { percentage } = computeMaterialShare(editingMaterialId!, editGrm2, parseFloat(expectedResults?.expectedFactor) || 0, parseFloat(editForm.quantityPlanned) || 0);
                                          return percentage.toFixed(2);
                                        })()}
                                        className="min-w-[80px] bg-green-50"
                                        readOnly
                                      />
                                    ) : (
                                      `${material.percentage}%`
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={(() => {
                                          const editGrm2 = parseFloat(editingMaterialData?.grm2) || 0;
                                          if (editGrm2 === 0) return '';
                                          const { quantity } = computeMaterialShare(editingMaterialId!, editGrm2, parseFloat(expectedResults?.expectedFactor) || 0, parseFloat(editForm.quantityPlanned) || 0);
                                          return quantity.toFixed(4);
                                        })()}
                                        className="min-w-[100px] bg-green-50"
                                        readOnly
                                      />
                                    ) : (
                                      material.quantity
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <select
                                        value={editingMaterialData?.unit || 'KG'}
                                        onChange={(e) => setEditingMaterialData({ ...editingMaterialData, unit: e.target.value })}
                                        className="border border-gray-300 rounded px-2 py-1"
                                      >
                                        <option value="KG">KG</option>
                                        <option value="M">M</option>
                                        <option value="PZ">PZ</option>
                                      </select>
                                    ) : (
                                      getUnitAbbreviation(material.unit)
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <Input
                                        value={editingMaterialData?.medida || ''}
                                        onChange={(e) => setEditingMaterialData({ ...editingMaterialData, medida: e.target.value })}
                                        placeholder="Ej. 1200mm"
                                        className="min-w-[90px]"
                                        title="Ancho del sustrato (medida)"
                                      />
                                    ) : (
                                      <span className="text-blue-700 font-medium">{material.medida || '—'}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {isEditing ? (
                                      <input
                                        type="checkbox"
                                        checked={editingMaterialData?.isMain ?? false}
                                        onChange={(e) => setEditingMaterialData({ ...editingMaterialData, isMain: e.target.checked })}
                                        className="w-4 h-4 accent-green-600 cursor-pointer"
                                        title="Marcar como material principal"
                                      />
                                    ) : (
                                      <input
                                        type="radio"
                                        name={`material-main-${selectedOrder?.id ?? 'new'}`}
                                        checked={material.isMain ?? false}
                                        onChange={() => handleSetMaterialMain(material)}
                                        disabled={!selectedOrder}
                                        className="w-4 h-4 accent-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                        title={selectedOrder ? 'Marcar como material principal' : 'Guarda la orden para poder marcar el material principal'}
                                      />
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <div className="flex gap-2">
                                      {isEditing ? (
                                        <>
                                          <button
                                            onClick={saveEditingMaterial}
                                            className="text-green-600 hover:text-green-800 font-semibold"
                                            disabled={loading.update?.loading}
                                            title="Guardar"
                                          >
                                            ✓
                                          </button>
                                          <button
                                            onClick={cancelEditingMaterial}
                                            className="text-gray-600 hover:text-gray-800"
                                            disabled={loading.update?.loading}
                                            title="Cancelar"
                                          >
                                            ✕
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => startEditingMaterial(material)}
                                            className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                            disabled={loading.update?.loading || !selectedOrder}
                                            title={selectedOrder ? 'Editar' : 'Guarda la orden para poder editar este material'}
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            onClick={async () => {
                                              if (selectedOrder && material.id) {
                                                await deleteOrderMaterial(selectedOrder.id, material.id);
                                              } else if (!selectedOrder) {
                                                // ROT-220: order doesn't exist yet — nothing to delete via the
                                                // API, just drop this row from the staged materials.
                                                const remaining = editForm.materials.filter((m: any) => m.id !== material.id);
                                                setEditForm(prev => ({ ...prev, materials: remaining }));
                                                syncNewOrderMaterialsDisplay(remaining);
                                              }
                                            }}
                                            className="text-red-600 hover:text-red-800"
                                            disabled={loading.update?.loading}
                                            title="Eliminar"
                                          >
                                            🗑️
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Section 3: Especificaciones del Proceso */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b text-blue-800">⚙️ 3. Especificaciones del Proceso</h3>
                <ProcessSpecsTableForm
                  specs={editForm.processSpecs}
                  onChange={(key, value) => handleEditFormChange(`processSpecs.${key}`, value)}
                  disabled={loading.update?.loading}
                  catalogColors={catalogColors}
                  standPouchEnabled={isStandPouchEnabled()}
                />
              </div>

              {/* Section 3.1: Laminación — solo si el tipo de proceso habilita laminación */}
              {catalogProcessTypes.find(pt => pt.name === editForm.processType)?.enablesLamination && (
                <div className="mb-8">
                  <LaminationSection
                    specs={editForm.processSpecs}
                    onChange={(key, value) => handleEditFormChange(`processSpecs.${key}`, value)}
                    disabled={loading.update?.loading}
                  />
                </div>
              )}

              {/* Section 3.2: Empaque */}
              <div className="mb-8">
                <PackagingSection
                  value={editForm.processSpecs.packaging}
                  onChange={(value) => handleEditFormChange('processSpecs.packaging', value)}
                  disabled={loading.update?.loading}
                />
              </div>

              {/* Section 4: Rutas de Fabricación */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b text-blue-800">🔄 4. Rutas de Fabricación</h3>
                
                {/* Add new manufacturing route inputs (above table, matching Materials style) */}
                <div className="grid grid-cols-12 gap-4 mb-4">
                  <div className="col-span-4 relative">
                    <Input
                      placeholder="Buscar proceso..."
                      value={manufRouteSearchTerm || newManufacturingRoute.process}
                      onChange={(e) => {
                        setManufRouteSearchTerm(e.target.value);
                        setNewManufacturingRoute({
                          ...newManufacturingRoute,
                          process: e.target.value,
                        });
                      }}
                      onFocus={() => {
                        setShowManufRouteDropdown(true);
                        if (!catalogManufRoutes.length) {
                          loadCatalogManufRoutes();
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowManufRouteDropdown(false), 200)}
                      disabled={loading.update?.loading}
                      className="border-teal-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                    {manufRoutesLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {showManufRouteDropdown && catalogManufRoutes.length > 0 && (
                      <div className="absolute z-50 w-full bg-white border border-teal-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {catalogManufRoutes
                          .filter(route => 
                            !manufRouteSearchTerm || 
                            route.name.toLowerCase().includes(manufRouteSearchTerm.toLowerCase()) ||
                            route.code.toLowerCase().includes(manufRouteSearchTerm.toLowerCase())
                          )
                          .map((route) => (
                            <div
                              key={route.id}
                              className={`px-3 py-2 cursor-pointer hover:bg-teal-50 border-b border-gray-100 last:border-b-0 ${
                                newManufacturingRoute.process === route.name ? 'bg-teal-100' : ''
                              }`}
                              onMouseDown={() => {
                                setNewManufacturingRoute({
                                  ...newManufacturingRoute,
                                  process: route.name,
                                });
                                setManufRouteSearchTerm('');
                                setShowManufRouteDropdown(false);
                              }}
                            >
                              <div className="font-medium text-gray-900">{route.name}</div>
                              <div className="text-sm text-gray-500">Código: {route.code}</div>
                            </div>
                          ))
                        }
                        {catalogManufRoutes.filter(route => 
                          !manufRouteSearchTerm || 
                          route.name.toLowerCase().includes(manufRouteSearchTerm.toLowerCase()) ||
                          route.code.toLowerCase().includes(manufRouteSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No se encontraron rutas. Puede crear una en Catálogos.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <select 
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={newManufacturingRoute.recordType}
                      onChange={(e) => setNewManufacturingRoute({
                        ...newManufacturingRoute,
                        recordType: e.target.value,
                      })}
                      disabled={loading.update?.loading}
                    >
                      <option value="">-- Tipo Registro --</option>
                      <option value="TEXTO">Texto Valor</option>
                      <option value="IMAGEN">Imagen</option>
                    </select>
                  </div>
                  <div className="col-span-3">
                    <Button
                      onClick={handleAddManufacturingRoute}
                      className="w-full bg-gray-800 hover:bg-gray-900 text-white"
                      disabled={loading.update?.loading}
                    >
                      + Agregar
                    </Button>
                  </div>
                </div>

                {/* Manufacturing Routes Table with Drag and Drop */}
                {orderManufacturingRoutes.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No hay rutas de fabricación registradas
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleManufacturingRouteDragEnd}>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-2 py-3 text-left text-sm font-medium text-gray-700 w-12"></th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-16">#</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Proceso</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo Registro</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Acciones</th>
                          </tr>
                        </thead>
                        <Droppable droppableId="manufacturing-routes">
                          {(provided: DroppableProvided) => (
                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                              {manufacturingRoutesLoading ? (
                                <tr>
                                  <td colSpan={6} className="px-4 py-4 text-center text-gray-500">
                                    Cargando rutas de fabricación...
                                  </td>
                                </tr>
                              ) : (
                                orderManufacturingRoutes.map((route, index) => (
                                  <Draggable key={route.id} draggableId={route.id} index={index}>
                                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                      <tr 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`border-b hover:bg-gray-50 ${snapshot.isDragging ? 'bg-blue-50 shadow-lg' : ''}`}
                                      >
                                        {editingManufacturingRouteId === route.id ? (
                                          <>
                                            <td className="px-2 py-3">
                                              <div {...provided.dragHandleProps} className="cursor-grab text-gray-400">
                                                <GripVertical className="w-5 h-5" />
                                              </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-600">{index + 1}</td>
                                            <td className="px-4 py-3">
                                              <Input
                                                value={editingManufacturingRouteData?.process || ''}
                                                onChange={(e) => setEditingManufacturingRouteData({
                                                  ...editingManufacturingRouteData,
                                                  process: e.target.value,
                                                })}
                                                placeholder="Proceso"
                                                className="w-full"
                                              />
                                            </td>
                                            <td className="px-4 py-3">
                                              <select 
                                                className="w-full p-2 border border-gray-300 rounded-md"
                                                value={editingManufacturingRouteData?.recordType || ''}
                                                onChange={(e) => setEditingManufacturingRouteData({
                                                  ...editingManufacturingRouteData,
                                                  recordType: e.target.value,
                                                })}
                                              >
                                                <option value="">-- Tipo Registro --</option>
                                                <option value="TEXTO">Texto Valor</option>
                                                <option value="IMAGEN">Imagen</option>
                                              </select>
                                            </td>
                                            <td className="px-4 py-3">
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={saveEditingManufacturingRoute}
                                                  className="text-green-600 hover:text-green-800 font-medium"
                                                  title="Guardar"
                                                >
                                                  ✓
                                                </button>
                                                <button
                                                  onClick={cancelEditingManufacturingRoute}
                                                  className="text-red-600 hover:text-red-800 font-medium"
                                                  title="Cancelar"
                                                >
                                                  ✗
                                                </button>
                                              </div>
                                            </td>
                                          </>
                                        ) : (
                                          <>
                                            <td className="px-2 py-3">
                                              <div {...provided.dragHandleProps} className="cursor-grab text-gray-400 hover:text-gray-600">
                                                <GripVertical className="w-5 h-5" />
                                              </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-600">{index + 1}</td>
                                            {/* ROT-205: orders freshly imported from ComerciaGS (still without
                                                Tipo de Proceso) can carry a specific machine name in this field
                                                (e.g. "EXTRUDER 1", "CORTADORA 3") instead of a generic process —
                                                confusing before the order has been typed. Hide it until then;
                                                the stored value is untouched, and shows normally once Tipo de
                                                Proceso is assigned. */}
                                            <td className="px-4 py-3 text-sm">{editForm.processType ? route.process : '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                              {route.recordType ? (route.recordType === 'TEXTO' ? 'Texto Valor' : 'Imagen') : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => startEditingManufacturingRoute(route)}
                                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                                  title="Editar"
                                                >
                                                  ✏️
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    if (confirm('¿Eliminar esta ruta de fabricación?') && selectedOrder) {
                                                      deleteOrderManufacturingRoute(selectedOrder.id, route.id);
                                                    }
                                                  }}
                                                  className="text-red-600 hover:text-red-800 text-sm"
                                                  title="Eliminar"
                                                >
                                                  🗑️
                                                </button>
                                              </div>
                                            </td>
                                          </>
                                        )}
                                      </tr>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder as React.ReactNode}
                            </tbody>
                          )}
                        </Droppable>
                      </table>
                    </div>
                  </DragDropContext>
                )}
                {selectedOrder?.id && (
                  <RoutePlanningSummary
                    orderId={selectedOrder.id}
                    routes={orderManufacturingRoutes}
                    onRecalculated={() => loadOrderManufacturingRoutes(selectedOrder.id)}
                  />
                )}
              </div>

              {/* Section 5: Configuración de Impresión */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b text-blue-800">🖨️ 5. Configuración de Impresión</h3>

                {/* Diámetros y sentido de embobinado */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Diámetro Interior</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Captura"
                        value={editForm.processSpecs.innerDiameter ?? ''}
                        onChange={(e) => handleEditFormChange('processSpecs.innerDiameter', e.target.value)}
                        disabled={loading.update?.loading}
                      />
                      <select
                        className="border border-gray-300 rounded px-2 py-2 text-sm bg-white w-28"
                        value={editForm.processSpecs.innerDiameterUnit ?? 'mm'}
                        onChange={(e) => handleEditFormChange('processSpecs.innerDiameterUnit', e.target.value)}
                        onFocus={() => { if (!catalogUnits.length) loadCatalogUnits(); }}
                        disabled={loading.update?.loading}
                      >
                        {catalogUnits.length > 0
                          ? catalogUnits.map(u => <option key={u.id} value={u.name}>{u.abbreviation || u.name}</option>)
                          : ['mm', 'cm', 'in', 'pulg'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Diámetro Exterior</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Captura"
                        value={editForm.processSpecs.outerDiameter ?? ''}
                        onChange={(e) => handleEditFormChange('processSpecs.outerDiameter', e.target.value)}
                        disabled={loading.update?.loading}
                      />
                      <select
                        className="border border-gray-300 rounded px-2 py-2 text-sm bg-white w-28"
                        value={editForm.processSpecs.outerDiameterUnit ?? 'mm'}
                        onChange={(e) => handleEditFormChange('processSpecs.outerDiameterUnit', e.target.value)}
                        onFocus={() => { if (!catalogUnits.length) loadCatalogUnits(); }}
                        disabled={loading.update?.loading}
                      >
                        {catalogUnits.length > 0
                          ? catalogUnits.map(u => <option key={u.id} value={u.name}>{u.abbreviation || u.name}</option>)
                          : ['mm', 'cm', 'in', 'pulg'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Sentido Embobinado Final</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Ej. PIES, CABEZA"
                      value={editForm.processSpecs.windingDirection ?? ''}
                      onChange={(e) => handleEditFormChange('processSpecs.windingDirection', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Lado Fotocelda</label>
                    <select
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={editForm.processSpecs.photocellSide ?? 'CENTRADA'}
                      onChange={(e) => handleEditFormChange('processSpecs.photocellSide', e.target.value)}
                      disabled={loading.update?.loading}
                    >
                      <option value="CENTRADA">CENTRADA</option>
                      <option value="IZQUIERDA">IZQUIERDA</option>
                      <option value="DERECHA">DERECHA</option>
                      <option value="DOS LADOS">DOS LADOS</option>
                      <option value="CARGADA LADO DERECHO">CARGADA LADO DERECHO</option>
                      <option value="CARGADA LADO IZQUIERDO">CARGADA LADO IZQUIERDO</option>
                      <option value="N/A">N/A</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Tipo Fotocelda</label>
                    <select
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={editForm.processSpecs.fotoceldaColor ?? ''}
                      onChange={(e) => handleEditFormChange('processSpecs.fotoceldaColor', e.target.value)}
                      onFocus={() => { if (!catalogPhotocellTypes.length) loadCatalogPhotocellTypes(); }}
                      disabled={loading.update?.loading}
                    >
                      <option value="">Seleccionar...</option>
                      {catalogPhotocellTypes.length > 0
                        ? catalogPhotocellTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
                        : ['BLANCO+NEGRO', 'COLOR', 'NINGUNA'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((imageNum) => (
                    <div
                      key={imageNum}
                      className={`relative border-2 rounded-lg p-2 cursor-pointer hover:border-blue-400 transition-colors ${
                        editForm.printConfigImage === imageNum ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      }`}
                      onClick={() => handleEditFormChange('printConfigImage', imageNum)}
                    >
                      <img
                        src={printConfigUrlFor(imageNum)}
                        alt={`Configuración ${imageNum}`}
                        className="w-full h-auto"
                      />
                      <div className="absolute top-1 right-1">
                        <div
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                            editForm.printConfigImage === imageNum
                              ? 'bg-green-500 border-green-600'
                              : 'bg-white border-gray-300'
                          }`}
                        >
                          {editForm.printConfigImage === imageNum && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!editForm.printConfigImage && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                    Selecciona una configuración de impresión haciendo clic en una imagen
                  </div>
                )}
              </div>
              </fieldset>

              {/* Section 6: Observaciones — deliberately OUTSIDE the fieldset
                  above: the one thing ROT-200 still allows editing on a locked
                  (non-CREATED) order. */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b text-blue-800">📝 6. Observaciones</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones Generales</label>
                    <textarea
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-md resize-vertical"
                      placeholder="Notas especiales, instrucciones adicionales, comentarios..."
                      value={editForm.observations}
                      onChange={(e) => handleEditFormChange('observations', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Etiqueta</label>
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-md"
                      placeholder="Etiqueta personalizada para esta orden..."
                      value={editForm.label}
                      onChange={(e) => handleEditFormChange('label', e.target.value)}
                      disabled={loading.update?.loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Imagen del Diseño</label>

                    {(editForm.designUrl && editForm.designUrl.startsWith('ordenesproduccion/')) || selectedOrder?.productCode ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setShowDesignImageModal(true);
                            }}
                          >
                            📷 Ver Imagen Almacenada
                          </Button>
                        </div>
                        
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const input = document.getElementById('design-image-upload-edit') as HTMLInputElement;
                            input?.click();
                          }}
                        >
                          📤 Subir Imagen del Diseño
                        </Button>
                        <input
                          id="design-image-upload-edit"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file && selectedOrder) {
                              await handleUploadDesignImage(selectedOrder.id, file);
                              e.target.value = '';
                            }
                          }}
                        />
                        <p className="text-sm text-gray-500">
                          Sube una imagen del diseño de la orden de producción
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
            
            {/* Info Message for New Orders */}
            {!selectedOrder && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  Después de guardar, podrás hacer clic en "Editar" para agregar materiales, especificaciones y más detalles.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                disabled={selectedOrder ? loading.update?.loading : loading.create?.loading}
                className="px-6 py-2"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateOrder}
                disabled={selectedOrder ? loading.update?.loading : loading.create?.loading}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-2"
              >
                {(selectedOrder ? loading.update?.loading : loading.create?.loading) && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {(selectedOrder ? loading.update?.loading : loading.create?.loading) ? (selectedOrder ? 'Actualizando Orden...' : 'Creando Orden...') : (selectedOrder ? 'Actualizar Orden de Producción' : 'Guardar Orden de Producción')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
              <h3 className="text-xl font-semibold">Vista Detallada: {selectedOrder.orderNumber}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetailModal(false)}
                className="p-2"
              >
                ✕
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Información Básica */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-blue-600 border-b pb-2">📋 Información General</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>Cliente:</strong> {selectedOrder.customerName || 'N/A'}</div>
                    <div><strong>Producto:</strong> {selectedOrder.productName}</div>
                    <div><strong>Código:</strong> {selectedOrder.productCode}</div>
                    <div><strong>Cantidad:</strong> {selectedOrder.quantityPlanned} {getUnitName(selectedOrder.unit)}</div>
                    <div><strong>Prioridad:</strong> {getPriorityLabel(selectedOrder.priority)}</div>
                    <div><strong>Estado:</strong> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(selectedOrder.status, selectedOrder)}`}>
                        {getStatusLabel(selectedOrder.status, selectedOrder)}
                      </span>
                    </div>
                    <div><strong>Fecha Límite:</strong> {selectedOrder.dueDate || 'N/A'}</div>
                    <div><strong>Lote:</strong> {selectedOrder.lotCode || 'N/A'}</div>
                    <div><strong>Equipo Preprensa:</strong> {(selectedOrder as any).equipoPreprensa || 'N/A'}</div>
                    <div><strong>Ruta:</strong> {selectedOrder.routeName || 'N/A'}</div>
                  </div>
                </div>
                
                {/* Imagen del Diseño */}
                <DetailDesignSection order={selectedOrder} />
              </div>
              
              {/* Observaciones */}
              {selectedOrder.notes && (
                <div>
                  <h4 className="text-lg font-semibold text-orange-600 border-b pb-2 mb-3">📝 Observaciones</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">{selectedOrder.notes}</p>
                  </div>
                </div>
              )}
              
              {/* Etiqueta */}
              {selectedOrder.label && (
                <div>
                  <h4 className="text-lg font-semibold text-blue-600 border-b pb-2 mb-3">🏷️ Etiqueta</h4>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">{selectedOrder.label}</p>
                  </div>
                </div>
              )}
              
              {/* Información de Auditoría */}
              <div>
                <h4 className="text-lg font-semibold text-gray-600 border-b pb-2 mb-3">🔍 Información de Auditoría</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div><strong>Creado:</strong> {new Date(selectedOrder.createdAt).toLocaleString('es-ES')}</div>
                  <div><strong>Actualizado:</strong> {new Date(selectedOrder.updatedAt).toLocaleString('es-ES')}</div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowDetailModal(false)}
              >
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  setShowDetailModal(false);
                  handleEdit(selectedOrder);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Editar Orden
              </Button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Importar Órdenes de Producción</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowImportModal(false)}
                className="p-2"
              >
                ✕
              </Button>
            </div>

            <div className="space-y-6">
              {/* Opción 1: Importación por Archivo */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Importar desde Archivo CSV/Excel
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Sube un archivo con las órdenes de producción. Debe incluir columnas: order_number, product_name, quantity_planned, unit, priority, due_date.
                </p>
                <div className="space-y-4">
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-300 transition-colors"
                    onDrop={handleFileDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                  >
                    <div className="text-gray-400 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    {importFile ? (
                      <div>
                        <p className="text-sm text-green-600 mb-2">Archivo seleccionado:</p>
                        <p className="text-sm font-medium">{importFile.name}</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-2">Arrastra tu archivo aquí o</p>
                        <Button variant="outline" size="sm" onClick={() => document.getElementById('file-input')?.click()}>
                          Seleccionar Archivo
                        </Button>
                      </>
                    )}
                    <input
                      id="file-input"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    disabled={loading.create.loading || !importFile}
                    onClick={handleFileImport}
                  >
                    {loading.import.loading ? 'Importando...' : 'Importar desde Archivo'}
                  </Button>
                </div>
              </div>

              {/* Opción 2: Importación por API */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Importar desde API Externa
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Configura la conexión con tu sistema ERP o API externa para importar órdenes automáticamente.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL del Endpoint API
                    </label>
                    <Input
                      type="url"
                      placeholder="https://api.tu-erp.com/orders"
                      value={apiConfig.url}
                      onChange={(e) => setApiConfig(prev => ({ ...prev, url: e.target.value }))}
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Método HTTP
                      </label>
                      <select 
                        className="w-full p-2 border border-gray-300 rounded-md"
                        value={apiConfig.method}
                        onChange={(e) => setApiConfig(prev => ({ ...prev, method: e.target.value }))}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Autenticación
                      </label>
                      <select 
                        className="w-full p-2 border border-gray-300 rounded-md"
                        value={apiConfig.authType}
                        onChange={(e) => setApiConfig(prev => ({ ...prev, authType: e.target.value }))}
                      >
                        <option value="bearer">Bearer Token</option>
                        <option value="basic">Basic Auth</option>
                        <option value="none">Sin Autenticación</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token/Credenciales
                    </label>
                    <Input
                      type="password"
                      placeholder="Tu token de API o credenciales"
                      value={apiConfig.credentials}
                      onChange={(e) => setApiConfig(prev => ({ ...prev, credentials: e.target.value }))}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={handleTestConnection}
                      disabled={!apiConfig.url}
                    >
                      Probar Conexión
                    </Button>
                    <Button 
                      className="flex-1" 
                      disabled={loading.create.loading || !apiConfig.url}
                      onClick={handleApiImport}
                    >
                      {loading.import.loading ? 'Importando...' : 'Importar desde API'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Configuración Avanzada */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">⚙</span>
                  Configuración de Importación
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      className="rounded" 
                      checked={importOptions.updateExisting}
                      onChange={(e) => setImportOptions(prev => ({ ...prev, updateExisting: e.target.checked }))}
                    />
                    <span className="text-sm">Actualizar órdenes existentes si ya existen</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      className="rounded" 
                      checked={importOptions.validateData}
                      onChange={(e) => setImportOptions(prev => ({ ...prev, validateData: e.target.checked }))}
                    />
                    <span className="text-sm">Validar datos antes de importar</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      className="rounded" 
                      checked={importOptions.sendNotification}
                      onChange={(e) => setImportOptions(prev => ({ ...prev, sendNotification: e.target.checked }))}
                    />
                    <span className="text-sm">Enviar notificación al completar importación</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowImportModal(false)}
                disabled={loading.list.loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Design Image Modal - Full Screen View with Zoom Controls */}
      {showDesignImageModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50 flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FileImage className="w-6 h-6 text-purple-600" />
                  Diseño / Plano
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Orden #{selectedOrder.orderNumber} - {selectedOrder.productName}
                </p>
              </div>
              
              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-white rounded-lg shadow-md px-2 py-1 mr-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomOut}
                    disabled={imageZoom <= 0.5}
                    className="h-8 w-8 p-0"
                    title="Alejar (Zoom Out)"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[60px] text-center">
                    {Math.round(imageZoom * 100)}%
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomIn}
                    disabled={imageZoom >= 5}
                    className="h-8 w-8 p-0"
                    title="Acercar (Zoom In)"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResetZoom}
                    className="h-8 w-8 p-0 ml-1"
                    title="Restablecer Vista"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <button
                  onClick={closeDesignModal}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                  title="Cerrar (ESC)"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div 
              className="flex-1 overflow-hidden bg-gray-100 p-4 relative"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              <div className="w-full h-full flex items-center justify-center">
                {selectedOrder.productCode && !productDesignFailed ? (
                  /* Primary: show the official product design from the Diseño module */
                  <img
                    src={`/api/catalog-designs/by-product-code/${encodeURIComponent(selectedOrder.productCode)}/image`}
                    alt="Diseño oficial del producto"
                    className={`max-w-none select-none ${imageZoom > 1 ? 'cursor-move' : 'cursor-default'}`}
                    style={{
                      transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                      maxHeight: '80vh',
                      maxWidth: '80vw',
                    }}
                    draggable={false}
                    onError={() => setProductDesignFailed(true)}
                  />
                ) : selectedOrder.designUrl && selectedOrder.designUrl.startsWith('ordenesproduccion/') ? (
                  /* Fallback: per-order design image */
                  <img
                    src={`/api/production-orders/${selectedOrder.id}/design-image?t=${Date.now()}`}
                    alt="Diseño de la orden"
                    className={`max-w-none select-none ${imageZoom > 1 ? 'cursor-move' : 'cursor-default'}`}
                    style={{
                      transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                      maxHeight: '80vh',
                      maxWidth: '80vw',
                    }}
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const errorMsg = document.createElement('div');
                        errorMsg.className = 'text-center text-red-500 py-8 bg-white rounded-lg p-8 shadow-lg';
                        errorMsg.innerHTML = `<div class="text-6xl mb-4">⚠️</div><p class="text-xl font-semibold mb-2">Error al cargar la imagen</p><p class="text-gray-600">Por favor, intenta de nuevo.</p>`;
                        parent.appendChild(errorMsg);
                      }
                    }}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-16 bg-white rounded-lg p-12 shadow-lg">
                    <div className="text-8xl mb-4">🎨</div>
                    <p className="text-xl font-semibold mb-2">Sin diseño registrado</p>
                    <p className="text-gray-600">El producto aún no tiene diseño en el módulo de Diseño</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t bg-gray-50 flex-shrink-0">
              {/* Design metadata strip */}
              {catalogDesignMeta && (
                <div className="px-6 py-2 flex flex-wrap items-center gap-4 border-b bg-purple-50 text-xs text-gray-700">
                  <span className="font-semibold text-purple-700">📌 Diseño v{catalogDesignMeta.version}</span>
                  {catalogDesignMeta.imageDate && (
                    <span>📅 Fecha: <strong>{new Date(catalogDesignMeta.imageDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
                  )}
                  {(catalogDesignMeta.uploaderName || catalogDesignMeta.uploaderUsername) && (
                    <span>👤 Subido por: <strong>{catalogDesignMeta.uploaderName || catalogDesignMeta.uploaderUsername}</strong></span>
                  )}
                  <span>🕐 {new Date(catalogDesignMeta.uploadedAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  {catalogDesignMeta.comments && (
                    <span className="italic text-gray-500">💬 {catalogDesignMeta.comments}</span>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center gap-2 px-6 py-4">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Move className="w-4 h-4" />
                    <span>{imageZoom > 1 ? 'Arrastra para mover' : 'Usa los controles de zoom'}</span>
                  </div>
                  <span>•</span>
                  <span>Rueda del mouse para zoom rápido</span>
                </div>
                <Button
                  variant="outline"
                  onClick={closeDesignModal}
                  className="min-w-[120px]"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-auto">
            <div className="sticky top-0 z-10 p-4 border-b flex justify-between items-center bg-white no-print">
              <h3 className="text-2xl font-bold text-gray-800">Vista Previa de Impresión</h3>
              <div className="flex gap-2">
                <Button
                  onClick={handleDownloadPDF}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                >
                  <FileDown className="w-4 h-4" />
                  Descargar PDF
                </Button>
                <Button
                  onClick={() => window.print()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  🖨️ Imprimir
                </Button>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold px-3"
                >
                  ×
                </button>
              </div>
            </div>
            
            <ProductionOrderDocument orderId={selectedOrder.id} printContentId="print-content" />
          </div>
        </div>
      )}

      {/* Engraving Order Modal */}
      {showEngravingModal && engravingModalOrder && (() => {
        const COLOR_LABELS = ['1','2','3','4','5','6','7','8'];
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-amber-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">🖨️ Orden de Grabado</h2>
                  <p className="text-amber-200 text-sm mt-0.5">
                    {engravingModalOrder.orderNumber} — {engravingModalOrder.customerName}
                  </p>
                </div>
                <button onClick={() => setShowEngravingModal(false)} className="text-amber-200 hover:text-white text-2xl font-bold">×</button>
              </div>

              <div className="p-6 space-y-5">
                {/* Auto-populated from production order — identification only */}
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase mb-2 border-b pb-1 tracking-wider">Datos de la Orden de Producción</h3>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-blue-50 rounded p-2 border border-blue-100"><span className="font-semibold text-blue-600 block mb-0.5">Nombre Grabado (Producto)</span>{engravingModalOrder.productCode} — {engravingModalOrder.productName}</div>
                    <div className="bg-blue-50 rounded p-2 border border-blue-100"><span className="font-semibold text-blue-600 block mb-0.5">Cliente</span>{engravingModalOrder.customerName}</div>
                    <div className="bg-blue-50 rounded p-2 border border-blue-100"><span className="font-semibold text-blue-600 block mb-0.5">No. Orden Producción</span>{engravingModalOrder.orderNumber}</div>
                  </div>
                </div>

                {/* Missing fields warning */}
                {engravingMissingFields.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 font-semibold text-sm mb-1">⚠️ Faltan los siguientes campos obligatorios:</p>
                    <ul className="list-disc list-inside text-red-700 text-sm space-y-0.5">
                      {engravingMissingFields.map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                )}

                {/* Save error — surfaced so a failed save is never mistaken for a
                    successful one (ROT-173) */}
                {engravingSaveError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 font-semibold text-sm">⚠️ {engravingSaveError}</p>
                  </div>
                )}

                {/* === ESPECIFICACIÓN DE FORMATO === */}
                <div>
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 border-b pb-1 tracking-wider">Especificación de Formato</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Row: Rodillo Final + Flexo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rodillo Final <span className="text-gray-400 font-normal text-xs">(circunferencia manga)</span></label>
                      <input
                        type="text"
                        value={engravingFormData.rodilloFinal}
                        onChange={e => setEngravingFormData(p => ({ ...p, rodilloFinal: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 59 cm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Flexo <span className="text-gray-400 font-normal text-xs">(no. de colores)</span></label>
                      <input
                        type="text"
                        value={engravingFormData.flexo}
                        onChange={e => setEngravingFormData(p => ({ ...p, flexo: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                        placeholder="Ej: 6"
                      />
                    </div>
                    {/* Row: Ancho Individual + Bobinas al Eje */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ancho Individual <span className="text-gray-400 font-normal text-xs">(mm)</span></label>
                      <input
                        type="text"
                        value={engravingFormData.anchoIndividual}
                        onChange={e => setEngravingFormData(p => ({ ...p, anchoIndividual: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 395.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bobinas al Eje</label>
                      <input
                        type="text"
                        value={engravingFormData.bobinasAlEje}
                        onChange={e => setEngravingFormData(p => ({ ...p, bobinasAlEje: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 2"
                      />
                    </div>
                    {/* Row: Repetición Individual + Bobinas al Desarrollo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Repetición Individual <span className="text-gray-400 font-normal text-xs">(mm)</span></label>
                      <input
                        type="text"
                        value={engravingFormData.repeticionIndividual}
                        onChange={e => setEngravingFormData(p => ({ ...p, repeticionIndividual: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 295.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bobinas al Desarrollo</label>
                      <input
                        type="text"
                        value={engravingFormData.bobinasAlDesarrollo}
                        onChange={e => setEngravingFormData(p => ({ ...p, bobinasAlDesarrollo: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 2"
                      />
                    </div>
                    {/* Row: Lineaje + Tipo de Impresión */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lineaje * <span className="text-gray-400 font-normal">(líneas/cm)</span></label>
                      <input
                        type="text"
                        value={engravingFormData.lineaje}
                        onChange={e => setEngravingFormData(p => ({ ...p, lineaje: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                        placeholder="Ej: 120"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Impresión *</label>
                      <select
                        value={engravingFormData.tipoPrint}
                        onChange={e => setEngravingFormData(p => ({ ...p, tipoPrint: e.target.value }))}
                        onFocus={() => { if (!catalogPrintTypes.length) loadCatalogPrintTypes(); }}
                        className="w-full border rounded-md p-2 text-sm"
                      >
                        {catalogPrintTypes.length > 0
                          ? <>
                              {/* ROT-173: if the order's saved value (e.g. a legacy
                                  "FLEXO" from before the catalog existed) isn't one
                                  of the catalog's names, a plain controlled <select>
                                  silently shows the first catalog option instead —
                                  the field LOOKS like something else is selected,
                                  but the actual state (and what gets saved if the
                                  user doesn't touch the field) is still the old
                                  value. Keeping it as an explicit option means the
                                  dropdown always shows what's really selected. */}
                              {!catalogPrintTypes.some(t => t.name === engravingFormData.tipoPrint) && engravingFormData.tipoPrint && (
                                <option value={engravingFormData.tipoPrint}>{engravingFormData.tipoPrint} (no está en el catálogo)</option>
                              )}
                              {catalogPrintTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                            </>
                          : ['FLEXO', 'LAMINACIÓN', 'HUECOGRABADO', 'SERIGRAFÍA', 'DIGITAL'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Espesor del Material *</label>
                      <select
                        value={engravingFormData.espesorMaterial}
                        onChange={e => setEngravingFormData(p => ({ ...p, espesorMaterial: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="45">45 μm</option>
                        <option value="67">67 μm</option>
                        <option value="100">100 μm</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fotocelda — Color/Tipo</label>
                      <select
                        value={engravingFormData.fotoceldaColor}
                        onChange={e => setEngravingFormData(p => ({
                          ...p,
                          fotoceldaColor: e.target.value,
                          // Clear the manual color name whenever "Color" stops
                          // being selected, so a stale value can't linger
                          // hidden and get saved/printed by mistake.
                          ...(e.target.value !== 'COLOR' ? { fotoceldaColorNombre: '' } : {}),
                        }))}
                        onFocus={() => { if (!catalogPhotocellTypes.length) loadCatalogPhotocellTypes(); }}
                        className="w-full border rounded-md p-2 text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        {catalogPhotocellTypes.length > 0
                          ? catalogPhotocellTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
                          : ['BLANCO+NEGRO', 'COLOR', 'NINGUNA'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {engravingFormData.fotoceldaColor === 'COLOR' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">¿Qué Color? *</label>
                        <input
                          type="text"
                          value={engravingFormData.fotoceldaColorNombre}
                          onChange={e => setEngravingFormData(p => ({ ...p, fotoceldaColorNombre: e.target.value }))}
                          className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                          placeholder="Ej: Rojo, Azul, Verde"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Especificación de Fotocelda</label>
                      <input
                        type="text"
                        value={engravingFormData.especFotocelda}
                        onChange={e => setEngravingFormData(p => ({ ...p, especFotocelda: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: Medida ancho × alto"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Código de Barras</label>
                      <input
                        type="text"
                        value={engravingFormData.codigoBarras}
                        onChange={e => setEngravingFormData(p => ({ ...p, codigoBarras: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm"
                        placeholder="Ej: 750300 9080217"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Número de Revisión del Diseño</label>
                      <input
                        type="text"
                        value={engravingFormData.revisionNumero}
                        onChange={e => setEngravingFormData(p => ({ ...p, revisionNumero: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                        placeholder="Ej: REV 04 ARCHIVO 307-25"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Entrega</label>
                      <input
                        type="date"
                        value={engravingFormData.fechaEntrega}
                        onChange={e => setEngravingFormData(p => ({ ...p, fechaEntrega: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm bg-yellow-50"
                      />
                    </div>
                  </div>
                </div>

                {/* === COLORES === */}
                <div>
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 border-b pb-1 tracking-wider">Colores (hasta 8)</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {COLOR_LABELS.map((lbl, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-gray-500 w-4 shrink-0">{lbl}</span>
                        <input
                          type="text"
                          value={engravingFormData.colores[idx] || ''}
                          onChange={e => {
                            const newColors = [...engravingFormData.colores];
                            newColors[idx] = e.target.value;
                            setEngravingFormData(p => ({ ...p, colores: newColors }));
                          }}
                          className="flex-1 border rounded p-1.5 text-xs"
                          placeholder={`Color ${lbl}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* === OBSERVACIONES Y COBRO === */}
                <div>
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 border-b pb-1 tracking-wider">Observaciones y Cobro</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                      <textarea
                        value={engravingFormData.observaciones}
                        onChange={e => setEngravingFormData(p => ({ ...p, observaciones: e.target.value }))}
                        className="w-full border border-yellow-400 rounded-md p-2 text-sm resize-none bg-yellow-50"
                        rows={3}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer bg-yellow-50 border border-yellow-400 rounded-md px-4 py-2">
                      <input
                        type="checkbox"
                        id="cobrarGrabados"
                        checked={engravingFormData.cobrarGrabados}
                        onChange={e => setEngravingFormData(p => ({ ...p, cobrarGrabados: e.target.checked }))}
                        className="w-4 h-4 accent-amber-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Cobrar Grabados</span>
                    </label>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2 border-t justify-end flex-wrap">
                  {engravingModalData?.id && (
                    <Button
                      variant="outline"
                      className="text-blue-600"
                      onClick={() => navigate(`/app/engraving-orders/${engravingModalData.id}/print`)}
                    >
                      🖨️ Imprimir
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowEngravingModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleSaveEngravingOrder}
                    disabled={engravingFormSaving}
                  >
                    {engravingFormSaving ? 'Guardando...' : (engravingModalData?.id ? 'Actualizar Orden de Grabado' : 'Crear Orden de Grabado')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Email Modal */}
      {showEmailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">📧 Enviar Orden por Email</h3>
            
            <div className="mb-4 bg-blue-50 border-l-4 border-blue-600 p-3 rounded">
              <p className="text-sm text-blue-900">
                <strong>Orden:</strong> {selectedOrder.orderNumber}
              </p>
              <p className="text-sm text-blue-900">
                <strong>Cliente:</strong> {selectedOrder.customerName}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email del destinatario *
              </label>
              <Input
                type="email"
                placeholder="ejemplo@correo.com"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailRecipient('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={sendOrderEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!emailRecipient || !emailRecipient.includes('@')}
              >
                📤 Enviar Email
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Coextrusion Modal */}
      {coextrusionOrderId && (
        <CoextrusionModal
          productionOrderId={coextrusionOrderId}
          onClose={() => { setCoextrusionOrderId(null); loadSavedCoextrusionIds(); }}
        />
      )}

      {(medidaPreview || medidaPreviewError) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {medidaPreview ? `Medida vs Comercia — OP ${medidaPreview.orderNumber}` : 'Comparar medida con Comercia'}
              </h3>
              <button
                onClick={() => { setMedidaPreview(null); setMedidaPreviewError(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {medidaPreviewError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                {medidaPreviewError}
              </div>
            )}

            {medidaPreview && (
              <>
                <p className="text-sm text-gray-500 mb-4">Comparado contra Comercia usando fecha {medidaPreview.fechaUsed}.</p>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="py-2 pr-4">Material</th>
                      <th className="py-2 pr-4">Medida actual</th>
                      <th className="py-2 pr-4">Medida en Comercia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medidaPreview.materials.map((m, i) => (
                      <tr key={i} className={`border-b ${m.changed ? 'bg-amber-50' : ''}`}>
                        <td className="py-2 pr-4">{m.material}</td>
                        <td className="py-2 pr-4">{m.localMedida ?? '—'}</td>
                        <td className={`py-2 pr-4 ${m.changed ? 'font-semibold text-amber-700' : ''}`}>
                          {m.catalogCode ? (m.externalMedida ?? '—') : 'sin código de catálogo — no comparable'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {medidaPreview.materials.some(m => m.changed) ? (
                  <>
                    <p className="text-xs text-amber-700 mt-3">
                      ⚠ Esto actualiza el catálogo de materiales, no solo esta orden — el nuevo valor de medida aplicará a cualquier otra orden que use el mismo código de material.
                    </p>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" onClick={() => setMedidaPreview(null)}>Cancelar</Button>
                      <Button onClick={handleApplyMedidaSync} disabled={medidaApplyLoading}>
                        {medidaApplyLoading ? 'Aplicando...' : 'Aplicar cambios de medida'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-4">No hay diferencias — la medida local ya coincide con Comercia.</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {(refreshPreview || refreshPreviewError) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {refreshPreview ? `Actualizar desde Comercia — OP ${refreshPreview.orderNumber}` : 'Actualizar orden desde Comercia'}
              </h3>
              <button
                onClick={() => { setRefreshPreview(null); setRefreshPreviewError(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {refreshPreviewError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                {refreshPreviewError}
              </div>
            )}

            {refreshPreview && (
              <>
                <p className="text-sm text-gray-500 mb-4">Comparado contra Comercia usando fecha {refreshPreview.fechaUsed}.</p>

                {refreshPreview.fieldDiffs.length > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Campos de la orden</h4>
                    <table className="w-full text-sm border-collapse mb-4">
                      <thead>
                        <tr className="border-b text-left text-gray-600">
                          <th className="py-2 pr-4">Campo</th>
                          <th className="py-2 pr-4">Valor actual</th>
                          <th className="py-2 pr-4">Valor en Comercia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refreshPreview.fieldDiffs.map((f, i) => (
                          <tr key={i} className="border-b bg-amber-50">
                            <td className="py-2 pr-4">{f.label}</td>
                            <td className="py-2 pr-4">{f.currentValue ?? '—'}</td>
                            <td className="py-2 pr-4 font-semibold text-amber-700">{f.newValue ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {refreshPreview.materials.length > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Materiales</h4>
                    <table className="w-full text-sm border-collapse mb-4">
                      <thead>
                        <tr className="border-b text-left text-gray-600">
                          <th className="py-2 pr-4">Material</th>
                          <th className="py-2 pr-4">Calibre / Factor / Medida actual</th>
                          <th className="py-2 pr-4">Calibre / Factor / Medida en Comercia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refreshPreview.materials.map((m, i) => (
                          <tr key={i} className={`border-b ${m.changed ? 'bg-amber-50' : ''}`}>
                            <td className="py-2 pr-4">{m.material}</td>
                            <td className="py-2 pr-4">
                              {m.current ? `${m.current.caliber ?? '—'} / ${m.current.factor ?? '—'} / ${m.current.medida ?? '—'}` : 'nuevo material'}
                            </td>
                            <td className={`py-2 pr-4 ${m.changed ? 'font-semibold text-amber-700' : ''}`}>
                              {m.incoming ? `${m.incoming.caliber ?? '—'} / ${m.incoming.factor ?? '—'} / ${m.incoming.medida ?? '—'}` : 'sin dato en Comercia'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <h4 className="text-sm font-semibold text-gray-700 mb-2">Otras secciones (solo conteo)</h4>
                <table className="w-full text-sm border-collapse mb-4">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="py-2 pr-4">Sección</th>
                      <th className="py-2 pr-4">Actual</th>
                      <th className="py-2 pr-4">En Comercia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Especificaciones</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.specifications.current}</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.specifications.incoming}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Rutas de fabricación</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.routes.current}</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.routes.incoming}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Movimientos de inventario</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.inventoryMovements.current}</td>
                      <td className="py-2 pr-4">{refreshPreview.counts.inventoryMovements.incoming}</td>
                    </tr>
                  </tbody>
                </table>

                {refreshPreview.hasChanges ? (
                  <>
                    <p className="text-xs text-amber-700 mt-1">
                      ⚠ Esto reemplaza los datos de la orden (items, materiales, especificaciones, rutas y movimientos) con lo que devuelve Comercia, y libera la protección de "Modificada" en esta orden.
                    </p>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" onClick={() => setRefreshPreview(null)}>Cancelar</Button>
                      <Button onClick={handleApplyComerciaFullRefresh} disabled={refreshApplyLoading}>
                        {refreshApplyLoading ? 'Aplicando...' : 'Actualizar orden'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-4">No hay diferencias — la orden local ya coincide con Comercia.</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}